import { i18n } from './i18n.js';
import {
  openMoonlightWall,
  resolveMoonlightOffer,
  shouldRaiseMoonlightWall,
} from './ui/moonlight-wall.js';
import { accountOnHoldCopy, isServicePause, openServicePauseNotice } from './ui/service-pause.js';
import { Store } from './ui/store.js';
import { HubSheets } from './ui/hub-sheets.js';
import { BootVeil } from './ui/boot-veil.js';
import { MoonfallActionTrace } from './utils/card-action-trace.js';
import { MoonfallCloudGuard } from './utils/cloud-mutation-guard.js';
import { MoonfallStorybookContext } from './ui/game-end.js';
import { GameResume } from './game-resume.js';
import { MoonfallGuest } from './guest-session.js';
import { MoonfallCloud } from './convex-client.js';
import { MoonfallAnalytics } from './analytics/posthog-init.js';
/**
 * Socket Handler Module
 * Manages all Socket.IO communication with the server
 */

import { onLanguageChange } from './i18n-events.js';
import { ActionIntent } from './ui/action-intent.js';
import { AssetPreloader } from './ui/asset-preloader.js';
import { Board } from './ui/board.js';
import { Cards } from './ui/cards.js';
import { DeckoutFeedback } from './ui/deckout-feedback.js';
import { TurnTimeoutFeedback } from './ui/turn-timeout-feedback.js';
import { DeckSelector } from './ui/deck-selector.js';
import { Effects } from './ui/effects.js';
import { EnemyFan } from './ui/enemy-fan.js';
import { EnemyReplay } from './ui/enemy-replay.js';
import { Firebase } from './firebase-loader.js';
import { FX } from './ui/fx.js';
import { GameEnd } from './ui/game-end.js';
import { Lobby } from './ui/lobby.js';
import { MatchLoading } from './ui/match-loading.js';
import { ServerUpdateWait } from './ui/server-update-wait.js';
import { Mulligan } from './ui/mulligan.js';
import { ReconnectStatus } from './ui/reconnect-status.js';
import { Referrals } from './ui/referrals.js';
import { SoundEffects } from './ui/sound-effects.js';
import { SpellAnnounce } from './ui/spell-announce.js';
import { StarterReward } from './ui/starter-reward.js';
import { StoryFeed } from './ui/story-feed.js';
import { ThinkingIndicator } from './ui/thinking-indicator.js';
import { TurnBanner } from './ui/turn-banner.js';
import { Tutorial } from './ui/tutorial.js';
import { GameState } from './game-state.js';
import { uiCssPx } from './ui/ui-scale.js';


/**
 * A translation, or the English fallback when it is not resolved.
 *
 * `t()` answers with the KEY when a translation is missing or the file has not
 * loaded yet, and a key is truthy — so the older `t(...) || fallback` shape put
 * `game.processingAction` in front of the player. Treat key-returned as
 * unresolved, which is what the rest of the client already does.
 */
function resolvedText(key, fallback, replacements) {
    const translated = i18n?.t?.(key, replacements);
    return translated && translated !== key ? translated : fallback;
}

const SocketHandler = (function () {
    const GAME_NOTIFICATION_ID = 'game-notification-live-region';
    const MANGA_CAP_NOTICE_KEY = 'moonfall:manga-cap-notice:v1';
    const gameNotificationQueue = [];
    let activeGameNotification = null;
    let gameNotificationTimer = null;
    let mangaCapNoticeShownInMemory = false;

    function consumeMangaCapNotice() {
        try {
            if (window.sessionStorage?.getItem(MANGA_CAP_NOTICE_KEY) === 'shown') return false;
            window.sessionStorage?.setItem(MANGA_CAP_NOTICE_KEY, 'shown');
            mangaCapNoticeShownInMemory = true;
            return true;
        } catch (_) {
            if (mangaCapNoticeShownInMemory) return false;
            mangaCapNoticeShownInMemory = true;
            return true;
        }
    }

    function debugLog(...args) {
        if (window.CLIENT_DEBUG_MODE) {
            console.log(...args);
        }
    }

    function translate(key, fallback, replacements = {}) {
        const translated = i18n?.t?.(key, replacements);
        return translated && translated !== key ? translated : fallback;
    }

    function runGameStartedBestEffort(label, step) {
        try {
            const result = step();
            result?.catch?.((error) => {
                console.error(`[SocketHandler] gameStarted ${label} failed:`, error);
            });
        } catch (error) {
            console.error(`[SocketHandler] gameStarted ${label} failed:`, error);
        }
    }

    function forceFreshGameFatalSurface() {
        runGameStartedBestEffort(
            'referrals teardown',
            () => Referrals?.close?.({ restoreFocus: false })
        );
        const lobbyView = document.getElementById('lobby-view');
        const gameView = document.getElementById('game-view');
        const authView = document.getElementById('auth-view');
        const userControls = document.getElementById('user-controls');
        if (lobbyView) {
            lobbyView.style.display = 'none';
            lobbyView.style.setProperty('display', 'none', 'important');
        }
        if (gameView) gameView.style.display = 'flex';
        if (authView) authView.style.display = 'none';
        if (userControls) userControls.style.display = 'none';
        document.body?.classList.add('game-active');
        document.body?.classList.remove('game-transition-pending');
    }

    function failFreshGameStartedInstall(error) {
        console.error('[SocketHandler] Fresh game view install failed:', error);
        // No first screen is coming to present: the fatal surface must be seen.
        runGameStartedBestEffort('match loading teardown', () => MatchLoading?.cancel?.());
        if (!document.body?.classList.contains('game-active')) {
            try {
                Lobby?.switchToGameView?.({ veil: false });
            } catch (handoffError) {
                console.error('[SocketHandler] Fresh game recovery handoff failed:', handoffError);
            }
        }
        // The canonical handoff owns normal overlay/audio/focus cleanup. Its
        // absence or a partial throw must still never leave an authoritative
        // match underneath interactive lobby or auth controls.
        forceFreshGameFatalSurface();
        const message = translate(
            'gameEnd.fatalError.message',
            'A critical AI service error occurred. Please refresh the page.'
        );
        try {
            if (typeof GameEnd?.showFatalError === 'function') {
                GameEnd.showFatalError(message);
            } else {
                alert(message);
            }
        } catch (noticeError) {
            console.error('[SocketHandler] Fresh game recovery notice failed:', noticeError);
        }
    }

    function connectionFailureMessage(error) {
        if (error?.message === 'Please sign in again to play.') {
            return translate('auth.signInAgainToPlay', 'Please sign in again to play.');
        }
        return translate('common.failedToConnect', 'Failed to connect.');
    }

    function roomInviteLookupFailureMessage(error) {
        if (error?.code === 'same_account_not_allowed') {
            return translate(
                'lobby.sameAccountNotAllowed',
                'You cannot join your own multiplayer room. Use a different account for the second player.'
            );
        }
        if (error?.message === 'Authentication required.') {
            // rejectGameplayAuth emits the same failure through the global
            // socket error lane before acknowledging this request.
            return null;
        }
        if (error?.message === 'Please sign in again to play.') {
            return connectionFailureMessage(error);
        }
        if (error?.message === 'Room not found') {
            return translate('lobby.validRoomCode', 'Please enter a valid room code.');
        }
        return translate('lobby.roomCheckFailed', 'Failed to check room.');
    }

    function ensureGameNotificationRegion() {
        let notification = document.getElementById(GAME_NOTIFICATION_ID);
        if (!notification && document.body) {
            notification = document.createElement('div');
            notification.id = GAME_NOTIFICATION_ID;
            notification.setAttribute('role', 'status');
            notification.setAttribute('aria-live', 'polite');
            notification.setAttribute('aria-atomic', 'true');
            document.body.appendChild(notification);
        }
        return notification;
    }

    function clearGameNotifications() {
        gameNotificationQueue.length = 0;
        activeGameNotification = null;
        if (gameNotificationTimer) clearTimeout(gameNotificationTimer);
        gameNotificationTimer = null;
        document.getElementById(GAME_NOTIFICATION_ID)?.remove();
    }

    // Connection notice: a single, reusable element rather than an appended div
    // per event. Backgrounding a phone repeatedly used to stack one permanent
    // "please refresh" per drop, none of which were ever removed.
    const CONNECTION_NOTICE_ID = 'connection-status-notice';

    function setConnectionNotice(message, { connectionLost = true } = {}) {
        let element = document.getElementById(CONNECTION_NOTICE_ID);
        if (!element) {
            element = document.createElement('div');
            element.id = CONNECTION_NOTICE_ID;
            element.className = 'game-notification';
            element.setAttribute('role', 'status');
            element.setAttribute('aria-live', 'polite');
            document.body.appendChild(element);
        }
        element.textContent = message;
        /* The match loading screen covers this notice: while the connection
           is down it says so itself. A refused resume is not the connection. */
        MatchLoading?.showConnectionState?.(connectionLost ? message : null);
    }

    function clearConnectionNotice() {
        document.getElementById(CONNECTION_NOTICE_ID)?.remove();
        MatchLoading?.showConnectionState?.(null);
    }

    function showNextGameNotification() {
        if (activeGameNotification || gameNotificationQueue.length === 0) return;
        const notification = ensureGameNotificationRegion();
        if (!notification) return;
        activeGameNotification = gameNotificationQueue.shift();
        notification.className = 'game-notification';
        if (activeGameNotification.allowWrap) {
            notification.classList.add('game-notification--wrap');
        }
        notification.textContent = '';
        // Mutate an already-mounted live region so assistive technology sees
        // the announcement, while the singleton queue preserves server order.
        void notification.offsetHeight;
        notification.textContent = activeGameNotification.message;
        const readableMs = Math.min(
            7000,
            Math.max(activeGameNotification.visibleMs, 1000 + activeGameNotification.message.length * 50),
        );
        gameNotificationTimer = setTimeout(() => {
            notification.classList.add('fade-out');
            gameNotificationTimer = setTimeout(() => {
                notification.className = '';
                notification.textContent = '';
                activeGameNotification = null;
                gameNotificationTimer = null;
                showNextGameNotification();
            }, 500);
        }, readableMs);
    }

    /** Ordered transient centered toast shared by gameplay feedback. */
    function showGameNotification(message, visibleMs = 1500, allowWrap = false) {
        if (!message) return;
        gameNotificationQueue.push({ message: String(message), visibleMs, allowWrap });
        showNextGameNotification();
    }

    // Never log the Firebase ID token: it is a live bearer credential.
    function redactAuthToken(payload) {
        if (!payload || typeof payload !== 'object' || !('authToken' in payload)) {
            return payload;
        }
        return { ...payload, authToken: '[redacted]' };
    }

    function beginActionTrace(sourceCardId, details) {
        const trace = MoonfallActionTrace;
        if (!trace) return null;
        return trace.findOpen(sourceCardId)
            || trace.start(sourceCardId, details, 'play-action-started');
    }

    function recordActionTrace(traceId, eventName, details) {
        if (!traceId) return;
        MoonfallActionTrace?.record?.(traceId, eventName, details);
    }

    function finishActionTrace(traceId, eventName, details) {
        if (!traceId) return;
        MoonfallActionTrace?.finish?.(traceId, eventName, details);
    }

    async function getAuthTokenSafe() {
        if (Firebase?.getIdToken) {
            try {
                return await Firebase.getIdToken();
            } catch (_) {
                return null;
            }
        }
        return null;
    }

    async function getRequiredAuthToken() {
        const authToken = await getAuthTokenSafe();
        if (!authToken) {
            throw new Error('Please sign in again to play.');
        }
        return authToken;
    }

    // Attach the credential the server expects for a gameplay action. A guest
    // tutorial session carries a signed guest token (no Firebase identity); every
    // signed-in game carries a Firebase ID token exactly as before. Only in-game
    // actions route through here — createRoom/joinRoom/startSolo call
    // getRequiredAuthToken directly and stay Firebase-only.
    async function withRequiredAuthPayload(payload) {
        if (MoonfallGuest && MoonfallGuest.isActive()) {
            return { ...(payload || {}), guestToken: MoonfallGuest.getToken() };
        }
        return {
            ...(payload || {}),
            authToken: await getRequiredAuthToken()
        };
    }

    function clearEndedGuestLatchForAuthenticatedStart() {
        try { MoonfallGuest?.deactivate?.({ clearEnded: true }); } catch (_) { }
    }

    function getCurrentLanguage() {
        return i18n?.getLanguage?.() || 'en';
    }

    function emitWithAck(eventName, payload, timeoutMs = 8000, {
        captureLateAck = false,
    } = {}) {
        return new Promise((resolve, reject) => {
            let settled = false;
            let timer = null;
            let lateTimer = null;
            let resolveLateAck = null;
            const lateAck = captureLateAck
                ? new Promise((resolveLate) => { resolveLateAck = resolveLate; })
                : null;
            const settleLateAck = (result) => {
                if (!resolveLateAck) return;
                const settle = resolveLateAck;
                resolveLateAck = null;
                if (lateTimer) clearTimeout(lateTimer);
                lateTimer = null;
                settle(result || { ok: false, code: 'no_response' });
            };
            const finish = (result) => {
                if (settled) {
                    settleLateAck(result);
                    return;
                }
                settled = true;
                if (timer) clearTimeout(timer);
                resolve(result || { ok: false, code: 'no_response' });
            };
            timer = setTimeout(() => {
                if (settled) return;
                settled = true;
                const timeoutResult = { ok: false, code: 'timeout' };
                if (lateAck) {
                    Object.defineProperty(timeoutResult, 'lateAck', {
                        configurable: false,
                        enumerable: false,
                        value: lateAck,
                    });
                    lateTimer = setTimeout(
                        () => settleLateAck({ ok: false, code: 'late_timeout' }),
                        timeoutMs,
                    );
                    lateTimer.unref?.();
                }
                resolve(timeoutResult);
            }, timeoutMs);
            try {
                if (captureLateAck && typeof socket.timeout === 'function') {
                    socket.timeout(timeoutMs * 2).emit(
                        eventName,
                        payload,
                        (error, result) => {
                            if (error) {
                                if (settled) {
                                    settleLateAck({ ok: false, code: 'late_timeout' });
                                } else {
                                    finish({ ok: false, code: 'timeout' });
                                }
                                return;
                            }
                            finish(result);
                        },
                    );
                } else {
                    socket.emit(eventName, payload, finish);
                }
            } catch (err) {
                settled = true;
                clearTimeout(timer);
                settleLateAck({ ok: false, code: 'emit_failed' });
                reject(err);
            }
        });
    }

    function guestStartWasSuperseded(isCurrent) {
        return typeof isCurrent === 'function' && isCurrent() !== true;
    }

    const LOBBY_START_ACK_TIMEOUT_MS = 30000;
    const LOBBY_START_PREFLIGHT_TIMEOUT_MS = 20000;

    function runLobbyStartPreflight(load) {
        let timeoutId = null;
        const timeout = new Promise((_, reject) => {
            timeoutId = setTimeout(() => {
                const error = new Error('Lobby start preflight timed out.');
                error.code = 'preflight_timeout';
                reject(error);
            }, LOBBY_START_PREFLIGHT_TIMEOUT_MS);
            timeoutId.unref?.();
        });
        return Promise.race([Promise.resolve().then(load), timeout])
            .finally(() => clearTimeout(timeoutId));
    }

    function warmOwnCardback(slug) {
        try {
            void MatchLoading?.warmCardback?.(slug);
        } catch (_) { /* a warm is never part of the start */ }
    }

    /**
     * THE PRESS SENDS AT ONCE. No account read stands in front of a lobby
     * start: the server trusts neither claim a start could carry. It re-reads
     * the Manga Pass on the authenticated token (`resolveMangaPassStatus`) and
     * takes the card back from the authenticated profile
     * (`resolveAuthoritativeCardbackSlug`), and waiting on the two reads here
     * cost 0.27-1.1 s of the match bar.
     *
     * Called synchronously in the press's own task. The card back the
     * session's boot profile names (main.js, `GameState.setUser`) starts
     * warming now, while the server builds the match (ui/match-loading.js).
     *
     * That back can be stale: the Collection's equip writes only the account,
     * and a back changed in another tab or on another device never reaches
     * this session. So the profile is still asked on every press, in the
     * background, never in front of the start. When the account names another
     * back, that back warms too and the session learns it, so the next press
     * warms only the right one. A session that knew no back warms the
     * account's; if the read fails, it warms the stock back. A warm is never
     * part of the start.
     *
     * Returns the start's card back claim: the slug when the session knows it
     * (the Playwright stub server, which has no profile to read, still honours
     * it), otherwise nothing, which the server reads as the stock back.
     */
    function warmStartCardback() {
        let pressUser = null;
        try {
            pressUser = GameState?.getUser?.() || null;
        } catch (_) { /* an unreadable session is a back it does not know */ }
        const known = cardbackName(pressUser?.selectedCardback);
        if (known) warmOwnCardback(known);
        const users = MoonfallCloud?.users;
        if (typeof users?.getProfile !== 'function') {
            if (!known) warmOwnCardback('default');
        } else {
            Promise.resolve()
                .then(() => users.getProfile())
                .then(
                    (profile) => adoptAccountCardback(profile, pressUser, known),
                    (err) => {
                        if (known) {
                            debugLog('[SocketHandler] Profile read failed; the session\'s card back stands', err?.message || err);
                            return;
                        }
                        console.warn('[SocketHandler] Failed to read the selected cardback, warming the standard one', err?.message || err);
                        warmOwnCardback('default');
                    },
                );
        }
        return known ? { selectedCardback: known } : {};
    }

    function cardbackName(slug) {
        return typeof slug === 'string' && slug.trim() ? slug.trim() : null;
    }

    /* The account's card back, from the press's background profile read.
       It warms when the session named another back or none, and it replaces
       the session's back, but only while the session is still the one the
       press read. A read that returns no profile writes nothing; if the
       session knew no back either, the stock back warms. */
    function adoptAccountCardback(profile, pressUser, known) {
        if (!profile || typeof profile !== 'object') {
            if (!known) warmOwnCardback('default');
            return;
        }
        const account = cardbackName(profile.selectedCardback) || 'default';
        if (account === known) return;
        warmOwnCardback(account);
        try {
            if (pressUser && typeof pressUser === 'object' && GameState?.getUser?.() === pressUser) {
                GameState.setUser?.({ ...pressUser, selectedCardback: account });
            }
        } catch (_) { /* the session only caches the account's back */ }
    }

    function normalizeLobbyStartFailure(error) {
        if (error?.code === 'preflight_timeout') {
            return { ok: false, code: 'preflight_timeout' };
        }
        return { ok: false, code: 'connection_failed' };
    }

    /**
     * A lobby start that failed before anything was sent.
     *
     * One that never reached the server (no connection, a preflight that
     * hung) used to raise a native alert, "Failed to connect.", and leave the
     * lobby to be pressed again; a phone browser may suppress that alert and
     * say nothing at all (ui/confirm-dialog.js). It is now handed back marked
     * `unreached` and without a dialog of its own: the lobby says it in the
     * app's dialog and offers "Try again" there, which runs the same start
     * (ui/lobby.js, `playerRetriesUnreachedStart`). The mark is not
     * enumerable, so the result reads as it always did.
     *
     * A start refused for its credential keeps its alert: asking again does
     * not sign anybody in.
     *
     * `sent`: the start was already handed to the socket when this failed (an
     * emit that threw, an acknowledgement that could not be read). That is
     * never marked, whatever the error says: the first start may be live on
     * the server, and "Try again" would send a second. It keeps the alert it
     * always had.
     */
    function failedLobbyStart(error, { sent = false } = {}) {
        const result = normalizeLobbyStartFailure(error);
        if (sent || error?.message === 'Please sign in again to play.') {
            alert(connectionFailureMessage(error));
            return result;
        }
        Object.defineProperty(result, 'unreached', {
            configurable: false,
            enumerable: false,
            value: true,
        });
        return result;
    }

    /**
     * Every lobby start funnels its result through here, so a refusal the
     * player cannot see becomes indistinguishable from a broken button.
     *
     * `too_many_attempts` used to fall straight through: the server throttles
     * starts (game/utils/actionThrottle.js) and the guest tutorial already
     * waits and retries on it, but the signed-in path dropped it silently. The
     * player saw the deck selector close and nothing happen. Measured on the
     * fourth solo start in quick succession.
     *
     * It is reported, not retried. A silent auto-retry would hide a limit the
     * player is genuinely hitting, and the honest message ("wait a few
     * seconds") is more useful than a second attempt they did not ask for.
     */
    function surfaceIndeterminateStart(result) {
        if (result?.ok !== false) return result;
        if (result.code === 'timeout' || result.code === 'no_response') {
            alert(translate('common.connectionIssues', 'Connection issues detected.'));
            return result;
        }
        if (result.code === 'too_many_attempts') {
            const seconds = Math.max(1, Math.ceil((Number(result.retryAfterMs) || 5000) / 1000));
            /* A toast, never a native dialog: an alert freezes the page and is
               a system modal over the board on a phone. */
            const message = translate(
                'lobby.startThrottled',
                'Starting too quickly — try again in {{seconds}}s.',
            ).replace('{{seconds}}', String(seconds));
            try {
                Lobby?.showStartRefusal?.(message);
            } catch (_) { /* the refusal must never itself throw */ }
        }
        if (result.code === 'server_full') {
            /* The worker holds every live match in memory and is at its cap
               (game/utils/roomCapacity.js). Not the player's fault and not a
               broken button: say so, and say when to try again. */
            const message = translate(
                'lobby.serverFull',
                'The server is full right now. Please try again in a moment.',
            );
            try {
                Lobby?.showStartRefusal?.(message);
            } catch (_) { /* the refusal must never itself throw */ }
        }
        if (result.code === 'too_many_live_games') {
            /* The account already holds its limit of live games on this server
               (game/utils/roomCapacity.js): finishing or leaving one frees it. */
            const message = translate(
                'lobby.tooManyLiveGames',
                'You already have the most games running at once. Finish or leave one to start another.',
            );
            try {
                Lobby?.showStartRefusal?.(message);
            } catch (_) { /* the refusal must never itself throw */ }
        }
        if (result.code === 'room_start_superseded' || result.code === 'room_admission_mismatch') {
            /* Refused by this client, not by the server: the admission was
               cancelled underneath the start (a lobby reset that raced it) or
               the server named a different room than the one admitted. The
               deck selector had already closed, so without a word this is a
               button that did nothing. Say it, and log the code so the next
               report carries it. */
            console.warn('[SocketHandler] Lobby start refused by the client:', result.code);
            const message = translate(
                'lobby.startInterrupted',
                'The room could not be opened. Please try again.',
            );
            try {
                Lobby?.showStartRefusal?.(message);
            } catch (_) { /* the refusal must never itself throw */ }
        }
        return result;
    }
    /**
     * A start refused while a release drains the server (docs/release-process.md,
     * "Step 1"). A battle start waits on its loading screen and is asked again
     * until the new version takes it (ui/server-update-wait.js): at once when
     * the connection comes back, otherwise on the server's retry hint. A start
     * with no screen to wait on (a friend room) says the same words in the
     * lobby's refusal toast. `server_restarting` has its own notice
     * (the serverRestarting event).
     */
    async function startThroughServerUpdate(startOnce) {
        const result = await ServerUpdateWait.run(startOnce, {
            onReconnect: (listener) => {
                socket.on('connect', listener);
                return () => socket.off('connect', listener);
            },
        });
        if (result?.ok === false && result.code === 'server_updating') {
            try {
                Lobby?.showStartRefusal?.(ServerUpdateWait.message());
            } catch (_) { /* the refusal must never itself throw */ }
        }
        return result;
    }

    // Initialize Socket.IO with lazy connection (connect on demand)
    const socketOrigin = (window.__MOONFALL_CONFIG && window.__MOONFALL_CONFIG.socketUrl) || undefined;
    if (!socketOrigin) {
        console.warn('[SocketHandler] socketUrl missing from configuration – defaulting to current origin.');
    }
    // Room affinity: when the server runs clustered, the primary picks the
    // worker by hashing the `room` on the HANDSHAKE. A player joining an
    // existing match must carry the code here -- by the time the later
    // `joinRoom` event fires, the socket is already bound to whichever worker
    // accepted it, which is almost certainly not the one holding the game.
    // Inert on a single-process deployment: the parameter is simply ignored.
    const socketQuery = {};
    const socket = io(socketOrigin, {
        autoConnect: false,
        reconnection: true,
        timeout: 20000,              // Client-side connection timeout
        transports: ['websocket', 'polling'], // Prefer WebSocket
        query: socketQuery
    });

    // A consenting page names the PostHog replay it records, so a server
    // failure in a match lands on that replay (game/utils/requestContext.js):
    // on every connect (the 'connect' handler below), and whenever PostHog
    // learns or rotates the session id (it loads after the game boots).
    // Reading the ids must never disturb play.
    function announceReplay() {
        try {
            const replay = MoonfallAnalytics.replayIds();
            if (replay && replay.sessionId && socket.connected) socket.emit('setReplay', replay);
        } catch (_) { /* analytics is optional */ }
    }
    try { MoonfallAnalytics.onReplayIds(announceReplay); } catch (_) { /* analytics is optional */ }

    /**
     * Pin the NEXT connection to a room's worker. Pass null to clear, so lobby
     * traffic is not pinned to one worker.
     */
    function setRoomAffinity(roomId) {
        // This value is typed in by a player and lands in the URL the primary
        // parses, so strip anything that could add another query parameter.
        const safe = String(roomId ?? '').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
        if (safe) socketQuery.room = safe;
        else delete socketQuery.room;
        // Socket.IO copies query onto the manager at construction; keep the
        // live manager in step so a reconnect carries the same affinity.
        try {
            if (socket.io && socket.io.opts) socket.io.opts.query = { ...socketQuery };
        } catch (_) { /* manager shape varies by version; query above still applies */ }
        return safe || null;
    }

    /**
     * Under the cluster the primary picks a worker from the `room` in the
     * handshake, and a worker asked about a room it does not own answers
     * `wrong_worker` (the match may be alive in the process next door). The
     * only way to reach that room is a fresh connection carrying it: pin, drop
     * the live connection, reconnect, and let the caller retry once. A
     * single-process server never answers this, so nothing here runs there.
     */
    function isWrongWorker(response) {
        return response?.code === 'wrong_worker';
    }

    async function rerouteToRoom(roomId) {
        setRoomAffinity(roomId);
        debugLog('[SocketHandler] Reconnecting so the handshake carries room affinity:', roomId);
        try {
            if (socket.connected) socket.disconnect();
        } catch (_) { /* a dead transport is already what we want */ }
        await ensureConnected();
    }

    // Track basic state
    let activeAuthoritativeScope = null;
    let activeLobbyRoomAdmission = null;
    let lobbyRoomAdmissionGeneration = 0;

    function normalizeAuthoritativeScopeId(value) {
        if (typeof value !== 'string' || !value || value.length > 128) return null;
        return value === value.trim() ? value : null;
    }

    function beginLobbyRoomAdmission(kind, expectedRoomId = null) {
        if (activeLobbyRoomAdmission) cancelLobbyRoomAdmission(activeLobbyRoomAdmission);
        const admission = {
            generation: ++lobbyRoomAdmissionGeneration,
            kind,
            expectedRoomId: expectedRoomId
                ? normalizeAuthoritativeScopeId(expectedRoomId)
                : null,
            acceptedRoomId: null,
        };
        activeLobbyRoomAdmission = admission;
        return admission;
    }

    function lobbyRoomAdmissionIsCurrent(admission) {
        return Boolean(admission && activeLobbyRoomAdmission === admission);
    }

    function cancelLobbyRoomAdmission(admission = null) {
        if (admission && activeLobbyRoomAdmission !== admission) return false;
        const retired = activeLobbyRoomAdmission;
        activeLobbyRoomAdmission = null;
        lobbyRoomAdmissionGeneration += 1;
        if (retired?.kind === 'joined') {
            clearTentativeRoomAffinity(retired.expectedRoomId);
        }
        return true;
    }

    function lobbyRoomEventMatchesAdmission(kind, data) {
        const roomId = normalizeAuthoritativeScopeId(data?.roomId);
        if (!roomId) return false;
        const current = GameState?.get?.() || {};
        if (current.roomId === roomId) return true;
        const admission = activeLobbyRoomAdmission;
        if (!admission || admission.kind !== kind) return false;
        if (admission.expectedRoomId && admission.expectedRoomId !== roomId) return false;
        if (admission.acceptedRoomId && admission.acceptedRoomId !== roomId) return false;
        admission.acceptedRoomId = roomId;
        return true;
    }

    function settleLobbyRoomAdmission(admission, result) {
        if (!lobbyRoomAdmissionIsCurrent(admission)) return result;
        if (result?.ok === true) {
            const roomId = normalizeAuthoritativeScopeId(result.roomId);
            if (roomId) {
                if (
                    (admission.expectedRoomId && admission.expectedRoomId !== roomId)
                    || (admission.acceptedRoomId && admission.acceptedRoomId !== roomId)
                ) {
                    cancelLobbyRoomAdmission(admission);
                    return { ok: false, code: 'room_admission_mismatch' };
                }
                admission.expectedRoomId = roomId;
                pinAcknowledgedStart(result);
            }
        } else if (!isIndeterminateLobbyStart(result)) {
            cancelLobbyRoomAdmission(admission);
        }
        return result;
    }

    function pinAcknowledgedStart(source) {
        const roomId = normalizeAuthoritativeScopeId(source?.roomId);
        const matchId = source?.matchId == null
            ? null
            : normalizeAuthoritativeScopeId(source.matchId);
        const playerId = source?.playerId == null
            ? null
            : normalizeAuthoritativeScopeId(source.playerId);
        if (
            !roomId
            || setRoomAffinity(roomId) !== roomId
            || (source?.matchId != null && !matchId)
            || (source?.playerId != null && !playerId)
        ) return null;
        activeAuthoritativeScope = {
            roomId,
            ...(matchId ? { matchId } : {}),
            ...(playerId ? { playerId } : {}),
        };
        return { ...activeAuthoritativeScope };
    }

    function isIndeterminateLobbyStart(result) {
        return result?.ok === false
            && (
                result.code === 'timeout'
                || result.code === 'no_response'
                || result.code === 'late_timeout'
            );
    }

    function clearTentativeRoomAffinity(roomId) {
        if (!roomId || socketQuery.room !== roomId) return false;
        const currentRoomId = GameState?.get?.()?.roomId || null;
        if (
            currentRoomId === roomId
            || activeAuthoritativeScope?.roomId === roomId
        ) return false;
        setRoomAffinity(null);
        return true;
    }

    function settleJoinRoomResult(result, tentativeRoomId, admission) {
        const settle = (candidate) => {
            // A superseded request may still receive an ACK. It has no
            // authority to change the current room's reconnect routing.
            if (!lobbyRoomAdmissionIsCurrent(admission)) return candidate;
            const settled = settleLobbyRoomAdmission(admission, candidate);
            if (settled?.ok !== true && !isIndeterminateLobbyStart(settled)) {
                clearTentativeRoomAffinity(tentativeRoomId);
            }
            return settled;
        };
        const settledResult = settle(result);
        const lateAck = result?.lateAck;
        if (!lateAck || typeof lateAck.then !== 'function') return settledResult;
        const scopedResult = { ...result };
        Object.defineProperty(scopedResult, 'lateAck', {
            configurable: false,
            enumerable: false,
            value: Promise.resolve(lateAck).then(settle),
        });
        return scopedResult;
    }

    let lastGameState = null;
    let pendingRematchTransition = null;
    let installedRematchTransition = null;
    /* The rematch cover THIS page put up (applyRematchTransition): a failed
       recovery may release only that one, never a loading screen something
       else opened meanwhile (a lobby start after a reload kept a stale hint). */
    let rematchCoverTransition = null;
    let rematchRecoveryTimer = null;
    let rematchRecoveryInFlight = false;
    let rematchRecoveryGeneration = 0;
    let rematchRecoveryAttempt = 0;
    let handleGameStartedPacket = null;
    let handleGameStateUpdatedPacket = null;
    // The match this page's last gameStarted installed, and the one state of a
    // match that arrived before its install packet (reconcileHeldArrival).
    let installedMatchScope = null;
    let heldArrivalSnapshot = null;
    const REMATCH_RECOVERY_STORAGE_KEY = 'moonfall:rematch-recovery:v1';
    const MAX_REMATCH_RECOVERY_TTL_MS = 60 * 60_000;
    const REMATCH_RECOVERY_WATCHDOG_MS = 5_000;
    const REMATCH_RECOVERY_MAX_BACKOFF_MS = 30_000;
    let hasSubmittedMulligan = false;
    let mulliganWarmGeneration = 0;
    let pendingMulliganWarm = null;
    let isTabVisible = true;
    let initialized = false;
    let pendingAutoJoinRoomId = null;
    let gameResumeRetryTimer = null;
    let gameResumeRetryAttempt = 0;
    let explicitLeaveInFlight = null;
    const GAME_RESUME_ACK_TIMEOUT_MS = 10_000;
    /* How long the boot veil waits for a resumed board before showing the
       lobby anyway (see resumeGameFromLifecycle). */
    const GAME_RESUME_BOOT_HOLD_MS = 4_000;
    const GAME_RESUME_MAX_RETRIES = 8;
    // If server warns about upcoming disconnects, suppress generic toast on subsequent 'disconnect'
    let __pendingInactivityDisconnect = false;
    let __pendingTTLDisconnect = false;

    function sameGameSeatScope(left, right) {
        if (!left || !right) return false;
        const leftPlayerId = left.playerId || left.player?.id || null;
        const rightPlayerId = right.playerId || right.player?.id || null;
        return Boolean(
            left.roomId
            && leftPlayerId
            && left.roomId === right.roomId
            && (left.matchId || null) === (right.matchId || null)
            && leftPlayerId === rightPlayerId
        );
    }
    let __pendingServerRestart = false;
    const TUTORIAL_FINALE_VIEW_MS = 5000;
    const MULLIGAN_ACK_TIMEOUT_MS = 5000;
    // The shared loading grace (cards.css `--card-art-grace`): a keep answered
    // inside it shows no processing chrome.
    const KEEP_PROCESSING_GRACE_MS = 450;
    const GAME_ACTION_TYPES = new Set(['summon', 'spell', 'ability']);
    const MAX_SOUNDED_ACTIONS = 256;
    const soundedActionIds = new Set();
    const MAX_RETIRED_OPPONENT_ACTIONS = 64;
    let activeOpponentAction = null;
    const retiredOpponentActionIds = new Set();

    function normalizeRematchScopeId(value) {
        if (typeof value !== 'string' || !value || value.length > 128) return null;
        return value === value.trim() ? value : null;
    }

    function normalizeRematchTransition(source) {
        const transition = {
            previousRoomId: normalizeRematchScopeId(source?.previousRoomId),
            previousMatchId: normalizeRematchScopeId(source?.previousMatchId),
            roomId: normalizeRematchScopeId(source?.roomId),
            matchId: normalizeRematchScopeId(source?.matchId),
        };
        if (Object.values(transition).some((value) => !value)) return null;
        if (
            transition.previousRoomId === transition.roomId
            || transition.previousMatchId === transition.matchId
        ) return null;
        return transition;
    }

    function sameRematchTransition(left, right) {
        return Boolean(
            left
            && right
            && left.previousRoomId === right.previousRoomId
            && left.previousMatchId === right.previousMatchId
            && left.roomId === right.roomId
            && left.matchId === right.matchId
        );
    }

    function readRematchRecoveryHint() {
        try {
            const raw = window.sessionStorage?.getItem(REMATCH_RECOVERY_STORAGE_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            const createdAt = Number(parsed?.createdAt);
            const previousRoomId = normalizeRematchScopeId(parsed?.previousRoomId);
            const previousMatchId = normalizeRematchScopeId(parsed?.previousMatchId);
            const roomId = parsed?.roomId == null ? null : normalizeRematchScopeId(parsed.roomId);
            const matchId = parsed?.matchId == null ? null : normalizeRematchScopeId(parsed.matchId);
            if (
                !previousRoomId
                || !previousMatchId
                || !Number.isFinite(createdAt)
                || createdAt > Date.now() + 60_000
                || Date.now() - createdAt > MAX_REMATCH_RECOVERY_TTL_MS
                || ((roomId || matchId) && (!roomId || !matchId))
            ) {
                window.sessionStorage?.removeItem(REMATCH_RECOVERY_STORAGE_KEY);
                return null;
            }
            return { previousRoomId, previousMatchId, roomId, matchId, createdAt };
        } catch (_) {
            try { window.sessionStorage?.removeItem(REMATCH_RECOVERY_STORAGE_KEY); } catch (_) { }
            return null;
        }
    }

    function rememberRematchRecovery(source) {
        const previousRoomId = normalizeRematchScopeId(source?.previousRoomId);
        const previousMatchId = normalizeRematchScopeId(source?.previousMatchId);
        if (!previousRoomId || !previousMatchId) return false;
        const transition = normalizeRematchTransition(source);
        const existing = readRematchRecoveryHint();
        const hint = {
            previousRoomId,
            previousMatchId,
            roomId: transition?.roomId || null,
            matchId: transition?.matchId || null,
            createdAt: existing?.previousRoomId === previousRoomId
                && existing?.previousMatchId === previousMatchId
                ? existing.createdAt
                : Date.now(),
        };
        try {
            window.sessionStorage?.setItem(REMATCH_RECOVERY_STORAGE_KEY, JSON.stringify(hint));
        } catch (_) { /* recovery remains available in memory */ }
        return true;
    }

    function stopRematchRecovery() {
        rematchRecoveryGeneration += 1;
        if (rematchRecoveryTimer) clearTimeout(rematchRecoveryTimer);
        rematchRecoveryTimer = null;
        rematchRecoveryInFlight = false;
        rematchRecoveryAttempt = 0;
        pendingRematchTransition = null;
        rematchCoverTransition = null;
        try { window.sessionStorage?.removeItem(REMATCH_RECOVERY_STORAGE_KEY); } catch (_) { }
    }

    function clearRematchRecovery({ clearInstalled = true } = {}) {
        stopRematchRecovery();
        if (clearInstalled) installedRematchTransition = null;
    }

    function markRematchInstalled(transition) {
        stopRematchRecovery();
        installedRematchTransition = transition || installedRematchTransition;
    }

    async function acknowledgeRematchInstalled(source) {
        const transition = normalizeRematchTransition(source);
        if (!transition) return false;
        try {
            const authToken = await getRequiredAuthToken();
            const current = GameState?.get?.() || {};
            if (
                !sameRematchTransition(installedRematchTransition, transition)
                || current.roomId !== transition.roomId
                || current.matchId !== transition.matchId
            ) return false;
            const response = await emitWithAck('ackRematchInstalled', {
                ...transition,
                authToken,
            }, 4_000);
            return response?.ok === true;
        } catch (_) {
            return false;
        }
    }

    function applyRematchTransition(source) {
        const transition = normalizeRematchTransition(source);
        if (!transition) return false;
        const current = GameState?.get?.() || {};
        if (sameRematchTransition(installedRematchTransition, transition)) return true;
        if (
            current.roomId === transition.roomId
            && current.matchId === transition.matchId
            && sameRematchTransition(pendingRematchTransition, transition)
        ) return true;
        if (
            current.roomId !== transition.previousRoomId
            || current.matchId !== transition.previousMatchId
        ) return false;

        pendingRematchTransition = transition;
        rememberRematchRecovery(transition);
        resetSocketMatchTracking({ preserveRematchRecovery: true });
        /* THE FINISHED BOARD IS COVERED BEFORE IT IS TORN DOWN. rematchStarted
           and gameStarted are two socket messages (two tasks), so a frame
           between them showed a gutted battlefield — boards, hands and piles
           emptied under stale portraits — and the new summoners' portraits
           then loaded in front of the player. The match loading screen is
           opaque from its first frame; the new board builds under it and the
           mulligan's reveal lifts it (ui/match-loading.js). */
        runGameStartedBestEffort('rematch cover', () => MatchLoading?.begin?.());
        rematchCoverTransition = transition;
        GameEnd?.prepareForNextMatch?.({ socketTrackingAlreadyReset: true });
        GameState.update({
            roomId: transition.roomId,
            matchId: transition.matchId,
            state: 'rematch_transition',
            winner: null,
            endReason: null,
        });
        MoonfallStorybookContext?.setFromGameState?.({
            gameState: { roomId: transition.roomId, matchId: transition.matchId },
        });
        armRematchRecoveryWatchdog({ resetBackoff: true });
        return true;
    }

    function canClearRecoveryForOldScope(roomId, matchId) {
        const current = GameState?.get?.() || {};
        const transitionOwnsScope = (transition) => Boolean(
            transition
            && transition.previousRoomId === roomId
            && transition.previousMatchId === matchId
        );
        return current.roomId === roomId
            && current.matchId === matchId
            && !transitionOwnsScope(pendingRematchTransition)
            && !transitionOwnsScope(installedRematchTransition);
    }

    async function attemptRematchRecovery() {
        const hint = readRematchRecoveryHint();
        if (!hint || !socket.connected || rematchRecoveryInFlight) return false;
        rematchRecoveryInFlight = true;
        const generation = rematchRecoveryGeneration;
        const clientActionId = createClientActionId();
        let recovered = false;
        try {
            const authToken = await getRequiredAuthToken();
            if (generation !== rematchRecoveryGeneration || !socket.connected) return false;
            const response = await emitWithAck('recoverRematch', {
                previousRoomId: hint.previousRoomId,
                previousMatchId: hint.previousMatchId,
                clientActionId,
                authToken,
            }, 8_000);
            if (
                generation !== rematchRecoveryGeneration
                || response?.action !== 'recoverRematch'
                || response?.clientActionId !== clientActionId
                || response?.ok !== true
            ) return false;
            const transition = normalizeRematchTransition(response.transition);
            const gameStarted = response.gameStarted;
            if (
                !transition
                || transition.previousRoomId !== hint.previousRoomId
                || transition.previousMatchId !== hint.previousMatchId
                || (hint.roomId && transition.roomId !== hint.roomId)
                || (hint.matchId && transition.matchId !== hint.matchId)
                || gameStarted?.gameState?.roomId !== transition.roomId
                || gameStarted?.gameState?.matchId !== transition.matchId
                || !gameStarted?.gameState?.player
            ) return false;
            if (!applyRematchTransition(transition)) return false;
            handleGameStartedPacket?.({ ...gameStarted, rematchTransition: transition });
            recovered = sameRematchTransition(installedRematchTransition, transition);
            return recovered;
        } catch (_) {
            return false;
        } finally {
            if (generation === rematchRecoveryGeneration) {
                rematchRecoveryInFlight = false;
                if (!recovered && socket.connected && readRematchRecoveryHint()) {
                    /* The rematch cover (applyRematchTransition) waited for a
                       gameStarted this attempt could not deliver: give the
                       player the board and its menu back while recovery
                       keeps trying behind it. Only the cover this page's
                       rematch put up: a stale hint (a reload mid-rematch)
                       never takes down a loading screen a lobby start opened. */
                    if (rematchCoverTransition && sameRematchTransition(rematchCoverTransition, pendingRematchTransition)) {
                        rematchCoverTransition = null;
                        runGameStartedBestEffort('rematch cover release', () => MatchLoading?.cancel?.());
                    }
                    armRematchRecoveryWatchdog();
                }
            }
        }
    }

    function armRematchRecoveryWatchdog({ resetBackoff = false } = {}) {
        if (resetBackoff) rematchRecoveryAttempt = 0;
        if (rematchRecoveryTimer) clearTimeout(rematchRecoveryTimer);
        const delayMs = Math.min(
            REMATCH_RECOVERY_MAX_BACKOFF_MS,
            REMATCH_RECOVERY_WATCHDOG_MS * (2 ** rematchRecoveryAttempt),
        );
        rematchRecoveryTimer = setTimeout(() => {
            rematchRecoveryTimer = null;
            rematchRecoveryAttempt += 1;
            void attemptRematchRecovery();
        }, delayMs);
    }

    function warmCardsWithoutBlocking(cards) {
        if (!Array.isArray(cards) || !AssetPreloader?.warmCards) return null;
        try {
            const warm = AssetPreloader.warmCards(cards, { priority: 'high' });
            if (warm && typeof warm.catch === 'function') {
                warm.catch(() => { /* art warm failure never blocks gameplay */ });
            }
            return warm;
        } catch (_) {
            return null;
        }
    }

    function warmOpponentActionCards(data, cards) {
        const state = GameState.get() || {};
        if (!data?.playerId || !state.playerId || data.playerId === state.playerId) return null;
        return warmCardsWithoutBlocking(cards);
    }

    function replacementCards(beforeHand, afterHand) {
        const previousIds = new Set((beforeHand || [])
            .map(card => card?.instanceId)
            .filter(instanceId => typeof instanceId === 'string' && instanceId.length > 0));
        return (afterHand || []).filter(card => {
            const instanceId = card?.instanceId;
            return typeof instanceId !== 'string' || !previousIds.has(instanceId);
        });
    }

    function clearMulliganWarm({ releaseHand = true } = {}) {
        mulliganWarmGeneration += 1;
        if (releaseHand) {
            Mulligan?.releasePlayerHand?.(pendingMulliganWarm?.handHoldToken);
        }
        pendingMulliganWarm = null;
        hasSubmittedMulligan = false;
    }

    function captureMulliganSubmission(state) {
        clearMulliganWarm({ releaseHand: false });
        const generation = mulliganWarmGeneration;
        pendingMulliganWarm = {
            generation,
            roomId: state.roomId,
            beforeHand: Array.isArray(state.player?.hand) ? state.player.hand.slice() : [],
            handHoldToken: Mulligan?.holdPlayerHand?.(),
            gate: null,
            revealScheduled: false,
            accepted: false,
        };
        hasSubmittedMulligan = true;
        return pendingMulliganWarm;
    }

    function recoverFailedMulliganSubmission(submission) {
        if (!submission || pendingMulliganWarm !== submission
            || submission.generation !== mulliganWarmGeneration) {
            return false;
        }
        clearMulliganWarm();
        try {
            Mulligan?.hideWaitingBanner?.();
            const current = GameState.get() || {};
            if (current.roomId === submission.roomId && current.state === 'mulligan') {
                Mulligan?.show?.(
                    Array.isArray(current.player?.hand) ? current.player.hand : [],
                    current.opponentMulliganReady === true,
                    {
                        timeoutMs: current.mulliganTimeoutMs,
                        deadlineAt: current.mulliganDeadlineAt,
                        serverNow: current.serverNow,
                    },
                );
            }
        } catch (err) {
            console.error('[SocketHandler] Failed to restore mulligan UI:', err);
        }
        return true;
    }

    function beginAuthoritativeMulliganWarm(gameState, player) {
        const pending = pendingMulliganWarm;
        if (!hasSubmittedMulligan || !pending) return pending;
        if (gameState?.roomId !== pending.roomId || gameState?.mulliganReady !== true) return null;
        pending.accepted = true;
        if (pending.gate) {
            warmTurnStartArrivals(pending, player);
            return pending;
        }

        pending.handHoldToken = Mulligan?.holdPlayerHand?.() || pending.handHoldToken;
        const cards = mulliganReplacements(pending.beforeHand, player?.hand);
        pending.warmedIds = new Set(cards.map(card => card?.instanceId));
        const warm = warmCardsWithoutBlocking(cards) || Promise.resolve();
        try {
            pending.gate = AssetPreloader?.gateWarm?.(warm) || Promise.resolve(warm);
        } catch (_) {
            pending.gate = Promise.resolve();
        }
        // A first view that is already the first turn names the turn-start
        // draw too: warmed now, at high priority, and never waited for.
        warmTurnStartArrivals(pending, player);
        return pending;
    }

    /* THE GATE NAMES THE MULLIGAN'S REPLACEMENTS, NEVER THE DRAW (round 15, 28
       September 2026). When the opponent was ready first, the first view after
       keep is already the first turn, and its hand holds the turn-start draw
       beside the replacements. Every new card used to join the gate, so the
       kept, already-warm hand waited up to the preloader's cap (450 ms) behind
       a stale "Waiting for opponent" banner for the draw's picture (WebKit
       phone mulligan-keep 1.0 -> 2.2-2.7 s).

       The server deals one replacement per card that left the hand, then the
       draw after them (game/managers/mulliganManager.js handleChoice, then
       turnManager.startTurn), so the replacements are the first new cards, as
       many as the kept hand lost. Counted from the server's hand, not from the
       client's choice: the tutorial forces its own discards. */
    function mulliganReplacements(beforeHand, afterHand) {
        const afterIds = new Set((afterHand || []).map(card => card?.instanceId));
        const discarded = (beforeHand || [])
            .filter(card => typeof card?.instanceId === 'string' && !afterIds.has(card.instanceId))
            .length;
        return replacementCards(beforeHand, afterHand).slice(0, discarded);
    }

    /* THE TURN-START DRAW IS WARMED, NEVER WAITED FOR (25 September 2026,
       WebKit phone revisit: the drawn card's picture loaded in front for
       595 ms). A player who keeps before the opponent gets the gate above from
       their own ready view, which names only the kept and replacement cards;
       the card that opens the first turn is named later, by the 'playing'
       snapshot. Its picture starts here, before the rebuild creates the card:
       high priority from the first request (a deck warm already in flight is
       raised), and decoded pixels back in the sampler if the LRU dropped them.

       It never joins the reveal gate. The gate is for the cards the mulligan
       named, and the kept ones are warm already. Holding them and the waiting
       banner for the draw (tried the same day) cost the whole hand up to the
       preloader's cap on a cold phone and bought nothing there: the drawn
       card has its own arrival, held off its seat until its face has settled
       and then flown in from the deck (cards.js renderPlayerHand).

       Only the player's own hand, from the player's own view. */
    function warmTurnStartArrivals(pending, player) {
        const warmedIds = pending.warmedIds || new Set();
        pending.warmedIds = warmedIds;
        const arrivals = replacementCards(pending.beforeHand, player?.hand)
            .filter(card => !warmedIds.has(card?.instanceId));
        if (!arrivals.length) return;
        arrivals.forEach(card => warmedIds.add(card?.instanceId));
        warmCardsWithoutBlocking(arrivals);
    }

    function scheduleMulliganHandReveal(pending, gameState) {
        if (gameState?.state !== 'playing') return false;
        /* A reveal still waiting on its gate owns the waiting banner and the
           held hand. A later snapshot inside that wait used to take the banner
           down with the hand still held: a frame with neither. */
        const waiting = pendingMulliganWarm;
        if (waiting?.revealScheduled && waiting.roomId === gameState.roomId) return true;
        if (!pending || !pending.gate || pending.revealScheduled) return false;
        pending.revealScheduled = true;
        Promise.resolve(pending.gate).catch(() => null).then(() => {
            if (pendingMulliganWarm !== pending || pending.generation !== mulliganWarmGeneration) return;
            if ((GameState.get() || {}).roomId !== pending.roomId) return;

            if (Mulligan?.isActive?.()) {
                if (Tutorial?.isActive?.()) {
                    Tutorial.onGameEvent('mulliganComplete');
                }
                Mulligan.hide({ keepHandHidden: true });
            }
            Mulligan?.releasePlayerHand?.(pending.handHoldToken);
            Mulligan?.hideWaitingBanner?.();
            pendingMulliganWarm = null;
            hasSubmittedMulligan = false;
        });
        return true;
    }

    // Debounce helper - prevents multiple rapid-fire events
    const debounceTimers = {};
    let pendingPlayAction = null;
    let pendingEndTurn = null;
    let pendingLegacyGameplayOwner = null;
    let sharedActionLockOwner = null;
    let recentPlayActionFailure = null;
    let recentEndTurnFailure = null;
    let clientActionSequence = 0;
    const ACTION_INTENT_CANCEL_ACK_TIMEOUT_MS = 10000;
    // The authoritative action-intent lane may legitimately spend up to 120s
    // queued/resolving. Keep the client owner alive beyond that ceiling so it
    // never abandons a server-side pending intent and becomes ownerlessly busy.
    const ACTION_INTENT_REQUEST_TIMEOUT_MS = 150000;
    const LEGACY_ACTION_TIMEOUT_MS = 150000;
    const END_TURN_TIMEOUT_MS = 150000;
    const RECENT_PLAY_ACTION_FAILURE_MS = 2000;
    const RECENT_END_TURN_FAILURE_MS = 2000;
    function debounce(key, fn, delay) {
        if (debounceTimers[key]) {
            clearTimeout(debounceTimers[key]);
        }
        debounceTimers[key] = setTimeout(() => {
            fn();
            debounceTimers[key] = null;
        }, delay);
    }

    // Detect fatal Gemini cache-related messages that require a hard stop
    function isFatalGeminiCacheMessage(m) {
        if (!m || typeof m !== 'string') return false;
        const needles = [
            'Caches API not available',
            'Failed to create explicit context cache',
            'Explicit context cache unavailable after reset'
        ];
        return needles.some(n => m.includes(n));
    }

    function normalizeSocketError(error, fallbackMessage = 'Socket action failed') {
        if (error instanceof Error) return error;
        const message = error && (error.message || error.msg || (typeof error === 'string' ? error : null));
        const normalized = new Error(message || fallbackMessage);
        normalized.originalError = error;
        return normalized;
    }

    function createClientActionId() {
        clientActionSequence += 1;
        return `client-action-${Date.now().toString(36)}-${clientActionSequence.toString(36)}`;
    }

    /**
     * Server refusals the player is meant to READ, keyed by the stable code the
     * server sends. Two entries kept their original lobby.* keys because those
     * translations already exist in all 8 languages; everything else lives
     * under socketError.*.
     *
     * The server is the authority on WHICH refusal happened; this map only
     * decides how it is worded for this player's language. An unknown code
     * falls through to the server's own English text rather than being
     * swallowed — a message the player cannot read still beats no message.
     */
    const SOCKET_ERROR_TRANSLATIONS = {
        same_account_not_allowed: ['lobby.sameAccountNotAllowed',
            'You cannot join your own multiplayer room. Use a different account for the second player.'],
        monthly_usage_cap_timeout: ['lobby.gameStartTimedOut',
            'Game creation took too long. Please try again.'],
        game_not_found: ['socketError.gameNotFound', 'Game not found.'],
        wrong_worker: ['socketError.wrongWorker', 'Your game is on another server; reconnect to reach it.'],
        match_no_longer_active: ['socketError.matchNoLongerActive',
            'The requested match is no longer active.'],
        not_part_of_game: ['socketError.notPartOfGame', 'You are not part of this game.'],
        player_not_in_game: ['socketError.playerNotInGame', 'Player not found in game.'],
        invalid_action_context: ['socketError.invalidActionContext', 'Invalid action context.'],
        sign_in_to_change_summoner: ['socketError.signInToChangeSummoner',
            'Sign in to change summoner.'],
        follow_tutorial_move: ['socketError.followTutorialMove',
            'Follow the highlighted tutorial move.'],
        tutorial_unavailable: ['socketError.tutorialUnavailable',
            'Tutorial continuation is unavailable right now.'],
        tutorial_unavailable_temporary: ['socketError.tutorialUnavailableTemporary',
            'Tutorial is temporarily unavailable.'],
        tutorial_start_failed: ['socketError.tutorialStartFailed', 'Failed to start Tutorial.'],
        character_unavailable: ['socketError.characterUnavailable', 'Character is not available.'],
        character_locked_during_action: ['socketError.characterLockedDuringAction',
            'Cannot change character while an action is in progress.'],
        // Reuses the existing game.aiTurnFailed string rather than minting a
        // second one — its copy is better ('Play continues.') and the error
        // handler below already special-cases this code.
        ai_turn_failed: ['game.aiTurnFailed',
            'The opposing AI turn could not complete. Play continues.'],
        turn_followup_failed: ['socketError.turnFollowupFailed', 'Turn follow-up failed.'],
        // The storyteller's durable workflow ended without an answer (provider
        // outage, an attempt aborted at its time budget, an invalid reply) or
        // the wait for it ran out. The server's message names the queue
        // category and status for the log; the player gets one sentence.
        story_queue_failed: ['socketError.storytellerFailed',
            'The storyteller could not respond. Try again.'],
        story_queue_timeout: ['socketError.storytellerTimedOut',
            'The storyteller did not respond in time. Try again.'],
        invalid_action_intent_options: ['socketError.storytellerIntentsUnusable',
            'The storyteller offered no usable intents. Try again.'],
    };

    function socketErrorMessage(error, fallback = 'Action failed') {
        const entry = SOCKET_ERROR_TRANSLATIONS[error?.code];
        if (entry) return translate(entry[0], entry[1]);
        return error?.message || error?.msg || (typeof error === 'string' ? error : fallback);
    }

    function isCurrentActionContext(roomId, matchId) {
        const current = GameState?.get?.() || {};
        if (!roomId || current.roomId !== roomId) return false;
        if ((current.matchId || null) !== (matchId || null)) return false;
        return true;
    }

    function isExactActionCorrelation(payload, action, clientActionId) {
        return payload?.action === action
            && typeof payload?.clientActionId === 'string'
            && payload.clientActionId === clientActionId;
    }

    function hasPendingGameplayOwner() {
        return Boolean(pendingPlayAction || pendingEndTurn || pendingLegacyGameplayOwner);
    }

    function isGameplayBusy(state = GameState?.get?.() || {}) {
        return hasPendingGameplayOwner() || state?.actionInProgress === true;
    }

    function ownerMatchesCurrentContext(owner) {
        if (!owner) return false;
        return isCurrentActionContext(owner.roomId, owner.matchId);
    }

    function ownerMatchesCurrentTurn(owner) {
        if (!ownerMatchesCurrentContext(owner)) return false;
        if (owner.expectedTurn === null || owner.expectedTurn === undefined) return true;
        return GameState?.get?.()?.currentTurn === owner.expectedTurn;
    }

    function createLegacyGameplayOwner(action, state, options = {}) {
        return {
            action,
            completionAction: options.completionAction || action,
            clientActionId: createClientActionId(),
            roomId: state?.roomId || null,
            matchId: state?.matchId || null,
            expectedTurn: Number.isInteger(state?.currentTurn) ? state.currentTurn : null,
            requestId: options.requestId || null,
            timeoutId: null,
            settled: false,
            uiLocked: false,
            cleanup: null,
            retire: null,
            handleSocketError: null,
            handleActionComplete: null,
        };
    }

    function isCurrentLegacyGameplayOwner(owner) {
        return Boolean(owner)
            && pendingLegacyGameplayOwner === owner
            && owner.settled !== true;
    }

    function acquireLegacyGameplayOwner(owner, lockOptions) {
        if (!owner || hasPendingGameplayOwner()) return false;
        pendingLegacyGameplayOwner = owner;
        acquireSharedActionLock(owner, lockOptions);
        return true;
    }

    function transferLegacyGameplayOwner(previousOwner, nextOwner) {
        if (!isCurrentLegacyGameplayOwner(previousOwner) || !nextOwner) return false;
        previousOwner.cleanup?.();
        previousOwner.settled = true;
        previousOwner.uiLocked = false;
        pendingLegacyGameplayOwner = nextOwner;
        if (sharedActionLockOwner === previousOwner) {
            sharedActionLockOwner = nextOwner;
            nextOwner.uiLocked = true;
        } else {
            acquireSharedActionLock(nextOwner);
        }
        return true;
    }

    function releaseLegacyGameplayOwner(owner) {
        if (!isCurrentLegacyGameplayOwner(owner)) return false;
        owner.settled = true;
        owner.cleanup?.();
        pendingLegacyGameplayOwner = null;
        if (owner.uiLocked) releaseSharedActionLock(owner);
        return true;
    }

    function settleLegacyGameplayOwner(owner, details = {}) {
        if (!releaseLegacyGameplayOwner(owner)) return false;
        if (details.recover) requestFreshGameStateForRecovery(owner.roomId, owner.matchId);
        owner.onSettled?.(details);
        return true;
    }

    function armCorrelatedLegacyGameplayOwner(owner, options = {}) {
        const completionAction = options.completionAction || owner.completionAction;
        owner.onSettled = options.onSettled || null;
        owner.timeoutMs = options.timeoutMs || LEGACY_ACTION_TIMEOUT_MS;
        owner.timeoutMessage = options.timeoutMessage || 'Action timed out';
        owner.onTimeout = options.onTimeout || null;
        owner.pauseTimeout = () => {
            if (owner.timeoutId) clearTimeout(owner.timeoutId);
            owner.timeoutId = null;
        };
        owner.cleanup = owner.pauseTimeout;
        owner.rearmTimeout = () => {
            if (!isCurrentLegacyGameplayOwner(owner)) return false;
            owner.pauseTimeout();
            owner.timeoutId = setTimeout(() => {
                owner.timeoutId = null;
                if (owner.onTimeout?.() === true) return;
                settleLegacyGameplayOwner(owner, {
                    error: new Error(owner.timeoutMessage),
                    reason: 'timeout',
                    recover: true,
                });
            }, owner.timeoutMs);
            return true;
        };
        owner.retire = (error) => settleLegacyGameplayOwner(owner, {
            error: error || new Error('The game changed before the action completed.'),
            reason: 'retired',
        });
        owner.handleSocketError = (error) => {
            if (!isExactActionCorrelation(error, owner.action, owner.clientActionId)) return false;
            return settleLegacyGameplayOwner(owner, { error, reason: 'server-error', recover: true });
        };
        owner.handleActionComplete = (data) => {
            if (!isExactActionCorrelation(data, completionAction, owner.clientActionId)) return false;
            return settleLegacyGameplayOwner(owner, { data, reason: 'complete' });
        };
        owner.handleUndoComplete = (data) => {
            if (!isExactActionCorrelation(data, owner.action, owner.clientActionId)) return false;
            return settleLegacyGameplayOwner(owner, { data, reason: 'complete' });
        };
        owner.rearmTimeout();
        return owner;
    }

    function cancelPendingActionIntent(owner, requestId) {
        return new Promise((resolve, reject) => {
            if (!isCurrentLegacyGameplayOwner(owner)
                || owner.action !== 'requestActionIntentOptions'
                || owner.requestId !== requestId) {
                reject(new Error(hasPendingGameplayOwner()
                    ? 'Action already in progress'
                    : 'Action intent request is no longer active'));
                return;
            }
            if (owner.cancelOperation && !owner.cancelOperation.settled) {
                reject(new Error('Action intent cancellation is already in progress'));
                return;
            }

            const operation = {
                clientActionId: createClientActionId(),
                settled: false,
                timeoutId: null,
                reject,
            };
            owner.cancelOperation = operation;
            owner.cancelUncertain = false;
            owner.pauseTimeout?.();

            const settleCancel = (callback) => {
                if (operation.settled || owner.cancelOperation !== operation) return false;
                operation.settled = true;
                if (operation.timeoutId) clearTimeout(operation.timeoutId);
                operation.timeoutId = null;
                owner.cancelOperation = null;
                callback();
                return true;
            };
            const rejectCancellation = (error, { uncertain = false, recover = false } = {}) => {
                return settleCancel(() => {
                    owner.cancelUncertain = uncertain;
                    owner.rearmTimeout?.();
                    if (recover) requestFreshGameStateForRecovery(owner.roomId, owner.matchId);
                    reject(error);
                });
            };

            // Bound the whole cancellation attempt, including connection and
            // credential lookup. Starting this only after auth would let a
            // stalled token refresh pause the intent owner indefinitely.
            operation.timeoutId = setTimeout(() => {
                rejectCancellation(
                    new Error('Cancel acknowledgement timed out'),
                    { uncertain: true, recover: true },
                );
            }, ACTION_INTENT_CANCEL_ACK_TIMEOUT_MS);

            ensureConnected()
                .then(async () => {
                    if (!isCurrentLegacyGameplayOwner(owner)
                        || owner.cancelOperation !== operation) return;
                    if (!ownerMatchesCurrentContext(owner)) {
                        owner.retire?.(new Error('The game changed before cancellation completed.'));
                        return;
                    }
                    const payload = await withRequiredAuthPayload({
                        roomId: owner.roomId,
                        ...(owner.matchId ? { matchId: owner.matchId } : {}),
                        clientActionId: operation.clientActionId,
                        requestId,
                    });
                    if (!isCurrentLegacyGameplayOwner(owner)
                        || owner.cancelOperation !== operation) return;
                    if (!ownerMatchesCurrentContext(owner)) {
                        owner.retire?.(new Error('The game changed before cancellation completed.'));
                        return;
                    }
                    socket.emit('cancelActionIntentRequest', payload, (response) => {
                        if (!isExactActionCorrelation(
                            response,
                            'cancelActionIntentRequest',
                            operation.clientActionId,
                        )) return;
                        if (response?.error) {
                            rejectCancellation(new Error(response.error));
                            return;
                        }
                        if (!response?.ok) return;
                        settleCancel(() => {
                            owner.cancelUncertain = false;
                            releaseLegacyGameplayOwner(owner);
                            resolve();
                        });
                    });
                })
                .catch((error) => {
                    rejectCancellation(error || new Error('Failed to connect'));
                });
        });
    }

    function reconcilePendingActionIntentCancellation(serverState) {
        const owner = pendingLegacyGameplayOwner;
        if (!isCurrentLegacyGameplayOwner(owner)
            || owner.action !== 'requestActionIntentOptions'
            || owner.cancelUncertain !== true
            || serverState?.actionInProgress !== false
            || serverState?.roomId !== owner.roomId
            || (serverState?.matchId || null) !== (owner.matchId || null)) {
            return false;
        }
        const didSettle = settleLegacyGameplayOwner(owner, {
            data: { ok: true },
            reason: 'authoritative-cancelled',
        });
        if (didSettle) ActionIntent?.clear?.('cancelled');
        return didSettle;
    }

    function legacyOwnerCanEmit(owner, { requireTurn = true } = {}) {
        if (!isCurrentLegacyGameplayOwner(owner) || !socket.connected) return false;
        return requireTurn ? ownerMatchesCurrentTurn(owner) : ownerMatchesCurrentContext(owner);
    }

    /**
     * Wait for the emit window rather than dropping the player's action.
     *
     * `legacyOwnerCanEmit` closes for two very different reasons, and the
     * pre-emit checks treated them identically:
     *
     *   the turn/match moved on  -> the action is stale; abandon it (correct)
     *   the socket dropped       -> says nothing about the action
     *
     * On a phone the second happens constantly: backgrounding the app
     * disconnects the socket, and the emit sites `await` a Firebase token
     * first, so a player who taps Summon and glances at another app comes back
     * to find the summon simply never happened. ("If i initiated an action it
     * should happen even if i am personally minimized at the time.")
     *
     * This is NOT a retry. The packet was never put on the wire — the old code
     * abandoned it BEFORE emitting — so waiting for the connection and then
     * emitting cannot duplicate anything. That distinction matters here because
     * `clientActionId` is correlation only; the server has no dedupe to lean on
     * if a duplicate were ever possible.
     */
    async function awaitLegacyEmitWindow(owner, options) {
        if (legacyOwnerCanEmit(owner, options)) return true;
        // Anything other than the transport means the action is genuinely stale.
        if (!isCurrentLegacyGameplayOwner(owner)) return false;
        const stillRelevant = options?.requireTurn === false
            ? ownerMatchesCurrentContext(owner)
            : ownerMatchesCurrentTurn(owner);
        if (!stillRelevant) return false;
        if (socket.connected) return false;
        try {
            await ensureConnected();
        } catch (_) {
            return false;
        }
        // Re-check everything: the reconnect may have landed in a new turn.
        return legacyOwnerCanEmit(owner, options);
    }

    function acquireSharedActionLock(owner, options) {
        sharedActionLockOwner = owner;
        owner.uiLocked = true;
        if (options === undefined) GameState.setActionInProgress(true);
        else GameState.setActionInProgress(true, options);
    }

    function releaseSharedActionLock(owner) {
        if (sharedActionLockOwner !== owner) return false;
        sharedActionLockOwner = null;
        if (!hasPendingGameplayOwner() && ownerMatchesCurrentContext(owner)) {
            GameState.setActionInProgress(false);
        }
        return true;
    }

    function retirePendingGameplayOwnersOutsideContext(roomId, matchId) {
        const matches = (owner) => Boolean(
            owner
            && owner.roomId === roomId
            && owner.matchId === matchId
        );
        if (pendingPlayAction && !matches(pendingPlayAction)) {
            pendingPlayAction.retire?.();
        }
        if (pendingEndTurn && !matches(pendingEndTurn)) {
            pendingEndTurn.retire?.();
        }
        if (pendingLegacyGameplayOwner && !matches(pendingLegacyGameplayOwner)) {
            pendingLegacyGameplayOwner.retire?.();
        }
    }

    function retireAllPendingGameplayOwners(error) {
        pendingPlayAction?.retire?.(error);
        pendingEndTurn?.retire?.(error);
        pendingLegacyGameplayOwner?.retire?.(error);
    }

    function syncPendingGameplayOwnersToCurrentContext(state = GameState?.get?.() || {}) {
        const current = state || {};
        retirePendingGameplayOwnersOutsideContext(
            current.roomId || null,
            current.matchId || null,
        );
    }

    function rememberPlayActionFailure(owner, error) {
        recentPlayActionFailure = {
            action: 'playAction',
            clientActionId: owner?.clientActionId || error?.clientActionId || null,
            message: socketErrorMessage(error),
            expiresAt: Date.now() + RECENT_PLAY_ACTION_FAILURE_MS,
        };
    }

    function consumeRecentPlayActionFailure(error) {
        const recent = recentPlayActionFailure;
        if (!recent || Date.now() > recent.expiresAt) {
            recentPlayActionFailure = null;
            return false;
        }
        const matches = isExactActionCorrelation(
            error,
            recent.action,
            recent.clientActionId,
        );
        if (!matches) return false;
        recentPlayActionFailure = null;
        return true;
    }

    function rememberEndTurnFailure(owner, error) {
        recentEndTurnFailure = {
            action: 'endTurn',
            clientActionId: owner?.clientActionId || error?.clientActionId || null,
            message: socketErrorMessage(error),
            expiresAt: Date.now() + RECENT_END_TURN_FAILURE_MS,
        };
    }

    function consumeRecentEndTurnFailure(error) {
        const recent = recentEndTurnFailure;
        if (!recent || Date.now() > recent.expiresAt) {
            recentEndTurnFailure = null;
            return false;
        }
        const matches = isExactActionCorrelation(
            error,
            recent.action,
            recent.clientActionId,
        );
        if (!matches) return false;
        recentEndTurnFailure = null;
        return true;
    }

    function syncEndTurnButton({ keepDisabled = false } = {}) {
        const button = document.getElementById('end-turn-btn');
        if (!button) return;
        button.removeAttribute('aria-busy');
        if (keepDisabled) {
            button.disabled = true;
            return;
        }
        const state = GameState?.get?.() || {};
        button.disabled = state.state === 'mulligan'
            || state.isYourTurn !== true
            || state.actionInProgress === true;
    }

    /* Why this player's moon is dark, when they could not guess it: this
       month's refills are already used (or one for a legacy monthly cycle),
       or their mailbox already had its welcome on an earlier account. */
    function moonlightWallReason(status) {
        /* A pass on hold for a disputed payment reads as free: its dark moon
           is the free one, and the reason is the hold. */
        if (status?.passOnHold === true) {
            return accountOnHoldCopy(status, translate).line;
        }
        if (status?.refillUsedThisCycle === true) {
            let date = '';
            const opensAt = Number(status?.refillsOpenAt);
            if (Number.isFinite(opensAt) && opensAt > 0) {
                try {
                    date = new Date(opensAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
                } catch (_) { date = ''; }
            }
            return translate(
                status?.weeklyReset === true ? 'moonlight.refillUsedThisMonth' : 'moonlight.refillUsedThisPeriod',
                status?.weeklyReset === true
                    ? `You've used all four refills this month. Refills open again ${date}. Weekly resets do not reset this limit.`
                    : `You've used this billing period's refill. Refills open again when your next billing period starts (${date}).`,
                { date },
            );
        }
        if (status?.welcomeWithheld === true) {
            return translate(
                'moonlight.welcomeWithheld',
                "This email already had its welcome gift on an earlier account, so this account's moon starts empty and fills a little each week.",
            );
        }
        return '';
    }

    /* The game server moved the Moonlight out of this client's sight (a match
       spent it, or a start was refused for it). Whoever shows or shares it
       listens: the lobby chip re-reads (main.js), the shared answer is dropped
       (convex-client.js), the picker's statuses too (lobby.js). */
    function announceMoonlightMoved() {
        try {
            window.dispatchEvent(new CustomEvent('moonfall:moonlight-stale'));
        } catch (error) {
            console.warn('[SocketHandler] Could not announce the Moonlight change:', error);
        }
    }

    /* Explains the refusal the server just made. Best-effort: the status read
       is only used to pick WHICH single action is offered, and a failed read
       falls back to the tier offer — never to a Refill, which `canRefill`
       would refuse for an account with no plan. */
    function raiseMoonlightWall() {
        /* The refusal is news about the balance: the server just measured it
           and found it short. The answer the client shares for a few seconds
           (convex-client.js) may predate it and pick the wrong offer (a
           Refill this cycle no longer has), and the lobby's chip would keep a
           moon the server calls too empty. Said first, the wall's read and
           the chip's are one fresh read. */
        announceMoonlightMoved();
        Promise.resolve()
            .then(() => MoonfallCloud?.users?.getMoonlightStatus?.())
            .catch(() => null)
            .then((status) => {
                const resetAt = Number(status?.returnsAt);
                const resetDate = Number.isFinite(resetAt) && resetAt > 0
                    ? new Date(resetAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric' }) : '';
                openMoonlightWall({
                    anchor: document.getElementById('solo-adventure-btn'),
                    status,
                    offer: resolveMoonlightOffer(status),
                    copy: {
                        title: translate('moonlight.wallTitle', 'A new duel needs a fuller moon.'),
                        reason: moonlightWallReason(status),
                        returns: status?.weeklyReset === true && resetDate
                            ? translate('moonlight.resetsWeekly', `Moonlight resets to 100% every week, next on ${resetDate}. Unused Moonlight expires.`, { date: resetDate })
                            : '',
                        finishes: translate('moonlight.finishes', 'A duel already under way always finishes.'),
                        refillNow: translate('moonlight.refillNow', 'Refill Moonlight'),
                        refillOnce: status?.weeklyReset === true
                            ? translate('moonlight.refillOnceWeekly', 'One-time purchase. Unused refill Moonlight expires at the next weekly reset. Up to four purchases per month.')
                            : translate('moonlight.refillOnce', 'One-time purchase. Your pass refills the bar at your next renewal regardless.'),
                        // A free player is offered a tier, never a Refill:
                        // without this their one action read "Refill Moonlight".
                        upgrade: translate('store.upgrade', 'Upgrade'),
                        // The × was named "Close" in English in every language.
                        closeLabel: translate('hub.close', 'Close'),
                    },
                    onAction: (offer) => {
                        // The store MODULE, not `window.UI` — that global
                        // exists only in the bridged test bundle, so reading
                        // it here would make the action silently dead in
                        // production.
                        if (offer === 'refill') {
                            Store?.beginCheckout?.('moonlight_refill');
                            return;
                        }
                        /* The store will not open over another modal dialog,
                           and the Adventure sheet this rose from is one: the
                           Upgrade did nothing at all. That sheet has done its
                           part; the store takes the screen. */
                        HubSheets?.closeAll?.();
                        Store?.open?.();
                    },
                });
            })
            .catch((err) => console.warn('[SocketHandler] Could not raise the Moonlight wall:', err));
    }

    /* The refusal's own words, from the pause the server named. A free
       player is offered the passes; the store takes the screen from the
       Adventure sheet the start button sits in, as the wall's Upgrade does. */
    function raiseServicePause(error) {
        try {
            openServicePauseNotice({
                payload: error,
                t: translate,
                anchor: document.getElementById('solo-adventure-btn'),
                locale: document.documentElement.lang || undefined,
                onSeePass: () => {
                    HubSheets?.closeAll?.();
                    Store?.open?.();
                },
            });
        } catch (err) {
            console.warn('[SocketHandler] Could not show the pause notice:', err);
            showGameNotification(socketErrorMessage(error, error?.message || ''), 5000, true);
        }
    }

    function surfacePlayActionFailure(error) {
        const message = socketErrorMessage(
            error,
            translate('common.actionFailed', 'Action failed.'),
        );
        if (isFatalGeminiCacheMessage(message)) {
            if (GameEnd?.showFatalError) {
                GameEnd.showFatalError('A critical AI service error occurred. Please refresh the page.');
            } else {
                alert(translate('gameEnd.fatalMessage', 'A critical AI service error occurred. Please refresh the page.'));
            }
            return;
        }
        showGameNotification(message, 3000, true);
    }

    function abortPendingPlayAction(error, traceEvent = 'client-error', recover = false) {
        return pendingPlayAction?.settle?.({
            error,
            traceEvent,
            traceDetails: { message: socketErrorMessage(error) },
            recover,
        }) || false;
    }

    function requestFreshGameStateForRecovery(roomId, matchId) {
        if (!roomId || !socket.connected || !isCurrentActionContext(roomId, matchId)) return;
        withRequiredAuthPayload({ roomId, ...(matchId ? { matchId } : {}) })
            .then((payload) => {
                if (!socket.connected || !isCurrentActionContext(roomId, matchId)) return;
                socket.emit('requestGameState', payload);
            })
            .catch((error) => {
                console.warn('[SocketHandler] Could not refresh game state after an action failure:', error?.message);
            });
    }

    function hasComparableAudioBaseline(previousState, nextState) {
        if (!previousState || !nextState) return false;
        if (!previousState.roomId || !nextState.roomId || previousState.roomId !== nextState.roomId) return false;
        return !!(previousState.player && previousState.opponent);
    }

    function claimActionSound(actionId) {
        const normalized = typeof actionId === 'string' ? actionId.trim() : '';
        if (!normalized || normalized.length > 128 || soundedActionIds.has(normalized)) return false;
        soundedActionIds.add(normalized);
        if (soundedActionIds.size > MAX_SOUNDED_ACTIONS) {
            soundedActionIds.delete(soundedActionIds.values().next().value);
        }
        return true;
    }

    function retireOpponentAction(actionId) {
        retiredOpponentActionIds.add(actionId);
        if (retiredOpponentActionIds.size <= MAX_RETIRED_OPPONENT_ACTIONS) return;
        const oldestActionId = retiredOpponentActionIds.values().next().value;
        retiredOpponentActionIds.delete(oldestActionId);
    }

    function resetOpponentActionCorrelation() {
        activeOpponentAction = null;
        retiredOpponentActionIds.clear();
    }

    function resetThinkingIndicator() {
        resetOpponentActionCorrelation();
        ThinkingIndicator?.reset?.();
    }

    function resetEnemyReplayPresentation() {
        if (typeof EnemyReplay?.reset === 'function') {
            EnemyReplay.reset();
            return;
        }
        // Defensive script-load fallback: FX may exist even if the replay
        // module failed before initialization.
        FX?.clearHeldGlides?.();
    }

    function resetSocketMatchTracking({ preserveRematchRecovery = false } = {}) {
        // The match is over; stop pinning this client to its worker or every
        // subsequent lobby connection inherits the old room's routing.
        if (!sameGameSeatScope(explicitLeaveInFlight, GameState?.get?.())) {
            setRoomAffinity(null);
        }
        activeAuthoritativeScope = null;
        installedMatchScope = null;
        if (!preserveRematchRecovery) clearRematchRecovery();
        lastGameState = null;
        hasSubmittedMulligan = false;
        clearMulliganWarm();
        retireAllPendingGameplayOwners(new Error('Match replaced by rematch.'));
        recentPlayActionFailure = null;
        recentEndTurnFailure = null;
        resetThinkingIndicator();
        soundedActionIds.clear();
        resetEnemyReplayPresentation();
        if (gameResumeRetryTimer) clearTimeout(gameResumeRetryTimer);
        gameResumeRetryTimer = null;
        gameResumeRetryAttempt = 0;
        GameResume?.clear?.();
        ReconnectStatus?.clear?.();
        document.body?.classList?.remove('game-resuming', 'game-suspended');
    }

    /**
     * A ROOM'S FIRST STATE CAN OVERTAKE THE PACKET THAT INSTALLS IT.
     *
     * A fresh tutorial start sends `gameStateUpdated` BEFORE `gameStarted`:
     * activating the room runs the recorded AI's keep at once and broadcasts
     * it, and the start handler then emits the view it built before the
     * activation (measured against the real socket handlers, 25 September
     * 2026). Presented in arrival order, the early state showed the mulligan
     * as an ordinary match, `gameStarted` showed it again as the tutorial's —
     * the hand built, staged and dealt twice, a flicker on a guest's first
     * board — and its older view put "Opponent is deciding..." back over an
     * opponent that had already kept its hand.
     *
     * The install packet owns the first presentation. A state of a match this
     * page has not installed is held (the latest one: each is a whole
     * snapshot) and reconciled right after that match installs, through the
     * ordinary path; the mulligan the install showed is then updated, never
     * built again. A state provably older than the install is dropped, as is
     * one of any other match.
     */
    function isInstalledMatch(roomId, matchId) {
        return Boolean(installedMatchScope)
            && installedMatchScope.roomId === roomId
            && installedMatchScope.matchId === matchId;
    }

    function reconcileHeldArrival(roomId, matchId, installedAt) {
        const held = heldArrivalSnapshot;
        heldArrivalSnapshot = null;
        const heldState = held?.gameState;
        if (!heldState || heldState.roomId !== roomId || heldState.matchId !== matchId) return false;
        const heldAt = Number(heldState.serverNow);
        const startedAt = Number(installedAt);
        if (Number.isFinite(heldAt) && Number.isFinite(startedAt) && heldAt < startedAt) return false;
        handleGameStateUpdatedPacket?.(held);
        return true;
    }

    function rememberGameResumeState(gameState, source = {}) {
        if (gameState?.state === 'ended') {
            GameResume?.clearIfScope?.({
                roomId: gameState.roomId,
                matchId: gameState.matchId,
                playerId: gameState.player?.id || gameState.playerId || null,
            });
            return false;
        }
        if (
            !gameState
            || !gameState.roomId
            || !gameState.matchId
            || !gameState.player?.id
        ) return false;
        const existingHint = GameResume?.read?.();
        return GameResume?.remember?.(gameState, {
            isGuest: MoonfallGuest?.isActive?.() === true
                || (source.gameResumed === true && existingHint?.mode === 'guest'),
            isTutorial: source.isTutorial === true || gameState.isTutorial === true,
            resumeRevision: source.resumeRevision ?? gameState.resumeRevision,
        }) === true;
    }

    /**
     * The match a resume answers is the one the player is already looking at
     * (a tab return, a reconnect), not a board arriving from elsewhere.
     */
    function resumesMatchOnScreen(scope) {
        const current = GameState?.get?.() || {};
        return document.body?.classList?.contains('game-active') === true
            && Boolean(scope?.roomId) && current.roomId === scope.roomId
            && Boolean(scope?.matchId) && current.matchId === scope.matchId;
    }

    function prepareGameResumeInstall({ sameMatchOnScreen = false, stillMulligan = false } = {}) {
        retireAllPendingGameplayOwners(new Error('Gameplay state is being reconciled.'));
        lastGameState = null;
        /* The mulligan on screen for this same match (a tab return while the
           player is choosing, or waiting after Keep Hand) stays as it is:
           hiding it and showing it again tore the hand down, dealt it again
           and dropped the cards the player had marked. */
        const keepsMulligan = sameMatchOnScreen && stillMulligan;
        if (!keepsMulligan) clearMulliganWarm();
        recentPlayActionFailure = null;
        recentEndTurnFailure = null;
        resetThinkingIndicator();
        soundedActionIds.clear();
        if (!keepsMulligan) Mulligan?.hide?.({ immediate: true });
        resetEnemyReplayPresentation();
        SoundEffects?.stopAll?.();
        /* A panel whose request is still in flight on the match on screen
           stays: its correlated answer owns it (game-resume.js keeps it
           across a hidden tab for the same reason). */
        if (!sameMatchOnScreen || ActionIntent?.hasPendingRequest?.() !== true) {
            ActionIntent?.clear?.('game-resume');
        }
        SpellAnnounce?.dismiss?.();
        TurnBanner?.reset?.();
        ReconnectStatus?.clear?.();
    }

    function clearGameResumeRetry() {
        if (gameResumeRetryTimer) clearTimeout(gameResumeRetryTimer);
        gameResumeRetryTimer = null;
    }

    function scheduleGameResumeRetry() {
        clearGameResumeRetry();
        if (!isTabVisible || !GameResume?.read?.()) return false;
        if (gameResumeRetryAttempt >= GAME_RESUME_MAX_RETRIES) {
            setConnectionNotice(translate(
                'common.connectionLostRefresh',
                'Connection lost. Please refresh the page.',
            ));
            return false;
        }
        const delay = Math.min(10_000, 500 * (2 ** gameResumeRetryAttempt));
        gameResumeRetryAttempt += 1;
        gameResumeRetryTimer = setTimeout(() => {
            gameResumeRetryTimer = null;
            if (!isTabVisible) return;
            if (socket.connected) {
                void attemptGameResume();
            } else {
                ensureConnected().catch(() => scheduleGameResumeRetry());
            }
        }, delay);
        return true;
    }

    /**
     * The player has moved on from the remembered match: a lobby start of
     * theirs is in flight, or the seat they hold is a different room. A
     * remembered match from before a server restart is refused only when the
     * retry ladder reaches a terminal answer, which can be a minute after
     * boot — long after Create Room was pressed. Tearing the lobby down for
     * that refusal (iOS, 2026-09-20: "after selecting deck it returns to the
     * state from before create room was pressed") cancelled the admission,
     * left the room the server had just opened, and posted "Game not found"
     * for a match nobody was trying to reach.
     */
    function playerHasMovedOnFrom(hint) {
        if (activeLobbyRoomAdmission) return true;
        const currentRoomId = normalizeAuthoritativeScopeId(GameState?.get?.()?.roomId);
        const hintRoomId = normalizeAuthoritativeScopeId(hint?.roomId);
        return Boolean(currentRoomId) && currentRoomId !== hintRoomId;
    }

    function retireUnresumableGame(code, hint = null) {
        if (playerHasMovedOnFrom(hint)) {
            /* Drop the stale hint and its retry; touch nothing the player
               started. The affinity pin, the lobby admission and GameState
               all belong to the newer room now. */
            clearGameResumeRetry();
            gameResumeRetryAttempt = 0;
            /* Only the refused hint: a match the player has since started and
               that is now remembered in its place must stay resumable. */
            if (hint) GameResume?.clearIfScope?.(hint);
            else GameResume?.clear?.();
            document.body?.classList?.remove('game-resuming', 'game-suspended');
            clearConnectionNotice();
            BootVeil.drop('lobby');
            debugLog('[SocketHandler] Dropped a refused stale resume; a newer lobby start owns the seat', {
                code,
                hintRoomId: hint?.roomId || null,
            });
            return;
        }
        prepareGameResumeInstall();
        resetSocketMatchTracking();
        /* NO BOARD WAS EVER INSTALLED (a reload whose match the server no
           longer has): the player never left the lobby. "Returning" to it
           replayed its entrance, dropped the veil through the lobby's own
           arrival hold, and — landing late in the retry ladder — closed the
           Collection, Store, Foundry or deck picker the player was using.
           Retire the hint and the resume's veil hold, and say why. */
        if (document.body?.classList?.contains('game-active') !== true) {
            clearGameResumeRetry();
            gameResumeRetryAttempt = 0;
            if (hint) GameResume?.clearIfScope?.(hint);
            else GameResume?.clear?.();
            document.body?.classList?.remove('game-resuming', 'game-suspended');
            BootVeil.settle?.('game-resume');
            GameState?.reset?.();
        } else {
            try {
                if (typeof Lobby?.switchToLobbyView === 'function') {
                    Lobby.switchToLobbyView();
                } else {
                    GameState?.reset?.();
                }
            } catch (_) {
                GameState?.reset?.();
            }
        }
        setConnectionNotice(code === 'resume_not_found'
            ? translate(
                'game.gameNotFound',
                'Game not found on server. The match may have ended or the server restarted.',
            )
            : translate(
                'game.rejoinFailed',
                'Could not rejoin this game. Please re-open the invite link.',
            ), { connectionLost: false });
    }

    /* A match on screen that this connect will ask the server for again
       (attemptGameResume); a guest tutorial's resume and a rematch's recovery
       have owners of their own. */
    function matchOnScreenAwaitsRejoin() {
        if (document.body?.classList?.contains('game-active') !== true) return false;
        if (readRematchRecoveryHint()) return false;
        const hint = GameResume?.read?.();
        return Boolean(hint) && !(hint.mode === 'guest' && hint.isTutorial);
    }

    async function attemptGameResume() {
        if (
            !isTabVisible
            || !socket.connected
            || readRematchRecoveryHint()
            || typeof handleGameStartedPacket !== 'function'
        ) return false;
        const attempt = GameResume?.beginAttempt?.();
        if (!attempt) return false;
        const { hint } = attempt;
        if (sameGameSeatScope(explicitLeaveInFlight, hint)) {
            GameResume.finishAttempt(attempt);
            return false;
        }

        // The guest tutorial already has a dedicated token-restoration owner in
        // guest-session.js. Keeping a single owner avoids two concurrent board
        // installs during boot.
        if (hint.mode === 'guest' && hint.isTutorial) {
            GameResume.finishAttempt(attempt);
            return false;
        }
        document.body?.classList?.add('game-resuming');

        const requestId = createClientActionId();
        try {
            // THE FIFTH AUTHENTICATED ENTRY POINT. createRoom, joinRoom,
            // startSolo and startTutorial each clear the guest latch before
            // they emit; resuming is the one that did not, and it is the only
            // one a signed-in player reaches WITHOUT pressing anything — a
            // mid-match refresh. Now that `deactivate()` arms the latch on the
            // signed-in boot callback, an uncleared latch here would let the
            // resumed board install and then silently drop every
            // `gameStateUpdated` behind it, freezing the match.
            let credentials;
            if (hint.mode === 'guest') {
                credentials = { guestToken: MoonfallGuest?.getToken?.() || null };
            } else {
                // AFTER the token resolves, not before: a failed auth aborts the
                // resume, and clearing the latch on the way out would leave a
                // stale guest room adoptable again for nothing.
                credentials = { authToken: await getRequiredAuthToken() };
                clearEndedGuestLatchForAuthenticatedStart();
            }
            if (!GameResume.isCurrent(attempt) || !socket.connected) return false;
            const response = await emitWithAck('resumeGame', {
                roomId: hint.roomId,
                matchId: hint.matchId,
                playerId: hint.playerId,
                requestId,
                lastResumeRevision: hint.resumeRevision,
                ...credentials,
            }, GAME_RESUME_ACK_TIMEOUT_MS);
            if (!GameResume.isCurrent(attempt)) return false;

            const revision = Number(response?.resumeRevision);
            const started = response?.gameStarted;
            const validResponse = response?.action === 'resumeGame'
                && response?.requestId === requestId
                && response?.ok === true
                && response?.roomId === hint.roomId
                && response?.matchId === hint.matchId
                && response?.playerId === hint.playerId
                && Number.isSafeInteger(revision)
                && revision > hint.resumeRevision
                && started?.gameState?.roomId === hint.roomId
                && started?.gameState?.matchId === hint.matchId
                && started?.gameState?.player?.id === hint.playerId;
            if (!validResponse) {
                GameResume.finishAttempt(attempt);
                if (isWrongWorker(response)) {
                    // The match lives in another worker. Reconnect with the room
                    // pinned; the connect handler runs the next attempt there.
                    rerouteToRoom(hint.roomId).catch(() => scheduleGameResumeRetry());
                } else if ([
                    'resume_not_found',
                    'resume_rejected',
                    'resume_invalid',
                ].includes(response?.code)) {
                    retireUnresumableGame(response.code, hint);
                } else {
                    scheduleGameResumeRetry();
                }
                return false;
            }

            if (!GameResume.finishAttempt(attempt)) return false;
            prepareGameResumeInstall({
                sameMatchOnScreen: resumesMatchOnScreen(hint),
                stillMulligan: started?.gameState?.state === 'mulligan',
            });
            handleGameStartedPacket(started);
            const installed = GameState?.get?.() || {};
            if (
                installed.roomId !== hint.roomId
                || installed.matchId !== hint.matchId
                || installed.player?.id !== hint.playerId
            ) {
                scheduleGameResumeRetry();
                return false;
            }
            gameResumeRetryAttempt = 0;
            clearGameResumeRetry();
            clearConnectionNotice();
            rememberGameResumeState(installed, started);
            document.body?.classList?.remove('game-resuming', 'game-suspended');
            return true;
        } catch (error) {
            if (GameResume?.finishAttempt?.(attempt) !== true) return false;
            scheduleGameResumeRetry();
            return false;
        }
    }

    function resumeGameFromLifecycle({ wake = false } = {}) {
        if (wake) GameResume?.wake?.();
        const hint = GameResume?.read?.();
        if (!hint || (hint.mode === 'guest' && hint.isTutorial)) return false;
        document.body?.classList?.add('game-resuming');
        /* A MID-MATCH REFRESH BOOTS TO THE BOARD, NOT THROUGH THE LOBBY.
           Measured on the real backend: the lobby painted at 749ms and the
           resumed board replaced it at 953ms — the "lobby, then the tutorial
           rendered on top of that" report. The boot veil now waits for the
           board (`Lobby.switchToGameView` drops it) or for a refusal
           (`switchToLobbyView`), and is capped: a resume still retrying at
           the cap shows the lobby it would otherwise have hidden, and a later
           success installs the board over it exactly as before. No-op once
           the veil is gone, which is every later reconnect and wake. */
        BootVeil.hold('game-resume', GAME_RESUME_BOOT_HOLD_MS);
        // The resumed board's shared art (the card back its piles and fan
        // paint) is known now: decode it while the resume is asked for.
        runGameStartedBestEffort('battle art warm', () => MatchLoading?.warmChrome?.());
        setRoomAffinity(hint.roomId);
        gameResumeRetryAttempt = 0;
        clearGameResumeRetry();
        if (socket.connected) {
            void attemptGameResume();
        } else {
            ensureConnected().catch(() => scheduleGameResumeRetry());
        }
        return true;
    }

    function getKnownActionCorrelation(data, { requireActionType = false } = {}) {
        const roomId = data?.roomId;
        const matchId = data?.matchId;
        const actionId = data?.actionId;
        const playerId = data?.playerId;
        if (typeof roomId !== 'string' || roomId.trim().length === 0) return null;
        if (typeof matchId !== 'string' || matchId.trim().length === 0) return null;
        if (typeof actionId !== 'string' || actionId.trim().length === 0) return null;
        if (typeof playerId !== 'string' || playerId.trim().length === 0) return null;
        if (
            roomId.length > 128
            || matchId.length > 128
            || actionId.length > 128
            || playerId.length > 128
        ) return null;
        if (requireActionType && !GAME_ACTION_TYPES.has(data?.actionType)) return null;

        const gameState = (typeof GameState !== 'undefined' && GameState?.get)
            ? GameState.get()
            : null;
        if (gameState?.roomId !== roomId || gameState?.matchId !== matchId) return null;
        const ownPlayerId = gameState?.player?.id || gameState?.playerId;
        const opponentPlayerId = gameState?.opponent?.id;
        if (playerId !== ownPlayerId && playerId !== opponentPlayerId) return null;

        return {
            roomId,
            matchId,
            actionId,
            playerId,
            isOpponent: playerId === opponentPlayerId,
        };
    }

    function getOpponentActionCorrelation(data, options = {}) {
        const correlation = getKnownActionCorrelation(data, options);
        if (!correlation?.isOpponent) return null;
        return correlation;
    }

    function isCurrentMatchEvent(data) {
        const roomId = typeof data?.roomId === 'string' ? data.roomId.trim() : '';
        const matchId = typeof data?.matchId === 'string' ? data.matchId.trim() : '';
        if (!roomId || !matchId || roomId.length > 128 || matchId.length > 128) return false;
        const current = (typeof GameState !== 'undefined' && GameState?.get)
            ? GameState.get()
            : null;
        return current?.roomId === roomId && current?.matchId === matchId;
    }

    function getDetailedActionCorrelation(data) {
        const correlation = getKnownActionCorrelation(data);
        if (!correlation || !isCurrentMatchEvent(data)) return null;
        // Owner-only target detail is valid only for this client's own actor.
        // An opponent-marked payload is malformed and must not reach replay or
        // the feed, even if every other correlation field looks current.
        if (data?.targetPresentationOwnerOnly === true && correlation.isOpponent) return null;
        return correlation;
    }

    /**
     * Lazy connect helper - connect only when explicitly requested.
     *
     * A HANDSHAKE NOBODY ANSWERS IS MADE AGAIN, NOT WAITED OUT.
     *
     * Measured on production, 13 October 2026: a handshake is normally
     * answered in about 0.6 s, but in spells one gets no answer for more than
     * 10 s (4 of 240 in one sampler) and others take 2 to 6.4 s, while the
     * handshake made right after is answered normally: a socket with no room
     * is dealt to the next worker in turn. This helper waited its whole 10 s
     * on the first one, and Socket.IO's own retry starts only when its 20 s
     * open timeout runs out, so 3 of 17 first visits ended on "Failed to
     * connect." with a server that would have answered the next try.
     *
     *  - A handshake still unanswered after CONNECT_ANSWER_WINDOW_MS is
     *    dropped and a fresh one is opened at once. 3 s is five normal
     *    answers, so a healthy handshake is never cut.
     *  - Only one is cut short per call. Its replacement keeps the rest of
     *    the 10 s, which is 7 s, longer than the slowest answer measured: a
     *    link that is only slow is still waited for, as it always was.
     *  - A handshake that is refused outright (an error, not silence) is made
     *    again after CONNECT_REFUSAL_PAUSES_MS, twice at most.
     *  - The call as a whole still gives up at 10 s, so every caller's worst
     *    case, and every screen that waits on it, is what it was.
     *
     * One handshake at a time: `socket.disconnect()` on a socket that never
     * connected closes the pending transport and emits nothing, and it is
     * called before every fresh `socket.connect()`. A handshake given up that
     * way can never become a second session: Chrome and WebKit drop its
     * connection at once; Firefox keeps the TCP connection until the server
     * answers, then hangs up without sending a frame (measured in all three,
     * with the late answer: tests/e2e-playwright/connect-retry.spec.js). No
     * caller emits before `connect`, so nothing can be started twice either.
     *
     * A call that gives up leaves its last handshake in flight, which is what
     * hands the connection back to Socket.IO's own reconnection, as before.
     * The call after it does not wait on that leftover: it starts fresh.
     *
     * Contract: tests/unit/client/socket-handler.connectRetry.test.js; the
     * vendored library over real TCP,
     * tests/integration/clientConnectRetry.integration.test.js; browsers,
     * tests/e2e-playwright/connect-retry.spec.js.
     */
    const CONNECT_ANSWER_WINDOW_MS = 3000;
    const CONNECT_REFUSAL_PAUSES_MS = [500, 1000];
    let connectPromise = null;
    let connectGaveUp = false;
    function ensureConnected() {
        if (socket.connected) {
            connectGaveUp = false;
            return Promise.resolve();
        }
        if (connectPromise) return connectPromise;
        let resolveAttempt;
        let rejectAttempt;
        let settled = false;
        let timeoutId = null;
        let answerTimer = null;
        let pauseTimer = null;
        let cutShort = false;
        let refusals = 0;
        const attemptPromise = new Promise((resolve, reject) => {
            resolveAttempt = resolve;
            rejectAttempt = reject;
        });
        connectPromise = attemptPromise;

        const cleanup = () => {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = null;
            if (answerTimer) clearTimeout(answerTimer);
            answerTimer = null;
            if (pauseTimer) clearTimeout(pauseTimer);
            pauseTimer = null;
            socket.off('connect', onConnect);
            socket.off('connect_error', onRefused);
            socket.off('error', onError);
            if (connectPromise === attemptPromise) connectPromise = null;
        };
        const onConnect = () => {
            if (settled) return;
            settled = true;
            connectGaveUp = false;
            startHeartbeat();
            cleanup();
            resolveAttempt();
        };
        const onError = (err) => {
            if (settled) return;
            settled = true;
            connectGaveUp = true;
            cleanup();
            rejectAttempt(err || new Error('Failed to connect'));
        };
        /* `fresh` drops whatever handshake is in flight first. */
        const openHandshake = (fresh) => {
            try {
                if (fresh) socket.disconnect();
                socket.connect();
            } catch (e) {
                onError(e);
                return;
            }
            // Answered or refused already, or the one cut is spent.
            if (settled || cutShort || pauseTimer) return;
            answerTimer = setTimeout(() => {
                answerTimer = null;
                if (settled || socket.connected) return;
                cutShort = true;
                openHandshake(true);
            }, CONNECT_ANSWER_WINDOW_MS);
        };
        const onRefused = (err) => {
            if (settled) return;
            if (answerTimer) clearTimeout(answerTimer);
            answerTimer = null;
            const pause = CONNECT_REFUSAL_PAUSES_MS[refusals];
            if (pause === undefined) {
                // Left as it is: Socket.IO's own reconnection carries on.
                onError(err);
                return;
            }
            refusals += 1;
            /* The pause is ours, so Socket.IO's own backoff is stopped. The
               next handshake waits for the timer even at 0 ms: reopened in
               the turn of this error, the library schedules its own
               reconnection on top and can never reconnect again. */
            try {
                socket.disconnect();
            } catch (_) { /* a dead transport is already what we want */ }
            pauseTimer = setTimeout(() => {
                pauseTimer = null;
                openHandshake(false);
            }, pause);
        };

        socket.once('connect', onConnect);
        socket.on('connect_error', onRefused);
        socket.once('error', onError);
        timeoutId = setTimeout(() => {
            if (socket.connected) return;
            if (pauseTimer) {
                // Never give up with nothing in flight (see above).
                clearTimeout(pauseTimer);
                pauseTimer = null;
                try {
                    socket.connect();
                } catch (_) { /* reported below as the timeout it is */ }
            }
            onError(new Error('Connection timeout'));
        }, 10000);
        openHandshake(connectGaveUp);
        return attemptPromise;
    }

    // Ping-pong heartbeat for connection health checks
    let heartbeatInterval = null;
    let missedHeartbeats = 0;
    let heartbeatActive = false; // Track if heartbeat is active

    // Throttled client-side user activity signaling
    const ACTIVITY_THROTTLE_MS = 1000; // emit at most once per second
    let __lastActivityEmit = 0;
    function __emitActivityThrottled(reason) {
        const now = Date.now();
        if (!socket.connected) return;
        if (now - __lastActivityEmit < ACTIVITY_THROTTLE_MS) return;
        __lastActivityEmit = now;
        try { socket.emit('activity', { r: reason || 'user' }); } catch (_) { }
    }

    function __bindUserActivityListeners() {
        const onUserActivity = () => __emitActivityThrottled('ui');
        try {
            window.addEventListener('mousemove', onUserActivity, { passive: true });
            window.addEventListener('mousedown', onUserActivity, { passive: true });
            window.addEventListener('keydown', onUserActivity, { passive: true });
            window.addEventListener('touchstart', onUserActivity, { passive: true });
            window.addEventListener('wheel', onUserActivity, { passive: true });
            window.addEventListener('scroll', onUserActivity, { passive: true });
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    onUserActivity();
                }
            });
        } catch (_) { /* jsdom may not support all events */ }
    }

    // Track browser tab visibility (no socket operations here)
    document.addEventListener('visibilitychange', () => {
        isTabVisible = document.visibilityState === 'visible';
        debugLog(`Tab visibility changed: ${isTabVisible ? 'visible' : 'hidden'}`);

        if (isTabVisible) {
            resumeGameFromLifecycle({ wake: true });
            // A private play-action owner outlives purely visual state. Do not
            // release its input lock just because the tab returned to view;
            // the correlated completion/error or watchdog owns that decision.
            if (GameState && !hasPendingGameplayOwner()) {
                GameState.setActionInProgress(false);
            }
        } else {
            clearGameResumeRetry();
            GameResume?.suspend?.();
            resetEnemyReplayPresentation();
            // Tab became hidden, clean up any visual states
            document.querySelectorAll('.valid-target').forEach(el => {
                el.classList.remove('valid-target');
            });
            document.querySelectorAll('.dragging').forEach(el => {
                el.classList.remove('dragging');
            });

            // Persist game state before potential sleep
            if (GameState && typeof GameState.forcePersist === 'function') {
                GameState.forcePersist();
            }
        }
    });

    // Set up event listeners
    function setupEventListeners() {
        // Basic connection events
        // Socket.IO surfaces retry exhaustion on the MANAGER, not the socket.
        // This is the only moment the player genuinely has to act.
        socket.io?.on?.('reconnect_failed', () => {
            console.warn('[SocketHandler] reconnection gave up');
            setConnectionNotice(translate('common.connectionLostRefresh', 'Connection lost. Please refresh the page.'));
        });

        socket.on('connect', () => {
            debugLog('Connected to server');
            announceReplay();
            // Recovered — retract whatever we told the player during the drop,
            // unless the match on screen is still to be rejoined: until the
            // server gives the seat back the board ignores the player
            // (body.game-resuming), and a board that looks live while End Turn
            // does nothing reads as a broken game (replay 01a17967: six presses
            // in 11 s, then the player left). The resume clears it.
            if (matchOnScreenAwaitsRejoin()) {
                setConnectionNotice(translate('common.connectionReconnecting', 'Connection lost. Reconnecting…'));
            } else {
                clearConnectionNotice();
            }

            // A reconnect can be part of ensureConnected() for a newly-owned
            // action. Never clear that owner's lock from a global event.
            if (GameState && !hasPendingGameplayOwner()) {
                GameState.setActionInProgress(false);
            }

            // Reset UI elements that might be in an error state
            document.body.classList.remove('reconnecting');
            const gameBoard = document.getElementById('game-view');
            if (gameBoard) {
                gameBoard.classList.remove('reconnecting');
            }
            // Start heartbeat when connected
            startHeartbeat();
            // Mark immediate activity on connect so short server thresholds don't drop us instantly
            __emitActivityThrottled('connect');
            if (readRematchRecoveryHint()) void attemptRematchRecovery();
            else void attemptGameResume();
        });

        socket.on('disconnect', (reason) => {
            console.warn(`⚠️ Disconnected from server: ${reason}`);
            if (reason === 'io server disconnect') {
                debugLog('Reason details: The server explicitly disconnected the socket.');
            } else if (reason === 'ping timeout') {
                debugLog('Reason details: The server did not send a PONG packet in time.');
            } else if (reason === 'transport close') {
                debugLog('Reason details: The connection was closed (e.g., browser tab closed, network change).');
            } else if (reason === 'transport error') {
                debugLog('Reason details: An error occurred in the transport layer.');
            }
            // Stop heartbeat on disconnect
            stopHeartbeat();
            GameResume?.cancelAttempt?.();
            resetThinkingIndicator();
            resetEnemyReplayPresentation();

            abortPendingPlayAction(
                new Error(translate('common.connectionLostWait', 'Connection lost. Please wait…')),
                'socket-disconnect',
                false,
            );
            pendingEndTurn?.settle?.({
                error: new Error(translate('common.connectionLostWait', 'Connection lost. Please wait…')),
                recover: false,
                notify: false,
            });
            pendingLegacyGameplayOwner?.retire?.(
                new Error(translate('common.connectionLostWait', 'Connection lost. Please wait…')),
            );

            // Reset any non-play-action lock to avoid stuck UI states.
            if (GameState && !hasPendingGameplayOwner()) {
                GameState.setActionInProgress(false);
            }

            // Show a notification only for unintentional disconnects.
            const suppress = (__pendingInactivityDisconnect === true) || (__pendingTTLDisconnect === true) || (__pendingServerRestart === true);
            __pendingInactivityDisconnect = false; // reset flag
            __pendingTTLDisconnect = false; // reset flag
            __pendingServerRestart = false; // reset flag
            if (reason !== 'io client disconnect' && !suppress) {
                // Socket.IO is configured with `reconnection: true`, so a drop is
                // the START of a recovery, not the end of the session. Phones
                // disconnect constantly and legitimately — screen lock, app
                // switch, WiFi/cellular handoff — and telling the player to
                // refresh while the client is silently succeeding is both wrong
                // and alarming. Escalate to "refresh" only from reconnect_failed.
                setConnectionNotice(translate('common.connectionReconnecting', 'Connection lost. Reconnecting…'));
            } else {
                debugLog('Suppressed disconnect notification (intentional client disconnect).');
            }
        });

        socket.on('rateLimitNotice', (payload) => {
            try {
                const retryAfterMs = Number(payload && payload.retryAfterMs) || 0;
                const guestTutorialStarting = payload?.event === 'startTutorial'
                    && MoonfallGuest?.isStarting?.();
                if (!guestTutorialStarting && MoonfallCloudGuard && typeof MoonfallCloudGuard.showRateLimitToast === 'function') {
                    MoonfallCloudGuard.showRateLimitToast(retryAfterMs);
                }
                debugLog('[SocketHandler] rateLimitNotice', payload);
                if (!hasPendingGameplayOwner()) {
                    GameState.setActionInProgress(false);
                }
            } catch (e) {
                console.warn('[SocketHandler] rateLimitNotice handler error:', e);
            }
        });

        socket.on('mangaUnavailable', (payload) => {
            if (payload?.code !== 'monthly_manga_usage_cap_exceeded') return;
            if (!consumeMangaCapNotice()) return;
            showGameNotification(
                translate(
                    'manga.unavailableMonthly',
                    'Manga is unavailable for this match because this month\'s generation allowance has been reached. The game will continue normally.'
                ),
                5000,
                true
            );
        });

        // Guest tutorial reached the end of its scripted segment: gracefully
        // convert to the sign-in moment instead of soft-locking the board.
        socket.on('guestTutorialLimit', (payload) => {
            debugLog('[SocketHandler] guestTutorialLimit', payload);
            // Scope to the CURRENT game: a stale limit from a previous tutorial (game
            // #1) that arrives after the visitor replayed into game #2 must NOT tear
            // down the new session. Only honor it when it targets the game in play (or
            // carries no roomId, for backward compatibility).
            const currentRoomId = (typeof GameState !== 'undefined' && GameState?.get) ? GameState.get().roomId : null;
            if (payload && payload.roomId && currentRoomId && payload.roomId !== currentRoomId) {
                debugLog('[SocketHandler] Ignoring stale guestTutorialLimit for', payload.roomId, 'current', currentRoomId);
                return;
            }
            const hadGameplayOwner = hasPendingGameplayOwner();
            retireAllPendingGameplayOwners(new Error(
                payload?.message || 'The guest tutorial ended before the action completed.',
            ));
            if (!hadGameplayOwner && !hasPendingGameplayOwner()
                && typeof GameState !== 'undefined'
                && GameState?.setActionInProgress) {
                GameState.setActionInProgress(false);
            }
            if (MoonfallGuest && typeof MoonfallGuest.endGuestTutorial === 'function') {
                MoonfallGuest.endGuestTutorial('limit');
            }
        });

        socket.on('error', (error) => {
            const rawMessage = error && (error.message || error.msg || (typeof error === 'string' ? error : null));
            // Route EVERY error through the translator. It used to be gated to
            // two codes, so every other refusal rendered raw English in all 8
            // languages. Unknown codes still fall through to the server text.
            const msg = socketErrorMessage(error, rawMessage);
            // Log full object and derived message for diagnostics
            console.error('Socket error:', error, 'message:', msg);

            // Any server error carrying a cooldown gets the countdown toast
            // ("Try again in Ns"), whatever surface later shows its message.
            const retryAfterMs = Number(error?.retryAfterMs) || 0;
            if (retryAfterMs > 0 && typeof MoonfallCloudGuard?.showRateLimitToast === 'function') {
                MoonfallCloudGuard.showRateLimitToast(retryAfterMs);
            }

            /* THE MOONLIGHT WALL. The SERVER decides whether a duel may
               start; the client only explains the refusal. Mirroring that
               decision client-side would eventually disagree with it, and the
               shape of that disagreement is a player told "no" by a wall the
               server would have said yes to. */
            if (shouldRaiseMoonlightWall(error)) {
                raiseMoonlightWall();
                return;
            }

            /* MOONFALL PAUSED NEW MATCHES (a loss brake or the operator's
               monthly fuse): said plainly, in the player's language and on
               their clock, never as their own moon (ui/service-pause.js). */
            if (isServicePause(error)) {
                raiseServicePause(error);
                return;
            }

            // A playAction is a correlated, single-owner transaction. Route
            // its socket error through that owner before any global alert or
            // lock reset can race the caller promise.
            if (pendingEndTurn?.handleSocketError?.(error)) return;
            if (pendingPlayAction?.handleSocketError?.(error)) return;
            if (pendingLegacyGameplayOwner?.handleSocketError?.(error)) return;
            if (consumeRecentEndTurnFailure(error)) return;
            if (consumeRecentPlayActionFailure(error)) return;

            if (error?.action === 'playAction' || error?.action === 'endTurn') {
                debugLog('[SocketHandler] Ignoring stale correlated gameplay error');
                return;
            }

            // An uncorrelated error is not allowed to mutate or interrupt a
            // newer private owner. Its own request lifecycle remains the only
            // authority that may release the shared input lock.
            if (hasPendingGameplayOwner()) {
                debugLog('[SocketHandler] Ignoring uncorrelated error while a gameplay owner is active');
                return;
            }

            GameState.setActionInProgress(false);

            // Modern gameplay errors identify their source action. Keep these
            // non-blocking and, crucially, never let a delayed End Turn error
            // interrupt a newer correlated card action.
            if (typeof error?.action === 'string' && error.action) {
                if (msg) showGameNotification(msg, 3000, true);
                return;
            }

            // Fatal Gemini cache failures -> show dedicated popup and stop
            if (isFatalGeminiCacheMessage(msg)) {
                console.error('[SocketHandler] Detected fatal Gemini cache error. Triggering fatal UI.');
                if (GameEnd && typeof GameEnd.showFatalError === 'function') {
                    GameEnd.showFatalError('A critical AI service error occurred. Please refresh the page.');
                } else {
                    alert(translate('gameEnd.fatalMessage', 'A critical AI service error occurred. Please refresh the page.'));
                }
                return;
            }

            // Informative popups for critical cases
            if (msg === 'Game not found' || msg === 'Game not found for summonCharacter') {
                alert(translate('game.gameNotFound', 'Game not found on server. The match may have ended or the server restarted.'));
                return;
            }

            if (msg === 'Player not found in game') {
                alert(translate('game.rejoinFailed', 'Could not rejoin this game. Please re-open the invite link.'));
                return;
            }

            // Surface all other errors without freezing the game behind a
            // blocking alert. Known codes get localized copy; the opponent-AI
            // failure explicitly tells the player their own turn continues.
            const displayMsg = error?.code === 'ai_turn_failed'
                ? translate('game.aiTurnFailed', 'The opposing AI turn could not complete. Play continues.')
                : (msg || translate('game.unknownConnectionError', 'An unknown connection error occurred. See console for details.'));
            if (typeof showGameNotification === 'function') {
                showGameNotification(displayMsg, 5000, true);
            } else {
                alert(displayMsg);
            }
        });

        function shouldIgnoreLobbyRoomEvent(kind, data) {
            const currentState = GameState?.get?.() || {};
            if (hasPendingGameplayOwner() || currentState.matchId) {
                debugLog('[SocketHandler] Ignoring stale lobby room event during an active match', {
                    incomingRoomId: data?.roomId || null,
                    currentRoomId: currentState.roomId || null,
                    currentMatchId: currentState.matchId || null,
                });
                return true;
            }
            if (lobbyRoomEventMatchesAdmission(kind, data)) return false;
            debugLog('[SocketHandler] Ignoring stale or unsolicited lobby room event', {
                kind,
                incomingRoomId: data?.roomId || null,
                currentRoomId: currentState.roomId || null,
                admissionGeneration: activeLobbyRoomAdmission?.generation || null,
            });
            return true;
        }

        // Room events
        socket.on('roomCreated', (data) => {
            debugLog('Room created:', data);
            if (shouldIgnoreLobbyRoomEvent('created', data)) return;
            // Pin this connection to the match's worker. Critically this also
            // pins RECONNECTS: a mid-match drop (screen lock, WiFi->cellular)
            // opens a brand-new connection that the primary routes from
            // scratch, and without the room it would land on a worker that has
            // never heard of the game.
            pinAcknowledgedStart(data) || setRoomAffinity(data?.roomId);
            resetThinkingIndicator();
            GameState.update({
                roomId: data.roomId,
                playerId: data.playerId,
                isHost: true
            });

            if (MoonfallGuest?.isActive?.()) {
                MoonfallGuest.rememberTutorial?.({
                    roomId: data.roomId,
                    playerId: data.playerId,
                });
            }

            Lobby.onRoomCreated(data);
        });

        socket.on('roomJoined', (data) => {
            debugLog('Room joined:', data);
            if (shouldIgnoreLobbyRoomEvent('joined', data)) return;
            pinAcknowledgedStart(data);
            resetThinkingIndicator();
            GameState.update({
                roomId: data.roomId,
                playerId: data.playerId
            });

            Lobby.onRoomJoined(data);
        });

        // Server emits consolidated player list updates
        socket.on('playerListUpdate', (data) => {
            debugLog('Player list update:', data);
            if (UI && Lobby && typeof Lobby.updatePlayersStatus === 'function') {
                Lobby.updatePlayersStatus(data.players);
            }
        });

        socket.on('playerConnectionUpdate', (data) => {
            ReconnectStatus?.handle?.(data, GameState?.get?.() || {});
        });

        // Server notifies when a player leaves due to disconnects
        socket.on('playerLeft', (data) => {
            debugLog('Player left:', data);
        });

        // Opponent disconnects permanently (timeout) -> show dedicated popup
        socket.on('opponentDisconnected', (data) => {
            if (!isCurrentMatchEvent(data)) return;
            ReconnectStatus?.clear?.();
            GameResume?.clear?.();
            resetThinkingIndicator();
            // Back-to-lobby path: never leave a float (esp. a sticky enemy
            // cast) hanging over the lobby.
            if (SpellAnnounce) SpellAnnounce.dismiss();
            debugLog('Opponent disconnected event:', data);
            if (data && data.storybookShortCode && MoonfallStorybookContext) {
                MoonfallStorybookContext.setShortCode(data.storybookShortCode, data);
            }
            if (GameEnd && typeof GameEnd.showOpponentDisconnected === 'function') {
                GameEnd.showOpponentDisconnected();
            } else {
                alert(translate('gameEnd.opponentDisconnectTitle', 'Opponent Disconnected :('));
            }
        });

        // Storybook becomes available after the game ends (late persist).
        socket.on('storybookReady', (data) => {
            if (data && data.storybookShortCode && MoonfallStorybookContext) {
                MoonfallStorybookContext.setShortCode(data.storybookShortCode, data);
            }
        });

        // Storybook persistence is terminally unavailable for this match. The
        // server exposes only a stable product code; provider details stay in
        // private server logs.
        socket.on('storybookFailed', (data) => {
            if (data && data.code && MoonfallStorybookContext
                && typeof MoonfallStorybookContext.setFailure === 'function') {
                MoonfallStorybookContext.setFailure(data.code, data);
            }
        });

        // The server is draining for a deploy/restart. It disconnects everyone a
        // moment after this event; socket.io then auto-reconnects to the new
        // process (reconnection: true). Show a heads-up instead of the generic
        // "Connection lost" toast so the drop reads as intentional.
        socket.on('serverRestarting', () => {
            console.warn('Server restarting notice received.');
            __pendingServerRestart = true;
            // A drain refusal can arrive instead of roomCreated/gameStarted —
            // unstick the start buttons (same reason rateLimitNotice does it).
            if (GameState) GameState.setActionInProgress(false);
            const message = i18n?.t?.('connection.serverRestarting');
            const notification = document.createElement('div');
            notification.className = 'game-notification';
            notification.textContent = (message && message !== 'connection.serverRestarting')
                ? message
                : 'The server is restarting for an update.';
            document.body.appendChild(notification);
            setTimeout(() => notification.remove(), 8000);
        });

        // You are being disconnected due to inactivity -> show dedicated popup
        socket.on('inactivityDisconnect', (data) => {
            resetThinkingIndicator();
            // Back-to-lobby path: never leave a float (esp. a sticky enemy
            // cast) hanging over the lobby.
            if (SpellAnnounce) SpellAnnounce.dismiss();
            console.warn('Inactivity disconnect event received:', data);
            __pendingInactivityDisconnect = true;
            if (data && data.storybookShortCode && MoonfallStorybookContext) {
                MoonfallStorybookContext.setShortCode(data.storybookShortCode, data);
            }
            if (GameEnd && typeof GameEnd.showInactivityDisconnect === 'function') {
                GameEnd.showInactivityDisconnect({ allowShare: data?.inGame !== false });
            } else {
                alert(translate('gameEnd.inactivityMessage', 'You were inactive for too long and have been disconnected.'));
            }
        });

        // You are being disconnected due to fixed session time limit -> show dedicated popup
        socket.on('sessionTTLDisconnect', (data) => {
            resetThinkingIndicator();
            // Back-to-lobby path: never leave a float (esp. a sticky enemy
            // cast) hanging over the lobby.
            if (SpellAnnounce) SpellAnnounce.dismiss();
            console.warn('Session TTL disconnect event received:', data);
            __pendingTTLDisconnect = true;
            if (data && data.storybookShortCode && MoonfallStorybookContext) {
                MoonfallStorybookContext.setShortCode(data.storybookShortCode, data);
            }
            if (GameEnd) {
                if (typeof GameEnd.showSessionTTLDisconnect === 'function') {
                    GameEnd.showSessionTTLDisconnect();
                } else if (typeof GameEnd.showInactivityDisconnect === 'function') {
                    // Fallback to inactivity popup if dedicated TTL popup not available
                    GameEnd.showInactivityDisconnect();
                } else {
                    alert(translate('gameEnd.sessionTTLMessage', 'Your session has reached its time limit and you have been disconnected.'));
                }
            } else {
                alert(translate('gameEnd.sessionTTLMessage', 'Your session has reached its time limit and you have been disconnected.'));
            }
        });

        socket.on('rematchState', (data) => {
            if (!isCurrentMatchEvent(data)) return;
            if (data?.youReady === true && data?.opponentReady === true) {
                rememberRematchRecovery({
                    previousRoomId: data.roomId,
                    previousMatchId: data.matchId,
                });
                armRematchRecoveryWatchdog({ resetBackoff: true });
            }
            GameEnd?.updateRematchState?.(data);
        });

        socket.on('rematchUnavailable', (data) => {
            if (!isCurrentMatchEvent(data)) return;
            GameEnd?.setRematchUnavailable?.(data);
            if (data?.retryable !== true) clearRematchRecovery();
        });

        socket.on('rematchStarted', (data) => {
            applyRematchTransition(data);
        });

        // Dedicated fatal error event from server (e.g., Gemini cache unavailable)
        socket.on('fatalError', (data) => {
            resetThinkingIndicator();
            // Back-to-lobby path: never leave a float (esp. a sticky enemy
            // cast) hanging over the lobby.
            if (SpellAnnounce) SpellAnnounce.dismiss();
            console.error('Fatal error event received:', data);
            const msg = (data && (data.message || data.msg)) || 'A critical AI service error occurred. Please refresh the page.';
            const hadGameplayOwner = hasPendingGameplayOwner();
            retireAllPendingGameplayOwners(new Error(msg));
            if (!hadGameplayOwner && !hasPendingGameplayOwner()) GameState.setActionInProgress(false);
            if (GameEnd && typeof GameEnd.showFatalError === 'function') {
                GameEnd.showFatalError(msg);
            } else {
                alert(msg);
            }
        });

        // Game events
        handleGameStartedPacket = (data) => {
            // Drop a stale guest board that arrives after the visitor already left the
            // tutorial (e.g. tapped Skip while the board was still loading). Otherwise
            // this late event would draw the board over the sign-in moment.
            const startedRoomId = data?.gameState?.roomId || null;
            const startedMatchId = data?.gameState?.matchId || null;
            let transitionForInstall = pendingRematchTransition;
            if (data?.rematchTransition) {
                const embeddedTransition = normalizeRematchTransition(data.rematchTransition);
                if (!embeddedTransition) return;
                if (sameRematchTransition(installedRematchTransition, embeddedTransition)) return;
                if (!applyRematchTransition(embeddedTransition)) return;
                transitionForInstall = embeddedTransition;
            }
            if (pendingRematchTransition) {
                if (
                    startedRoomId !== pendingRematchTransition.roomId
                    || startedMatchId !== pendingRematchTransition.matchId
                ) {
                    debugLog('[SocketHandler] Ignoring gameStarted outside pending rematch scope');
                    return;
                }
            } else if (
                installedRematchTransition
                && (
                    startedRoomId !== installedRematchTransition.roomId
                    || startedMatchId !== installedRematchTransition.matchId
                )
            ) {
                debugLog('[SocketHandler] Ignoring gameStarted outside installed rematch scope');
                return;
            }
            if (MoonfallGuest && typeof MoonfallGuest.isEnded === 'function' && MoonfallGuest.isEnded(startedRoomId)) {
                debugLog('[SocketHandler] Ignoring gameStarted for an ended guest session');
                return;
            }
            // Retain the validated room before validating the narrower match/player
            // scope. A malformed packet still represents a server-side admission
            // that must be disconnected when the fatal surface returns to lobby.
            // Without a valid room there is no safe scoped leave, so close the
            // transport immediately rather than orphaning an authoritative game.
            const cleanupScope = pinAcknowledgedStart({ roomId: startedRoomId });
            if (!cleanupScope) {
                activeAuthoritativeScope = null;
                setRoomAffinity(null);
                try {
                    if (socket.connected) socket.disconnect();
                } catch (_) { /* fail closed even if the adapter is already gone */ }
                failFreshGameStartedInstall(new Error('The gameStarted room scope is invalid.'));
                return;
            }
            const startedPlayerId = data?.gameState?.player?.id || null;
            if (
                !normalizeAuthoritativeScopeId(startedMatchId)
                || !normalizeAuthoritativeScopeId(startedPlayerId)
            ) {
                failFreshGameStartedInstall(
                    new Error('The gameStarted match/player scope is invalid.')
                );
                return;
            }
            const pinnedScope = pinAcknowledgedStart({
                roomId: startedRoomId,
                matchId: startedMatchId,
                playerId: startedPlayerId,
            });
            if (!pinnedScope) {
                failFreshGameStartedInstall(new Error('The gameStarted scope is invalid.'));
                return;
            }
            /* A resume of the match already on screen (a tab return, a
               reconnect) reconciles under the board: no battle veil going
               black over it, no coach marks replayed, the mulligan kept. Read
               before anything below replaces the scope. */
            const sameMatchOnScreen = data?.gameResumed === true
                && resumesMatchOnScreen({ roomId: startedRoomId, matchId: startedMatchId });
            const keepsMulligan = sameMatchOnScreen && data?.gameState?.state === 'mulligan';
            try {
                runGameStartedBestEffort(
                    'previous action retirement',
                    () => retirePendingGameplayOwnersOutsideContext(startedRoomId, startedMatchId)
                );
                /* The kept mulligan keeps its warm and its hold on the hand
                   behind it (prepareGameResumeInstall skips it for the same
                   reason): resetting it un-hid the battlefield hand through
                   the overlay on every tab return. */
                if (!keepsMulligan) runGameStartedBestEffort('mulligan warm reset', clearMulliganWarm);
                runGameStartedBestEffort('thinking indicator reset', resetThinkingIndicator);
                runGameStartedBestEffort(
                    'deckout feedback reset',
                    () => DeckoutFeedback?.reset?.()
                );
                runGameStartedBestEffort(
                    'turn timeout feedback reset',
                    () => TurnTimeoutFeedback?.reset?.()
                );
                debugLog('[SocketHandler] gameStarted: RAW DATA RECEIVED:', data);
                debugLog(
                    '[SocketHandler] gameStarted keys:',
                    Object.keys(data || {}),
                    'isTutorial:',
                    data?.isTutorial,
                    'hasTutorialData:',
                    !!data?.tutorialData
                );
                if (window.CLIENT_DEBUG_MODE && data) {
                    debugLog('[SocketHandler] gameStarted: RAW DATA KEYS:', Object.keys(data));
                    if (data.gameState) {
                        debugLog('[SocketHandler] gameStarted: data.gameState KEYS:', Object.keys(data.gameState));
                    }
                }

                const generalGameStateOnStart = data.gameState || {};
                const playerOnStart = generalGameStateOnStart.player || null;
                const opponentOnStart = generalGameStateOnStart.opponent || null;
                if (!playerOnStart) {
                    console.warn('[SocketHandler] gameStarted: data.gameState.player is missing.');
                }
                if (!opponentOnStart) {
                    console.warn('[SocketHandler] gameStarted: data.gameState.opponent is missing.');
                }

                // Commit the authoritative match scope before any presentation
                // dependency runs. The structural view handoff follows
                // immediately, so an optional enhancement can never leave a
                // server-active match underneath interactive lobby controls.
                GameState.update({
                    currentTurn: generalGameStateOnStart.currentTurn,
                    state: generalGameStateOnStart.state,
                    winner: generalGameStateOnStart.winner || null,
                    endReason: generalGameStateOnStart.endReason || null,
                    isYourTurn: generalGameStateOnStart.isYourTurn,
                    roomId: generalGameStateOnStart.roomId,
                    matchId: generalGameStateOnStart.matchId,
                    battleLocation: generalGameStateOnStart.battleLocation,
                    player: playerOnStart,
                    opponent: opponentOnStart,
                    playerId: playerOnStart ? playerOnStart.id : null,
                    gameStarted: true,
                    mulliganTimeoutMs: generalGameStateOnStart.mulliganTimeoutMs,
                    mulliganDeadlineAt: generalGameStateOnStart.mulliganDeadlineAt,
                    serverNow: generalGameStateOnStart.serverNow ?? data.serverNow,
                    resumeRevision: data.resumeRevision || 0,
                    isTutorial: data.isTutorial || false,
                    tutorialStep: data.tutorialStep || 0
                });
                rememberGameResumeState(GameState.get(), data);
                installedMatchScope = { roomId: startedRoomId, matchId: startedMatchId };
                /* THE BOARD IS BUILT UNDER A COVER AND SHOWN WHOLE. Announced
                   before the view switch, so the match loading screen — or the
                   boot veil on a first visit or a mid-match refresh — is over
                   the board before it exists; a board already on screen (a
                   rematch, a reconnect) gets null and is never covered
                   (ui/match-loading.js). */
                let matchArrival = null;
                try {
                    matchArrival = MatchLoading?.arrive?.() || null;
                } catch (error) {
                    console.error('[SocketHandler] gameStarted match arrival failed:', error);
                }
                Lobby.switchToGameView({
                    veil: !sameMatchOnScreen && generalGameStateOnStart.state !== 'mulligan',
                    ...(matchArrival ? { arrival: matchArrival } : {}),
                });

                runGameStartedBestEffort('guest resume receipt', () => {
                    if (data.tutorialResumed === true && MoonfallGuest?.consumeResumeToken) {
                        MoonfallGuest.consumeResumeToken();
                    } else if (
                        data.tutorialResumeRejected === true
                        && MoonfallGuest?.clearResumeToken
                    ) {
                        MoonfallGuest.clearResumeToken();
                    }
                });
                runGameStartedBestEffort(
                    'hand tracking reset',
                    () => Cards?.resetHandTracking?.()
                );
                runGameStartedBestEffort(
                    'asset warmup',
                    () => AssetPreloader?.beginMatchWarm?.({
                        player: playerOnStart,
                        opponent: opponentOnStart,
                    })
                );

                Board.renderGameState(data);

                document.getElementById('end-turn-btn')?.classList.toggle(
                    'end-turn-btn--your-turn',
                    !!generalGameStateOnStart.isYourTurn
                        && generalGameStateOnStart.state !== 'mulligan'
                );

                if (data.isTutorial && Tutorial) {
                    runGameStartedBestEffort('tutorial presentation', () => {
                        if (sameMatchOnScreen && Tutorial.isActive?.() === true) {
                            /* The coach marks on screen belong to this match.
                               Catch up through the ordinary step path if the
                               server moved on while the tab was away; never
                               restart (that replayed the current tip and
                               brought back one the player had dismissed). */
                            const serverStep = Number(data.tutorialStep) || 0;
                            for (let guard = 0; guard < 16 && Number(Tutorial.getStep?.()) < serverStep; guard += 1) {
                                Tutorial.advance?.();
                            }
                            return;
                        }
                        const tutorialStartOptions = {
                            step: data.tutorialStep || 0,
                            resumed: data.tutorialResumed === true
                                || data.guestReloadResumed === true,
                        };
                        if (data.tutorialData) {
                            Tutorial.setTutorialData(data.tutorialData);
                            Tutorial.start(tutorialStartOptions);
                            return;
                        }
                        const backendUrl = (
                            window.__MOONFALL_CONFIG
                            && window.__MOONFALL_CONFIG.socketUrl
                        ) || '';
                        /* ASK IN THE PLAYER'S LANGUAGE. This fallback fetched
                           the document with no language at all, and the route
                           answered with the raw English file — so a
                           non-English player who came through here (the
                           game-start bundle did not carry the tutorial) read
                           English coach marks. Both halves were English-only;
                           fixing only the route would have changed nothing. */
                        const tutorialDataPath = `/tutorial/data?lang=${encodeURIComponent(getCurrentLanguage())}`;
                        const tutorialDataUrl = backendUrl
                            ? `${backendUrl}${tutorialDataPath}`
                            : tutorialDataPath;
                        return fetch(tutorialDataUrl)
                            .then((response) => (
                                response.ok
                                    ? response.json()
                                    : Promise.reject(new Error('Failed to load tutorial data'))
                            ))
                            .then((tutorialData) => {
                                Tutorial.setTutorialData(tutorialData);
                                Tutorial.start(tutorialStartOptions);
                            });
                    });
                }

                const presentsMulligan = generalGameStateOnStart.state === 'mulligan' && Mulligan;
                let showsMulligan = false;
                if (presentsMulligan) {
                    const playerHand = playerOnStart?.hand || [];
                    const opponentReady = generalGameStateOnStart.opponentMulliganReady || false;
                    /* The gameStateUpdated rule: a mulligan on screen for
                       this match keeps its hand, choices and timer (the
                       timer already counts to the same deadline); a player
                       who has kept their hand is never shown it again. */
                    if (sameMatchOnScreen && Mulligan.isActive?.() === true) {
                        Mulligan.updateOpponentStatus?.(opponentReady);
                    } else if (generalGameStateOnStart.mulliganReady !== true) {
                        Mulligan.show(playerHand, opponentReady, {
                            timeoutMs: generalGameStateOnStart.mulliganTimeoutMs,
                            deadlineAt: generalGameStateOnStart.mulliganDeadlineAt
                                ?? data.deadlines?.mulliganDeadlineAt,
                            serverNow: generalGameStateOnStart.serverNow ?? data.serverNow,
                        });
                        showsMulligan = true;
                    }
                }
                // The first screen exists now: the mulligan presents itself on
                // its reveal; any other start is presented by the whole board.
                runGameStartedBestEffort(
                    'match arrival',
                    () => MatchLoading?.settle?.(showsMulligan ? 'mulligan' : 'board'),
                );
                if (transitionForInstall) {
                    markRematchInstalled(transitionForInstall);
                    void acknowledgeRematchInstalled(transitionForInstall);
                }
                // A state of this match that overtook this packet (the tutorial
                // AI's keep) is applied now, over the screen this packet built.
                runGameStartedBestEffort('early state reconcile', () => reconcileHeldArrival(
                    startedRoomId,
                    startedMatchId,
                    generalGameStateOnStart.serverNow ?? data.serverNow,
                ));
            } catch (error) {
                // A rematch keeps its durable recovery receipt until the full
                // board installs; its watchdog must be allowed to retry.
                if (transitionForInstall) throw error;
                failFreshGameStartedInstall(error);
            }
        };
        socket.on('gameStarted', handleGameStartedPacket);

        handleGameStateUpdatedPacket = (data) => {
            const updatedRoomId = data?.gameState?.roomId || null;
            const updatedMatchId = data?.gameState?.matchId || null;
            const currentState = GameState?.get?.() || {};
            retirePendingGameplayOwnersOutsideContext(
                currentState.roomId || null,
                currentState.matchId || null,
            );
            const staleRoom = currentState.roomId && updatedRoomId
                && currentState.roomId !== updatedRoomId;
            const staleMatch = currentState.matchId && updatedMatchId
                && currentState.matchId !== updatedMatchId;
            /* A match this page has not installed yet: no match at all, or
               only the one GameState restored from storage (a returning
               visitor's last room reads as "stale" to the new one). Its
               install packet is still to come (reconcileHeldArrival). */
            const beforeItsInstall = Boolean(updatedRoomId)
                && !isInstalledMatch(updatedRoomId, updatedMatchId)
                && (!currentState.roomId || ((staleRoom || staleMatch) && !installedMatchScope));
            if ((staleRoom || staleMatch) && !beforeItsInstall) {
                debugLog('[SocketHandler] Ignoring stale gameStateUpdated snapshot', {
                    currentRoomId: currentState.roomId || null,
                    currentMatchId: currentState.matchId || null,
                    updatedRoomId,
                    updatedMatchId,
                });
                return;
            }
            if (MoonfallGuest && typeof MoonfallGuest.isEnded === 'function' && MoonfallGuest.isEnded(updatedRoomId)) {
                debugLog('[SocketHandler] Ignoring gameStateUpdated for an ended guest session');
                return;
            }
            if (beforeItsInstall) {
                heldArrivalSnapshot = data;
                debugLog('[SocketHandler] Holding a gameStateUpdated that arrived before its gameStarted', {
                    updatedRoomId,
                    updatedMatchId,
                });
                return;
            }
            // State broadcast after an action also marks the cast as resolved
            // (covers empty-story resolutions where no storyUpdate is emitted).
            if (SpellAnnounce?.resolve) SpellAnnounce.resolve();
            // Log 1: CRITICAL - Log the raw 'data' object's keys.
            debugLog('[SocketHandler] gameStateUpdated: RAW DATA RECEIVED FROM SERVER (logging keys):');
            if (window.CLIENT_DEBUG_MODE) {
                if (data) {
                    debugLog('[SocketHandler] gameStateUpdated: RAW DATA KEYS:', Object.keys(data));
                    if (data.gameState) {
                        debugLog('[SocketHandler] gameStateUpdated: data.gameState KEYS:', Object.keys(data.gameState));
                    }
                } else {
                    debugLog('[SocketHandler] gameStateUpdated: RAW DATA is null/undefined');
                }
            }

            if (!data) {
                console.error('[SocketHandler] gameStateUpdated: Received data is null or undefined. Aborting update.');
                return;
            }

            // Capture storybook context (set when game ends server-side).
            try {
                if (MoonfallStorybookContext) {
                    MoonfallStorybookContext.setFromGameState(data);
                }
            } catch (_) { /* non-critical */ }

            const generalGameState = data.gameState || {};
            if (!data.gameState) {
                console.warn('[SocketHandler] gameStateUpdated: data.gameState property is missing. Using an empty state.');
            }
            debugLog('[SocketHandler] gameStateUpdated: Extracted generalGameState (from data.gameState):', generalGameState);

            let playerDataToUse = null;
            let opponentDataToUse = null;
            let playerDataSource = "unknown", opponentDataSource = "unknown";

            // Correctly source player and opponent data from data.gameState
            if (generalGameState.player) {
                playerDataToUse = generalGameState.player;
                playerDataSource = "data.gameState.player";
                debugLog('[SocketHandler] gameStateUpdated: Using player data from data.gameState.player:', playerDataToUse);
            } else {
                console.warn('[SocketHandler] gameStateUpdated: data.gameState.player is missing.');
            }

            if (generalGameState.opponent) {
                opponentDataToUse = generalGameState.opponent;
                opponentDataSource = "data.gameState.opponent";
                debugLog('[SocketHandler] gameStateUpdated: Using opponent data from data.gameState.opponent:', opponentDataToUse);
            } else {
                console.warn('[SocketHandler] gameStateUpdated: data.gameState.opponent is missing.');
            }

            try {
                lastGameState = JSON.parse(JSON.stringify(data));
            } catch (e) {
                console.error('[SocketHandler] gameStateUpdated: Error cloning the latest state:', e);
                lastGameState = data;
            }

            // Deep clone previous state to avoid mutation after GameState.update
            const previousState = JSON.parse(JSON.stringify(GameState.get()));

            if (generalGameState.state !== 'mulligan' && generalGameState.state !== 'playing') {
                clearMulliganWarm();
            }

            // Only the post-submit, player-scoped authoritative view may name
            // replacement art. This happens before the board rebuild so the
            // normal hand remains hidden until its bounded warm gate settles.
            const authoritativeMulliganWarm = beginAuthoritativeMulliganWarm(
                generalGameState,
                playerDataToUse,
            );

            const previousTurn = previousState.isYourTurn;
            const previousHandSize = previousState.player && previousState.player.hand ? previousState.player.hand.length : 0;
            const hasAudioBaseline = hasComparableAudioBaseline(previousState, generalGameState);

            const becameYourTurn = !previousTurn && (generalGameState.isYourTurn || (playerDataToUse && playerDataToUse.isYourTurn));
            const becameEnemyTurn = previousTurn && !generalGameState.isYourTurn;

            const updatePayload = {
                state: generalGameState.state,
                winner: generalGameState.winner || null,
                endReason: generalGameState.endReason || null,
                currentTurn: generalGameState.currentTurn,
                isYourTurn: generalGameState.isYourTurn, // isYourTurn should be directly from gameState
                roomId: generalGameState.roomId,
                matchId: generalGameState.matchId,
                battleLocation: generalGameState.battleLocation,
                player: playerDataToUse, // This is now the full player object from data.gameState.player
                opponent: opponentDataToUse, // This is now the full opponent object from data.gameState.opponent
                playerId: playerDataToUse ? playerDataToUse.id : undefined, // Ensure self-targeting uses character ID
                mulliganTimeoutMs: generalGameState.mulliganTimeoutMs,
                mulliganDeadlineAt: generalGameState.mulliganDeadlineAt,
                serverNow: generalGameState.serverNow,
            };

            debugLog(`[SocketHandler] gameStateUpdated: Player data sourced from: ${playerDataSource}`);
            debugLog(`[SocketHandler] gameStateUpdated: Opponent data sourced from: ${opponentDataSource}`);
            debugLog('[SocketHandler] gameStateUpdated: FINAL Payload for GameState.update():', updatePayload);
            GameState.update(updatePayload);
            const installedState = GameState.get();
            rememberGameResumeState(installedState, {
                isTutorial: installedState?.isTutorial === true,
                resumeRevision: installedState?.resumeRevision || 0,
            });

            // Tutorial gates read GameState (mana, hand, active player), so notify only
            // after the authoritative update has been committed locally.
            if (becameYourTurn && Tutorial?.isActive?.()) {
                Tutorial.onGameEvent('turnStart', { turnNumber: generalGameState.currentTurn });
            }
            if (becameEnemyTurn && Tutorial?.isActive?.()) {
                Tutorial.onGameEvent('enemyTurnStart');
            }

            // Turn banner: established games only (baseline gate), never during
            // mulligan or on turn 1. The tutorial owns the returning-player cue,
            // but still needs a clear opponent-turn transition.
            const tutorialActive = Boolean(Tutorial?.isActive?.());
            const showTurnBanner = hasAudioBaseline
                && generalGameState.state !== 'mulligan'
                && (generalGameState.currentTurn || 0) > 1
                && !(Mulligan?.isActive?.());
            if (showTurnBanner && becameYourTurn && !tutorialActive) TurnBanner?.show('your');
            else if (showTurnBanner && becameEnemyTurn) TurnBanner?.show('enemy');

            // One-shot glow replays only when this class transitions off -> on.
            document.getElementById('end-turn-btn')?.classList.toggle(
                'end-turn-btn--your-turn',
                !!generalGameState.isYourTurn && generalGameState.state !== 'mulligan'
            );

            // --- Play sound effects and visual feedback for health changes ---
            if (hasAudioBaseline && SoundEffects) {
                try {
                    const prevMyHealth = (previousState && previousState.player && typeof previousState.player.health === 'number')
                        ? previousState.player.health : null;
                    const newMyHealth = (playerDataToUse && typeof playerDataToUse.health === 'number')
                        ? playerDataToUse.health : null;

                    if (prevMyHealth !== null && newMyHealth !== null && prevMyHealth !== newMyHealth) {
                        if (newMyHealth < prevMyHealth) {
                            const dmg = prevMyHealth - newMyHealth;
                            SoundEffects.play('damage');
                            if (Effects) {
                                // Every hit registers: FX scales shake with damage
                                // (light kick for 1-3, full shake above).
                                Effects.screenShake(dmg, 450);
                                setTimeout(() => {
                                    if (Effects) {
                                        Effects.portraitShake('player', dmg, 500);
                                    }
                                }, 0);
                            }
                            FX?.damageNumber(document.getElementById('player-portrait'), dmg, { kind: 'damage' });
                        } else if (newMyHealth > prevMyHealth) {
                            const heal = newMyHealth - prevMyHealth;
                            SoundEffects.play('heal');
                            FX?.damageNumber(document.getElementById('player-portrait'), heal, { kind: 'heal' });
                        }
                    }

                    const prevOppHealth = (previousState && previousState.opponent && typeof previousState.opponent.health === 'number')
                        ? previousState.opponent.health : null;
                    const newOppHealth = (opponentDataToUse && typeof opponentDataToUse.health === 'number')
                        ? opponentDataToUse.health : null;

                    if (prevOppHealth !== null && newOppHealth !== null && prevOppHealth !== newOppHealth) {
                        if (newOppHealth < prevOppHealth) {
                            const dmg = prevOppHealth - newOppHealth;
                            SoundEffects.play('damage');
                            if (Effects) {
                                setTimeout(() => {
                                    if (Effects) {
                                        Effects.portraitShake('opponent', dmg, 500);
                                    }
                                }, 0);
                                // Landing a big hit gives a small satisfying screen kick.
                                if (dmg > 4) Effects.screenShake(dmg * 0.5, 300);
                            }
                            FX?.damageNumber(document.getElementById('opponent-portrait'), dmg, { kind: 'damage' });
                        } else if (newOppHealth > prevOppHealth) {
                            const heal = newOppHealth - prevOppHealth;
                            SoundEffects.play('heal');
                            FX?.damageNumber(document.getElementById('opponent-portrait'), heal, { kind: 'heal' });
                        }
                    }
                } catch (e) {
                    console.error('Health sound diff error', e);
                }
            }

            // Action cues are intentionally absent from snapshot reconciliation.
            // The post-validation spellCastStarted/gameActionStarted events own them;
            // replaying a state diff here made opponent actions sound two or three
            // times and made the final copy arrive long after the physical motion.

            // Play draw sound only if a card was actually added to the hand at turn start
            if (hasAudioBaseline && becameYourTurn && SoundEffects) {
                const newHandSize = playerDataToUse && playerDataToUse.hand ? playerDataToUse.hand.length : 0;
                if (newHandSize > previousHandSize) {
                    debugLog('Playing draw sound – a card was drawn to your hand');
                    SoundEffects.play('draw');
                } else {
                    debugLog('No card drawn (hand full or deck empty) – draw sound suppressed');
                }
            }

            // If the turn has changed (especially from your turn to opponent's turn)
            // Make sure we clear any in-progress actions or drags
            if (previousTurn && !data.gameState.isYourTurn) {
                debugLog('Turn changed from yours to opponent - cleaning up UI state');
                GameState.set('draggedCard', null);
                GameState.set('draggedCardIndex', null);
                GameState.set('dragSourceType', null);

                // Remove any active drag visual indicators
                document.querySelectorAll('.valid-target').forEach(el => {
                    el.classList.remove('valid-target');
                });

                // A correlated gameplay owner remains authoritative until its
                // terminal event even if the turn snapshot arrives first.
                if (!hasPendingGameplayOwner()) {
                    GameState.setActionInProgress(false);
                }

                // Clear all 'dragging' classes
                document.querySelectorAll('.dragging').forEach(el => {
                    el.classList.remove('dragging');
                });
            }

            // Cleanup any lingering UI indicators from previous code
            if (document.body.classList.contains('reconnecting')) {
                document.body.classList.remove('reconnecting');
            }

            const gameBoard = document.getElementById('game-view');
            if (gameBoard && gameBoard.classList.contains('reconnecting')) {
                gameBoard.classList.remove('reconnecting');
            }

            // The authoritative board is about to render — any held enemy
            // arrival not committed by this exact snapshot is stale. A matching
            // pending summon keeps its captured pre-render hand geometry.
            EnemyReplay?.onSnapshot?.(generalGameState);

            // Board-diff FX must capture the outgoing DOM (death ghost rects)
            // BEFORE renderGameState rebuilds it, and play back AFTER the new
            // DOM exists (summon entrances / impact rings target fresh nodes).
            let fxPlan = null;
            if (hasAudioBaseline && generalGameState.state === 'playing' && FX) {
                try {
                    fxPlan = FX.planBoardEffects(previousState, generalGameState);
                    if (fxPlan) FX.captureRects(fxPlan);
                } catch (e) {
                    console.error('FX board diff error', e);
                    fxPlan = null;
                }
            }

            Board.renderGameState(data);
            // The matching authoritative board card now exists. EnemyReplay
            // can hide only that duplicate and hand the prepared physical card
            // to it without re-resolving a now-shifted opponent-hand index.
            EnemyReplay?.onSnapshotRendered?.(generalGameState);
            DeckoutFeedback?.handleStateUpdate?.(previousState, generalGameState);
            TurnTimeoutFeedback?.handleStateUpdate?.(previousState, generalGameState);

            if (fxPlan) {
                try {
                    FX.playBoardEffects(fxPlan);
                } catch (e) {
                    console.error('FX board playback error', e);
                }
            }

            // Handle mulligan phase updates
            if (generalGameState.state === 'mulligan' && Mulligan) {
                // Update opponent status if mulligan UI is active
                if (Mulligan.isActive()) {
                    Mulligan.updateOpponentStatus(generalGameState.opponentMulliganReady || false);
                } else {
                    // Show mulligan UI if not already shown, BUT only if we aren't ready yet
                    const amIReady = hasSubmittedMulligan || (generalGameState.mulliganReady === true);

                    if (!amIReady) {
                        const playerHand = playerDataToUse?.hand || [];
                        const opponentReady = generalGameState.opponentMulliganReady || false;
                        // Reconnect/re-show path: re-kick the visible-first
                        // warm (cached URLs resolve instantly) so the reveal
                        // gate has something meaningful to wait on.
                        AssetPreloader?.beginMatchWarm?.({
                            player: playerDataToUse,
                            opponent: opponentDataToUse,
                        });
                        Mulligan.show(playerHand, opponentReady, {
                            timeoutMs: generalGameState.mulliganTimeoutMs,
                            deadlineAt: generalGameState.mulliganDeadlineAt,
                            serverNow: generalGameState.serverNow,
                        });
                    } else {
                        debugLog('[SocketHandler] Skipping mulligan UI show (already ready)');
                    }
                }
            } else if (generalGameState.state === 'playing') {
                // NOTE: no battle veil here. Keep Hand hides the mulligan
                // overlay client-side (~t=39ms) BEFORE this 'playing' snapshot
                // arrives (~t=212ms), so a veil fired here only ever flashed
                // black over the already-revealed board. The mulligan overlay's
                // own fade-out (Mulligan.hide) is the reveal now.
                // Mulligan phase ended — clean up any remaining mulligan UI
                const revealScheduled = scheduleMulliganHandReveal(
                    authoritativeMulliganWarm,
                    generalGameState,
                );
                if (!revealScheduled && Mulligan?.isActive?.()) {
                    debugLog('[SocketHandler] Mulligan phase ended, hiding overlay');
                    if (Tutorial?.isActive?.()) {
                        Tutorial.onGameEvent('mulliganComplete');
                    }
                    Mulligan.hide();
                }
                // Always clear the waiting banner when transitioning to playing
                if (!revealScheduled && Mulligan?.hideWaitingBanner) {
                    Mulligan.hideWaitingBanner();
                }
            }

            // Mirror server-reported action state to keep the loading indicator accurate
            try {
                const serverState = data?.gameState;
                reconcilePendingActionIntentCancellation(serverState);
                const localActionInProgress = Boolean(
                    hasPendingGameplayOwner()
                    || (serverState?.actionInProgress && serverState.isYourTurn === true)
                );
                GameState.setActionInProgress(localActionInProgress);
            } catch (_) {
                GameState.setActionInProgress(hasPendingGameplayOwner());
            }
        };
        socket.on('gameStateUpdated', handleGameStateUpdatedPacket);

        socket.on('updateLanguage', (data) => {
            const language = data && data.language;
            if (!language || !i18n || typeof i18n.setLanguage !== 'function') return;
            i18n.setLanguage(language, false);
        });

        socket.on('playerDisconnected', (data) => {
            resetThinkingIndicator();
            debugLog('Player disconnected:', data);
            alert(translate('game.playerDisconnected', `Player ${data.playerId} has disconnected from the game.`, {
                player: data.playerId,
            }));
            // For simplicity, just redirect to the home page
            window.location.href = '/';
        });

        // Removed duplicate 'error' handler that triggered alert popups
        // to avoid disruptive UX during expected transient reconnect errors.

        // Initial story event (starts Solo Adventure)
        socket.on('initialStory', (data) => {
            debugLog('Initial story received:', data);
            // Same stale-guest guard as gameStarted (see above).
            if (MoonfallGuest && typeof MoonfallGuest.isEnded === 'function' && MoonfallGuest.isEnded()) {
                debugLog('[SocketHandler] Ignoring initialStory for an ended guest session');
                return;
            }
            Lobby.switchToGameView();
            if (data) {
                // Determine if data is string or object
                const text = typeof data === 'string' ? data : data.story;
                // Prologue entry: no action metadata, lands neutral in the feed.
                StoryFeed.onStoryUpdate({ story: text });
            }
        });

        // Story events — the payload carries the full play identity
        // (playerId / turn / cardInstanceId / cardId / cardName / cardType),
        // which the feed uses for side attribution and card linkage.
        socket.on('storyUpdate', (data) => {
            debugLog('Story update received:', data);
            // A story landing means the pending cast resolved — release the
            // sticky enemy-cast float (it holds while the storyteller works).
            if (SpellAnnounce?.resolve) SpellAnnounce.resolve();
            if (data.story) {
                // Story is presentation derived from an already accepted action.
                // It may arrive seconds later and must never re-voice that action.
                Cards?.consumeStellarSpellCastSoundSuppression?.(data);

                StoryFeed.onStoryUpdate(data);
            }
        });

        // One canonical lifecycle signal covers every action source. It voices
        // nothing itself: the summon cue belongs to the LANDING (the board
        // entrance in fx.js, or the enemy traveler in enemy-replay.js), which
        // alone knows its contact frame — acceptance precedes it by an amount
        // nobody can know. Only a known opponent continues into the
        // thinking-indicator presentation below.
        socket.on('gameActionStarted', (data) => {
            const correlation = getKnownActionCorrelation(data, { requireActionType: true });
            if (!correlation) return;
            if (!correlation.isOpponent) return;
            if (retiredOpponentActionIds.has(correlation.actionId)) return;
            if (
                activeOpponentAction
                && activeOpponentAction.roomId === correlation.roomId
                && activeOpponentAction.matchId === correlation.matchId
                && activeOpponentAction.actionId === correlation.actionId
                && activeOpponentAction.playerId === correlation.playerId
            ) return;

            if (activeOpponentAction) retireOpponentAction(activeOpponentAction.actionId);
            activeOpponentAction = correlation;

            ThinkingIndicator?.enemyActionStarted?.();
        });

        socket.on('gameActionSettled', (data) => {
            const correlation = getKnownActionCorrelation(data);
            if (!correlation) return;
            if (typeof data?.succeeded !== 'boolean') return;

            if (data.succeeded === false) {
                Cards?.rejectStellarSpellCast?.(data);
            } else {
                // State reconciliation and storyUpdate are emitted before the
                // settlement on every server action path. The story normally
                // consumes this marker; a storyless success still needs a
                // terminal cleanup so it cannot suppress a later spell.
                Cards?.consumeStellarSpellCastSoundSuppression?.(data);
            }

            StoryFeed?.onGameActionSettled?.({
                roomId: correlation.roomId,
                matchId: correlation.matchId,
                actionId: correlation.actionId,
                playerId: correlation.playerId,
                succeeded: data.succeeded,
            });
            if (!correlation.isOpponent) return;
            // The back the player watched leave is held out for the whole
            // resolution; the settlement ends it (a failed cast, or a caster
            // drawing one card, leaves the count where it was).
            EnemyFan?.settleDeparture?.();

            const matchesActiveAction = activeOpponentAction
                && activeOpponentAction.roomId === correlation.roomId
                && activeOpponentAction.matchId === correlation.matchId
                && activeOpponentAction.actionId === correlation.actionId
                && activeOpponentAction.playerId === correlation.playerId;
            retireOpponentAction(correlation.actionId);
            if (!matchesActiveAction) return;
            activeOpponentAction = null;

            ThinkingIndicator?.enemyActionSettled?.(data.succeeded);
        });

        // Float the full card face when the OPPONENT casts a spell so the
        // player can actually read it (parity with the Unity client, which
        // already consumes this event).
        /* The opponent's spell TRAVELS (enemy-replay glides the card to its
           target); the impact has to land when the card does, not when the
           event arrives. Matches glidePlay's default duration. */
        const SPELL_GLIDE_LANDING_MS = 500;

        socket.on('spellCastStarted', (data) => {
            const correlation = getDetailedActionCorrelation(data);
            if (!correlation || !claimActionSound(correlation.actionId)) return;
            const stellarCastOwnsSound = Cards?.registerStellarSpellCast?.(data) === true;
            warmOpponentActionCards(data, data?.card ? [data.card] : []);
            let ordinaryCuePlayed = false;
            const playOrdinaryCue = () => {
                if (stellarCastOwnsSound || ordinaryCuePlayed) return;
                ordinaryCuePlayed = true;
                SoundEffects?.play?.('magic');
            };
            let announcementOwnsSound = false;
            try {
                if (SpellAnnounce) {
                    const state = GameState.get() || {};
                    announcementOwnsSound = SpellAnnounce.handleSpellCastStarted({
                        ...data,
                        playerName: state.opponent?.name || null
                    }, state.playerId, {
                        // Queuing the float transfers cue ownership only while
                        // its card can actually reach a painted frame. A failed
                        // raster hands the same once-guarded cue back here.
                        onPresentationAbandoned: playOrdinaryCue,
                    }) === true;
                }
            } catch (e) {
                console.error('Failed to float enemy spell cast:', e);
            }
            // An opponent's ordinary cue is synchronized to the settled card
            // announcement. Local ordinary casts (and announcement failures)
            // sound here, in the authoritative started-event task. Stellar cast
            // media owns its authored audio instead of stacking generic magic.
            if (!stellarCastOwnsSound && !announcementOwnsSound) {
                playOrdinaryCue();
            }
            /* The ARRIVAL beat, for either caster.
               A targeted Spell used to produce nothing at the target: the card
               left the hand, the effect applied, and the thing that was hit
               never acknowledged it. The opponent's cast gets a travel glide
               below but no landing, and your own cast is deliberately skipped
               by the replay module because YOU performed the drag — so the
               local case had no animation whatsoever ("dragging spells on a
               target doesn't have any animation").

               Presentation only, and strictly downstream: this observes the
               authoritative started-event, draws on whatever element the shared
               resolver points at, and changes nothing. Failure to draw is
               swallowed — a missing flourish must never look like a different
               gameplay result. The opponent's strike waits for its glide to
               land; yours is immediate, because your drag was the travel. */
            try {
                /* Only a cast that DECLARED a target gets the arrival beat. The
                   shared resolver falls back to a portrait when targetType is
                   absent — correct for attacks, wrong for a non-targetable
                   spell, which then flashed on the enemy summoner every time
                   ("it blinks on the enemy summoner ... the untargetable one").
                   An untargeted spell has nothing to land on, so it lands on
                   nothing. */
                const declared = EnemyReplay?.hasDeclaredSpellTarget?.(data) === true;
                const targetEl = declared
                    ? EnemyReplay?.resolveTargetEl?.(data)
                    : null;
                const state = GameState.get() || {};
                const isOwnCast = data?.playerId && data.playerId === state.playerId;
                const cost = Number(data?.card?.manaCost);
                const power = Number.isFinite(cost)
                    ? Math.max(0.6, Math.min(1.8, 0.6 + cost * 0.16))
                    : 1;
                const delayMs = isOwnCast ? 0 : SPELL_GLIDE_LANDING_MS;
                if (targetEl) {
                    /* WHOSE side the spell lands on decides the beat. A target
                       on the caster's OWN side gets the touch — light gathered
                       in, motes rising, the ally lifting: positive-leaning but
                       open, because what the spell DID is the storyteller's to
                       say and may still be a wound. The other side gets the
                       strike, as before. ("make targeting of friendly targets
                       with a card have a more positive looking anim. now it
                       looks like a hit regardless. but keep it ambiguous".)
                       Read off the element the beat is drawn on; a relationship
                       nobody can state is drawn as the strike the game had. */
                    const friendly = EnemyReplay?.isFriendlyTarget?.(data, targetEl) === true;
                    if (friendly) FX?.spellTouch?.(targetEl, { power, delayMs });
                    else FX?.spellStrike?.(targetEl, { power, delayMs });
                } else {
                    /* An untargeted cast is not a cast without a beat. Suppressing
                       the strike stopped it lying about hitting the enemy summoner,
                       but left these spells with no acknowledgement at all — and an
                       untargetable spell may affect an unbounded number of things,
                       so pointing at one is a smaller truth than pointing at none.
                       The surge starts at the CASTER and crosses the board with no
                       direction and nothing singled out. */
                    /* The SPELL CARD is the origin — owner call ("make it happen on
                       the spell card instead of making it happen on the
                       summoner"). Your own cast erupts from the card still in
                       your hand; an enemy cast from the announce float's
                       revealed face; the caster's portrait remains the last
                       honest anchor when neither exists. */
                    const origin = EnemyReplay?.surgeOriginEl?.(data)
                        || document.getElementById(
                            data?.playerId && data.playerId === state.playerId
                                ? 'player-portrait' : 'opponent-portrait',
                        );
                    FX?.spellSurge?.(origin, { power, delayMs });
                }
            } catch (_) { /* the cast itself is authoritative; the flourish is not */ }

            try {
                EnemyReplay?.onSpellCast?.(data);
            } catch (e) {
                console.error('Failed to replay enemy spell cast:', e);
            }
            try {
                // Pre-resolution pending tile: the feed marks float-announced
                // casts so the story fill stays silent (no double toast).
                StoryFeed?.onSpellCastStarted?.(data);
            } catch (e) {
                console.error('Failed to add pending story tile:', e);
            }
        });

        // Board attacks, both sides: a pre-resolution action event synthesized
        // into a lunge. The server emits it only after every validation gate,
        // so routing my OWN accepted attack here is what makes the strike
        // observe a decision instead of predicting one at pointer release.
        // The modules still gate pre-baseline hydration; recorded tutorial
        // board attacks intentionally replay because their cue layer has no
        // equivalent source-to-target motion.
        socket.on('characterActionStarted', (data) => {
            const correlation = getDetailedActionCorrelation(data);
            if (!correlation) return;
            try {
                // The event is emitted post-validation, so my own accepted
                // attack gets the same authoritative lunge the opponent's does.
                if (correlation.isOpponent) EnemyReplay?.onCharacterAction?.(data);
                else EnemyReplay?.onOwnCharacterAction?.(data);
            } catch (e) {
                console.error('Failed to replay board action:', e);
            }
            try {
                StoryFeed?.onCharacterActionStarted?.(data);
            } catch (e) {
                console.error('Failed to add pending board-action story tile:', e);
            }
        });

        socket.on('summonStarted', (data) => {
            const correlation = getDetailedActionCorrelation(data);
            if (!correlation?.isOpponent) return;
            const topLevelInstanceId = typeof data?.cardInstanceId === 'string'
                ? data.cardInstanceId.trim()
                : '';
            const cardInstanceId = typeof data?.card?.instanceId === 'string'
                ? data.card.instanceId.trim()
                : '';
            if (topLevelInstanceId && cardInstanceId && topLevelInstanceId !== cardInstanceId) return;
            warmOpponentActionCards(data, [{
                id: data?.cardId,
                instanceId: data?.cardInstanceId,
                picture: data?.picture,
                imageUrl: data?.imageUrl,
            }]);
            try {
                EnemyReplay?.onSummon?.(data);
            } catch (e) {
                console.error('Failed to replay enemy summon:', e);
            }
        });

        socket.on('summonCompleted', (data) => {
            try {
                StoryFeed?.onSummonCompleted?.(data);
            } catch (e) {
                console.error('Failed to add completed summon to story feed:', e);
            }
        });

        // Reveal mechanic: the storyteller exposed hidden cards to this player.
        socket.on('cardsRevealed', (data) => {
            warmCardsWithoutBlocking(data?.cards || []);
            try {
                if (!SpellAnnounce) return;
                const state = GameState.get() || {};
                SpellAnnounce.handleCardsRevealed(data, state.playerId);
            } catch (e) {
                console.error('Failed to float revealed cards:', e);
            }
        });

        socket.on('actionIntentOptions', (data) => {
            try {
                const owner = pendingLegacyGameplayOwner;
                if (!isCurrentLegacyGameplayOwner(owner)
                    || owner.action !== 'requestActionIntentOptions'
                    || !isExactActionCorrelation(data, owner.action, owner.clientActionId)
                    || data?.requestId !== owner.requestId) {
                    debugLog('[SocketHandler] Ignoring stale action intent options');
                    return;
                }
                let handled = true;
                if (ActionIntent && typeof ActionIntent.handleOptions === 'function') {
                    handled = ActionIntent.handleOptions(data) !== false;
                }
                // A late options packet for a superseded request must not clear
                // the timeout protecting the currently active request. Valid
                // options start a fresh decision window. The server-side
                // pending intent expires only with the TURN, which the turn
                // deadline can end on its own — it cancels the request and
                // announces `actionIntentCancelled` when it does.
                if (!handled) return;
                owner.rearmTimeout?.();
            } catch (e) {
                console.error('Failed to handle action intent options:', e);
            }
        });

        socket.on('actionIntentError', (data) => {
            try {
                pendingLegacyGameplayOwner?.handleIntentError?.(data);
            } catch (e) {
                console.error('Failed to handle action intent error:', e);
            }
        });

        // Async image recap events (non-blocking)
        socket.on('mangaGenerationStarted', (data) => {
            try {
                if (!data || data.status !== 'pending' || typeof data.turn !== 'number') return;
                if (!isCurrentMatchEvent(data)) return;
                StoryFeed.onMangaGenerationStarted(data);
            } catch (e) {
                console.error('Failed to render pending manga panel:', e);
            }
        });

        socket.on('turnResolutionComplete', (data) => {
            try {
                if (!data || typeof data.turn !== 'number') return;
                if (!isCurrentMatchEvent(data)) return;
                StoryFeed.onTurnResolutionComplete?.(data);
            } catch (e) {
                console.error('Failed to release the turn story display barrier:', e);
            }
        });

        socket.on('storyImageUpdate', async (data) => {
            try {
                if (!data) return;
                if (!isCurrentMatchEvent(data)) return;
                if (data.status === 'failed') {
                    // A bridge observation deadline is not the artifact's
                    // durable lifecycle state. Ignore legacy timeout signals
                    // so a worker's later authoritative completion can land.
                    if (data.failureCode === 'generation_timeout') return;
                    StoryFeed.onStoryImageUpdate(data);
                    console.warn('Story image generation failed', {
                        turn: data.turn,
                        artifactId: data.artifactId || null,
                    });
                    return;
                }
                if (!data.imageUrl || data.status !== 'complete') return;
                debugLog('Story image update received for turn', data.turn);
                MoonfallStorybookContext?.markMediaReady?.(data);
                const displayed = await StoryFeed.onStoryImageUpdate(data);
                if (displayed === true && data.tutorialFinale === true) {
                    if (!data.artifactId) return;
                    await new Promise((resolve) => setTimeout(resolve, TUTORIAL_FINALE_VIEW_MS));
                    if (!isCurrentMatchEvent(data)) return;
                    socket.emit('confirmTutorialMangaDisplayed', await withRequiredAuthPayload({
                        roomId: data.roomId,
                        matchId: data.matchId,
                        artifactId: data.artifactId,
                    }));
                }
            } catch (e) {
                console.error('Failed to render story image update:', e);
            }
        });

        socket.on('tutorialRecordedPhaseComplete', (data) => {
            if (!data?.roomId || data.roomId !== GameState?.get?.()?.roomId) return;
            Tutorial?.rememberCompleted?.();
            if (data.resumeToken && MoonfallGuest?.isActive?.()) {
                MoonfallGuest.completeWithResumeToken?.(data.resumeToken);
                return;
            }
            Tutorial?.completeRecordedPhase?.();
        });

        /* The server's per-turn safety net fired. The authoritative state
           update that accompanies it already flipped the turn (and ActionIntent
           clears itself on the new turn number); this only tells the players
           WHY, so an auto-ended turn never reads as the game misbehaving. */
        socket.on('turnAutoEnded', (data) => {
            if (!document.body?.classList?.contains('game-active')) return;
            if (!isCurrentMatchEvent(data) || !data?.playerId) return;
            const mine = GameState.get()?.player?.id === data.playerId;
            /* One timed-out turn left means the next one loses the match, so
               the toast has to say so — the calm "your turn ended" copy reads
               as a non-event, which is exactly the wrong signal at that point.
               It is a backstop, not the warning: the player it concerns was
               away, so the durable telling is TurnTimeoutFeedback's storyboard
               beat and the countdown's own last-chance state. */
            const lastChance = data.timeoutStrikesRemaining === 1;
            let message;
            if (mine) {
                message = lastChance
                    ? translate('game.turnTimerExpiredYouLastChance', 'Time is up — your turn ended. One more and you forfeit.')
                    : translate('game.turnTimerExpiredYou', 'Time is up — your turn ended.');
            } else {
                message = lastChance
                    ? translate('game.turnTimerExpiredOpponentLastChance', "Time is up — your opponent's turn ended. One more and they forfeit.")
                    : translate('game.turnTimerExpiredOpponent', "Time is up — your opponent's turn ended.");
            }
            showGameNotification(message, 3000, true);
        });

        /* A pending intent the server cancelled on the player's behalf (only
           the turn deadline does this today). Board.renderGameState's turn-number
           sync also clears the panel, but that depends on the accompanying state
           packet landing first; closing it here means the player never sees a
           choice the server has already thrown away. */
        socket.on('actionIntentCancelled', (data) => {
            if (!isCurrentMatchEvent(data)) return;
            try {
                // Record it BEFORE clearing. The cancel notice and the next
                // game-state broadcast are separate packets, and the broadcast
                // may still carry the pending projection — without this, a
                // timer-cancelled dialog is resurrected for a turn that has
                // already ended.
                ActionIntent?.markAnswered?.(data?.requestId);
                ActionIntent?.clear?.('server-cancelled');
            } catch (e) {
                console.error('Failed to clear a server-cancelled action intent:', e);
            }
        });

        socket.on('turnDrawOverflow', (data) => {
            if (!document.body?.classList?.contains('game-active')) return;
            if (!isCurrentMatchEvent(data) || !data?.playerId) return;
            DeckoutFeedback?.handleOverflow?.(data, GameState.get());
        });

        // Game over event
        socket.on('gameOver', (data) => {
            if (!document.body?.classList?.contains('game-active')) return;
            if (!isCurrentMatchEvent(data)) return;
            const current = GameState.get();
            const validWinnerIds = [current?.player?.id, current?.opponent?.id].filter(Boolean);
            if (!data?.winnerId || !validWinnerIds.includes(data.winnerId)) {
                console.error('[SocketHandler] Ignoring gameOver with an unknown winnerId.');
                return;
            }
            clearGameNotifications();
            ReconnectStatus?.clear?.();
            GameResume?.clear?.();
            resetThinkingIndicator();
            DeckoutFeedback?.reset?.();
            TurnTimeoutFeedback?.reset?.();
            debugLog('Game over received:', data);

            const localPlayerId = current?.player?.id;
            const isWinner = Boolean(localPlayerId && data?.winnerId === localPlayerId);
            GameState.update({
                state: 'ended',
                winner: data.winnerId,
                endReason: data.reason || null,
            });

            if (SpellAnnounce) {
                SpellAnnounce.dismiss();
            }

            if (data && data.storybookShortCode && MoonfallStorybookContext) {
                MoonfallStorybookContext.setShortCode(data.storybookShortCode, data);
            }
            /* The duel spent Moonlight on every AI turn. Said now, while the
               result is up and the lobby's chip is hidden, the chip re-reads
               out of sight and the lobby comes back with the new figure
               already on it, instead of the pre-duel one (kept until a reload
               before) or a figure changing in front of the player. */
            announceMoonlightMoved();
            if (GameEnd) {
                GameEnd.showGameOverScreen(isWinner, {
                    canRematch: data?.canRematch === true,
                });
            } else {
                console.error('GameEnd module not found');
                alert(isWinner
                    ? translate('gameEnd.victory', 'Victory!')
                    : translate('gameEnd.defeat', 'Defeat'));
            }
        });

        // Action events
        socket.on('actionComplete', (data) => {
            debugLog('Action complete notification received');
            if (pendingPlayAction) {
                // A mismatched completion belongs to an older request. It must
                // not unlock, resolve, or advance tutorial for this owner.
                if (!pendingPlayAction.handleActionComplete?.(data)) return;
            } else if (pendingEndTurn) {
                // A delayed card completion cannot release an End Turn owner.
                return;
            } else if (pendingLegacyGameplayOwner) {
                if (!pendingLegacyGameplayOwner.handleActionComplete?.(data)) return;
            } else if (typeof data?.clientActionId === 'string') {
                // A correlated completion without its owner is stale.
                return;
            } else {
                GameState.setActionInProgress(false);
            }

            // Notify tutorial system of the action
            if (Tutorial?.isActive?.()) {
                const action = data?.action || 'summonCharacter';
                Tutorial.onGameEvent(action, data);
            }
        });

        // Undo events
        socket.on('undoComplete', (data) => {
            debugLog('Undo complete notification received');
            const owner = pendingLegacyGameplayOwner;
            if (!isCurrentLegacyGameplayOwner(owner) || owner.action !== 'undoAction') return;
            owner.handleUndoComplete?.(data);
        });
    }

    // Setup heartbeat mechanism to detect connection problems early
    function startHeartbeat() {
        // Clear any existing heartbeat
        if (heartbeatInterval) {
            clearInterval(heartbeatInterval);
            missedHeartbeats = 0;
        }

        heartbeatInterval = setInterval(() => {
            // Only send heartbeats if connected
            if (!socket.connected) return;

            const shouldSendHeartbeat = isTabVisible || missedHeartbeats % 2 === 0;
            if (!shouldSendHeartbeat) return;

            socket.emit('heartbeat', null, () => {
                // Reset missed heartbeats on successful response
                missedHeartbeats = 0;
            });

            // Increment missed heartbeats
            missedHeartbeats++;

            // If we've missed too many heartbeats, just notify; do not auto-reconnect
            if (missedHeartbeats >= 3) {
                console.warn(`Missed ${missedHeartbeats} heartbeats. Connection appears unhealthy.`);
                const notification = document.createElement('div');
                notification.className = 'game-notification';
                notification.textContent = translate('common.connectionIssues', 'Connection issues detected.');
                document.body.appendChild(notification);
                setTimeout(() => {
                    notification.classList.add('fade-out');
                    setTimeout(() => notification.remove(), 500);
                }, 3000);
                missedHeartbeats = 0;
            }
        }, isTabVisible ? 15000 : 30000); // Send heartbeats more frequently when tab is visible
    }

    function stopHeartbeat() {
        if (heartbeatInterval) {
            clearInterval(heartbeatInterval);
            heartbeatInterval = null;
        }
    }

    // Public API
    return {
        resetMatchTracking: resetSocketMatchTracking,
        /** Pin the next connection to a room's worker (clustered deployments). */
        setRoomAffinity,
        /** Preserve an acknowledged private match scope before presentation runs. */
        pinAcknowledgedStart,
        /**
         * Initialize the socket handler
         */
        init: function () {
            if (initialized) return;
            initialized = true;

            setupEventListeners();
            __bindUserActivityListeners();

            // Check URL for room parameter and join automatically if present
            const urlParams = new URLSearchParams(window.location.search);
            const roomId = urlParams.get('room');

            if (roomId) {
                pendingAutoJoinRoomId = roomId;
                debugLog('[SocketHandler] Auto-join detected for room:', roomId);
                document.getElementById('room-code-input').value = roomId;

                const consumeRoomInvite = () => {
                    try {
                        if (window.history && typeof window.history.replaceState === 'function') {
                            const nextUrl = new URL(window.location.href);
                            nextUrl.searchParams.delete('room');
                            const nextSearch = nextUrl.searchParams.toString();
                            window.history.replaceState(
                                {},
                                '',
                                nextUrl.pathname + (nextSearch ? `?${nextSearch}` : '') + nextUrl.hash
                            );
                        }
                    } catch (_) { /* no-op */ }
                    pendingAutoJoinRoomId = null;
                };

                const showStarterRewardIfLobby = () => {
                    const consumeLobbyRewards = Referrals?.consumeLobbyRewards
                        || StarterReward?.consumeAndShow;
                    if (!consumeLobbyRewards) return;
                    Promise.resolve(consumeLobbyRewards()).catch((err) => {
                        console.warn('[SocketHandler] Failed to show lobby rewards after invite:', err);
                    });
                };

                // Wait for DeckSelector to be available, then open it
                const waitForDeckSelector = setInterval(() => {
                    if (DeckSelector) {
                        clearInterval(waitForDeckSelector);
                        debugLog('[SocketHandler] Opening DeckSelector for auto-join');

                        const beginAutoJoin = async () => {
                            let info;
                            try {
                                info = await this.getRoomInfo(roomId);
                                if (info?.exists !== true) {
                                    const lookupError = new Error('Room lookup failed');
                                    lookupError.code = info?.code || 'room_lookup_failed';
                                    throw lookupError;
                                }
                            } catch (err) {
                                console.warn('[SocketHandler] Failed to get room info for auto-join:', err);
                                const failureMessage = roomInviteLookupFailureMessage(err);
                                if (failureMessage) alert(failureMessage);
                                return;
                            }
                            if (Number(info.playerCount) >= 2) {
                                alert(translate('lobby.roomFull', 'This room is already full.'));
                                return;
                            }

                            const excludedCharacter = info?.hostCharacter || null;
                            debugLog('[SocketHandler] Auto-join host character:', excludedCharacter);
                            consumeRoomInvite();
                            try {
                                const choice = await DeckSelector.choose({
                                    excludedCharacter,
                                    mangaStatus: await MoonfallCloud?.users?.getMangaGameStatus?.()
                                        .catch(() => undefined),
                                    returnFocusTo: document.getElementById('join-room-btn'),
                                });
                                if (choice?.kind !== 'selected') {
                                    showStarterRewardIfLobby();
                                    return;
                                }
                                debugLog('[SocketHandler] Deck selected for auto-join:', choice.deck);
                                const runGameAdmission = Lobby?.runGameAdmission;
                                if (typeof runGameAdmission !== 'function') {
                                    throw new Error('Lobby game-admission coordinator is unavailable.');
                                }
                                const result = await runGameAdmission(
                                    () => this.joinRoom(roomId, choice.deck, {
                                        includeManga: choice.includeManga,
                                    })
                                );
                                const indeterminate = result?.ok === false && [
                                    'timeout',
                                    'no_response',
                                    'late_timeout',
                                    'transition_busy',
                                ].includes(result.code);
                                if (result?.ok === false && !indeterminate) {
                                    showStarterRewardIfLobby();
                                }
                            } catch (err) {
                                console.error('[SocketHandler] Auto-join flow failed:', err);
                                showStarterRewardIfLobby();
                            }
                        };

                        beginAutoJoin();
                    }
                }, 100); // Check every 100ms

                // Timeout after 5 seconds
                setTimeout(() => {
                    clearInterval(waitForDeckSelector);
                    if (!DeckSelector) {
                        console.error('[SocketHandler] DeckSelector not available after 5s, joining without deck');
                        alert(translate('deckSelector.unavailable', 'Deck selection is unavailable. Please reload the page and try again.'));
                    }
                }, 5000);
            }

            // Add CSS for reconnection state
            if (!document.getElementById('socket-handler-runtime-styles')) {
                const style = document.createElement('style');
                style.id = 'socket-handler-runtime-styles';
                style.textContent = `
                .reconnecting::after {
                    content: 'Reconnecting...';
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    padding: ${uiCssPx(10)};
                    background-color: rgba(255, 0, 0, 0.7);
                    color: white;
                    text-align: center;
                    z-index: 9999;
                    animation: pulse 1s infinite alternate;
                }
                @keyframes pulse {
                    from { opacity: 0.7; }
                    to { opacity: 1; }
                }

                .game-notification {
                    max-width: 80%;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .game-notification.game-notification--wrap {
                    white-space: normal;
                    overflow: visible;
                    text-overflow: clip;
                }
            `;
                document.head.appendChild(style);
            }

            // Heartbeat starts on connect; no auto start here. Page exit is a
            // recoverable suspension on mobile, not an explicit match leave.
            const onPageExit = () => {
                try {
                    clearGameResumeRetry();
                    GameResume?.suspend?.();
                    resetEnemyReplayPresentation();
                } catch (_) { }
                try {
                    GameState?.forcePersist?.();
                } catch (_) { }
                try {
                    // A failed lookup remains retryable across refresh. Validated
                    // invitations are consumed before selection and may be cleared
                    // here only as a fallback when History API replacement failed.
                    if (!pendingAutoJoinRoomId && window.history && typeof window.history.replaceState === 'function') {
                        const newUrl = window.location.pathname + (window.location.hash || '');
                        window.history.replaceState({}, '', newUrl);
                    }
                } catch (_) { }
            };
            const onPageShow = () => {
                isTabVisible = document.visibilityState !== 'hidden';
                resumeGameFromLifecycle({ wake: true });
            };
            try {
                // pagehide/pageshow covers iOS bfcache and app switching;
                // beforeunload persists the same minimal recovery hint.
                window.addEventListener('beforeunload', onPageExit, { capture: true });
                window.addEventListener('pagehide', onPageExit, { capture: true });
                window.addEventListener('pageshow', onPageShow, { capture: true });
            } catch (_) { /* jsdom/old browsers may not support */ }
            if (!pendingAutoJoinRoomId) resumeGameFromLifecycle();
        },

        /**
         * Undo the last action (single-level)
         */
        undoAction: function () {
            const state = GameState.get();
            syncPendingGameplayOwnersToCurrentContext(state);
            if (isGameplayBusy(state)) {
                debugLog('Undo blocked while another action is in progress');
                return false;
            }
            const owner = createLegacyGameplayOwner('undoAction', state);
            if (!acquireLegacyGameplayOwner(owner)) return false;
            armCorrelatedLegacyGameplayOwner(owner, {
                onSettled: ({ error, reason }) => {
                    if (!error || reason === 'retired') return;
                    if (reason === 'server-error') alert(socketErrorMessage(error));
                    else showGameNotification(translate('common.actionFailed', 'Action failed.'), 3000, true);
                },
            });
            ensureConnected()
                .then(async () => {
                    if (!(await awaitLegacyEmitWindow(owner))) {
                        owner.retire();
                        return;
                    }
                    const payload = await withRequiredAuthPayload({
                        roomId: owner.roomId,
                        ...(owner.matchId ? { matchId: owner.matchId } : {}),
                        ...(owner.expectedTurn !== null ? { expectedTurn: owner.expectedTurn } : {}),
                        clientActionId: owner.clientActionId,
                    });
                    if (!(await awaitLegacyEmitWindow(owner))) {
                        owner.retire();
                        return;
                    }
                    socket.emit('undoAction', payload);
                })
                .catch((err) => {
                    if (!releaseLegacyGameplayOwner(owner)) return;
                    alert(connectionFailureMessage(err));
                });
        },

        /**
         * Backwards-compatible alias used by UI
         */
        undoLastAction: function () {
            return this.undoAction();
        },





        /**
         * Create a new game room
         */
        createRoom: async function (deck = null, options = {}) {
            return startThroughServerUpdate(async () => {
                debugLog('[SocketHandler] createRoom called with deck:', deck);
                const admission = beginLobbyRoomAdmission('created');
                // Set just before the start is handed to the socket (failedLobbyStart).
                let sent = false;
                try {
                    const cardbackClaim = warmStartCardback();
                    const prepared = await runLobbyStartPreflight(async () => {
                        await ensureConnected();
                        return {
                            authToken: await getRequiredAuthToken(),
                            ...cardbackClaim,
                            language: getCurrentLanguage(),
                        };
                    });
                    const payload = deck ? { deck, ...prepared } : { ...prepared };
                    if (!lobbyRoomAdmissionIsCurrent(admission)) {
                        return surfaceIndeterminateStart({ ok: false, code: 'room_start_superseded' });
                    }
                    if (typeof options.includeManga === 'boolean') payload.includeManga = options.includeManga;
                    if (typeof options.lucid === 'boolean') payload.lucid = options.lucid;
                    clearEndedGuestLatchForAuthenticatedStart();
                    debugLog('[SocketHandler] Emitting createRoom with payload:', redactAuthToken(payload));
                    sent = true;
                    const result = settleLobbyRoomAdmission(
                        admission,
                        await emitWithAck('createRoom', payload, LOBBY_START_ACK_TIMEOUT_MS),
                    );
                    return surfaceIndeterminateStart(result);
                } catch (err) {
                    cancelLobbyRoomAdmission(admission);
                    return failedLobbyStart(err, { sent });
                }
            });
        },

        /**
         * Join an existing game room
         * @param {string} roomId - Room ID to join
         */
        joinRoom: async function (roomId, deck = null, options = {}) {
            return startThroughServerUpdate(async () => {
                if (!roomId) {
                    alert(translate('lobby.validRoomCode', 'Please enter a valid room code.'));
                    return { ok: false, code: 'invalid_room' };
                }
                // Pin before connecting: routing is decided when the connection is
                // established, so setting this afterwards is too late.
                const tentativeRoomId = setRoomAffinity(roomId);
                const admission = beginLobbyRoomAdmission('joined', tentativeRoomId);
                debugLog('[SocketHandler] joinRoom called with roomId:', roomId, 'deck:', deck);
                // Set just before a join is handed to the socket (failedLobbyStart).
                let sent = false;
                try {
                    const cardbackClaim = warmStartCardback();
                    const prepared = await runLobbyStartPreflight(async () => {
                        await ensureConnected();
                        return {
                            authToken: await getRequiredAuthToken(),
                            ...cardbackClaim,
                            language: getCurrentLanguage(),
                        };
                    });
                    const payload = deck
                        ? { roomId, deck, ...prepared }
                        : { roomId, ...prepared };
                    if (!lobbyRoomAdmissionIsCurrent(admission)) {
                        clearTentativeRoomAffinity(tentativeRoomId);
                        return surfaceIndeterminateStart({ ok: false, code: 'room_start_superseded' });
                    }
                    if (typeof options.includeManga === 'boolean') payload.includeManga = options.includeManga;
                    if (typeof options.lucid === 'boolean') payload.lucid = options.lucid;
                    clearEndedGuestLatchForAuthenticatedStart();
                    debugLog('[SocketHandler] Emitting joinRoom with payload:', redactAuthToken(payload));
                    sent = true;
                    let ack = await emitWithAck(
                        'joinRoom',
                        payload,
                        LOBBY_START_ACK_TIMEOUT_MS,
                        { captureLateAck: true },
                    );
                    if (isWrongWorker(ack) && lobbyRoomAdmissionIsCurrent(admission)) {
                        // This connection landed on a worker that does not own the
                        // room; reconnect with the room pinned and join once more.
                        // That worker started nothing, so until the second join is
                        // sent, nothing has been started anywhere.
                        sent = false;
                        await rerouteToRoom(roomId);
                        if (!lobbyRoomAdmissionIsCurrent(admission)) {
                            clearTentativeRoomAffinity(tentativeRoomId);
                            return surfaceIndeterminateStart({ ok: false, code: 'room_start_superseded' });
                        }
                        sent = true;
                        ack = await emitWithAck(
                            'joinRoom',
                            payload,
                            LOBBY_START_ACK_TIMEOUT_MS,
                            { captureLateAck: true },
                        );
                    }
                    const result = settleJoinRoomResult(ack, tentativeRoomId, admission);
                    return surfaceIndeterminateStart(result);
                } catch (err) {
                    cancelLobbyRoomAdmission(admission);
                    clearTentativeRoomAffinity(tentativeRoomId);
                    return failedLobbyStart(err, { sent });
                }
            });
        },

        /* The waiting room's Discord line asks the server to post this room
           to #looking-for-players; the server takes the post down when the
           room fills or closes. Answers { ok } or { ok: false, code }. */
        postRoomToDiscord: async function (roomId) {
            if (!roomId) return { ok: false, code: 'room_post_invalid' };
            try {
                await ensureConnected();
                const response = await emitWithAck('postRoomToDiscord', { roomId }, 8_000);
                return response?.action === 'postRoomToDiscord'
                    ? response
                    : { ok: false, code: response?.code || 'no_response' };
            } catch (err) {
                console.error('[SocketHandler] postRoomToDiscord failed:', err);
                return { ok: false, code: 'connection_failed' };
            }
        },

        // Dev-only manual match end. Ends the current in-progress match so the
        // end-of-match / rematch screen can be reached without playing to the
        // end. `outcome` is `'concede'` (the caller loses — the default) or
        // `'win'` (the caller wins). The buttons that call this are only shown
        // when the server enabled CLIENT_DEBUG_MODE; the server re-checks
        // DEBUG_MODE and exact-matches the outcome too.
        debugEndMatch: async function (outcome = 'concede') {
            if (window.CLIENT_DEBUG_MODE !== true) {
                return { ok: false, code: 'debug_disabled' };
            }
            if (outcome !== 'concede' && outcome !== 'win') {
                return { ok: false, code: 'invalid_outcome' };
            }
            const state = GameState?.get?.() || {};
            const roomId = state.roomId || null;
            const matchId = state.matchId || null;
            if (!roomId || !matchId) {
                return { ok: false, code: 'no_active_match' };
            }
            try {
                await ensureConnected();
                const clientActionId = createClientActionId();
                const authToken = await getRequiredAuthToken().catch(() => null);
                const payload = { roomId, matchId, clientActionId, outcome };
                if (authToken) payload.authToken = authToken;
                const response = await emitWithAck('debugEndMatch', payload, 15_000);
                if (
                    response?.action !== 'debugEndMatch'
                    || response?.clientActionId !== clientActionId
                ) {
                    return { ok: false, code: 'stale_response' };
                }
                return response;
            } catch (err) {
                console.error('[SocketHandler] debugEndMatch failed:', err);
                return { ok: false, code: 'connection_failed' };
            }
        },

        /* Dev-only: put the opponent's hand face-up (the storyteller-reveal
           state) so a revealed card's rendering and hover can be exercised by
           hand. Convenience, not authority: the server re-checks DEBUG_MODE. */
        debugRevealHand: async function () {
            if (window.CLIENT_DEBUG_MODE !== true) {
                return { ok: false, code: 'debug_disabled' };
            }
            const state = GameState?.get?.() || {};
            const roomId = state.roomId || null;
            const matchId = state.matchId || null;
            if (!roomId || !matchId) {
                return { ok: false, code: 'no_active_match' };
            }
            try {
                await ensureConnected();
                const clientActionId = createClientActionId();
                const authToken = await getRequiredAuthToken().catch(() => null);
                const payload = { roomId, matchId, clientActionId };
                if (authToken) payload.authToken = authToken;
                const response = await emitWithAck('debugRevealHand', payload, 15_000);
                if (
                    response?.action !== 'debugRevealHand'
                    || response?.clientActionId !== clientActionId
                ) {
                    return { ok: false, code: 'stale_response' };
                }
                return response;
            } catch (err) {
                console.error('[SocketHandler] debugRevealHand failed:', err);
                return { ok: false, code: 'connection_failed' };
            }
        },

        /* Dev-only: the scene a revealed enemy card is read in, in one press —
           the AI's own Characters stood on its board and one of its hand cards
           face-up (game/utils/debugBoardReveal.js). Convenience, not
           authority: the server re-checks DEBUG_MODE, the turn and the seat. */
        debugBoardReveal: async function () {
            if (window.CLIENT_DEBUG_MODE !== true) {
                return { ok: false, code: 'debug_disabled' };
            }
            const state = GameState?.get?.() || {};
            const roomId = state.roomId || null;
            const matchId = state.matchId || null;
            if (!roomId || !matchId) {
                return { ok: false, code: 'no_active_match' };
            }
            try {
                await ensureConnected();
                const clientActionId = createClientActionId();
                const authToken = await getRequiredAuthToken().catch(() => null);
                const payload = { roomId, matchId, clientActionId };
                if (authToken) payload.authToken = authToken;
                const response = await emitWithAck('debugBoardReveal', payload, 15_000);
                if (
                    response?.action !== 'debugBoardReveal'
                    || response?.clientActionId !== clientActionId
                ) {
                    return { ok: false, code: 'stale_response' };
                }
                return response;
            } catch (err) {
                console.error('[SocketHandler] debugBoardReveal failed:', err);
                return { ok: false, code: 'connection_failed' };
            }
        },

        requestRematch: async function (deck, options = {}) {
            const state = GameState?.get?.() || {};
            const roomId = state.roomId || null;
            const matchId = state.matchId || null;
            if (!roomId || !matchId || state.state !== 'ended') {
                return { ok: false, code: 'rematch_unavailable' };
            }
            const deckReference = deck?.id
                ? { id: String(deck.id) }
                : (deck?.character ? { character: String(deck.character) } : null);
            if (!deckReference) return { ok: false, code: 'invalid_selection' };

            try {
                await ensureConnected();
                const clientActionId = createClientActionId();
                const [authToken, hasMangaPass] = await Promise.all([
                    getRequiredAuthToken(),
                    Promise.resolve(MoonfallCloud?.users?.isPaid?.()).catch(() => false),
                ]);
                if (!isCurrentActionContext(roomId, matchId)) {
                    return { ok: false, code: 'rematch_stale' };
                }
                const payload = {
                    roomId,
                    matchId,
                    clientActionId,
                    deck: deckReference,
                    hasMangaPass: hasMangaPass === true,
                    ...(typeof options.includeManga === 'boolean'
                        ? { includeManga: options.includeManga }
                        : {}),
                    ...(typeof options.lucid === 'boolean'
                        ? { lucid: options.lucid }
                        : {}),
                    authToken,
                };
                rememberRematchRecovery({
                    previousRoomId: roomId,
                    previousMatchId: matchId,
                });
                armRematchRecoveryWatchdog({ resetBackoff: true });
                const response = await emitWithAck('requestRematch', payload, 30_000);
                if (
                    response?.action !== 'requestRematch'
                    || response?.clientActionId !== clientActionId
                ) return { ok: false, code: 'stale_response' };
                if (response?.ok === true && response?.status === 'starting') {
                    armRematchRecoveryWatchdog({ resetBackoff: true });
                } else if (
                    response?.ok !== true
                    && response?.code !== 'timeout'
                    && response?.code !== 'no_response'
                    && canClearRecoveryForOldScope(roomId, matchId)
                ) {
                    clearRematchRecovery();
                }
                return response;
            } catch (error) {
                return {
                    ok: false,
                    code: 'connection_failed',
                    message: connectionFailureMessage(error),
                };
            }
        },

        cancelRematch: async function () {
            const state = GameState?.get?.() || {};
            const roomId = state.roomId || null;
            const matchId = state.matchId || null;
            if (!roomId || !matchId || state.state !== 'ended') {
                return { ok: false, code: 'rematch_unavailable' };
            }
            try {
                const authToken = await getRequiredAuthToken();
                if (!isCurrentActionContext(roomId, matchId)) {
                    return { ok: false, code: 'rematch_stale' };
                }
                const response = await emitWithAck(
                    'cancelRematch',
                    { roomId, matchId, authToken },
                    3_000,
                );
                if (
                    (response?.ok === true || response?.code === 'rematch_unavailable')
                    && canClearRecoveryForOldScope(roomId, matchId)
                ) {
                    clearRematchRecovery();
                }
                return response;
            } catch (error) {
                return { ok: false, code: 'connection_failed' };
            }
        },



        /**
         * Leave the current room
         */
        leaveRoom: function (roomId) {
            cancelLobbyRoomAdmission();
            if (!roomId) {
                console.error('leaveRoom(roomId) requires a valid roomId. Make sure to call this before GameState.reset().');
                // Non-blocking toast for UX feedback
                try {
                    const notification = document.createElement('div');
                    notification.className = 'game-notification';
                    notification.textContent = translate('common.noRoomToLeave', 'No room to leave (missing roomId).');
                    document.body.appendChild(notification);
                    setTimeout(() => {
                        notification.classList.add('fade-out');
                        setTimeout(() => {
                            if (notification.parentNode) {
                                notification.parentNode.removeChild(notification);
                            }
                        }, 500);
                    }, 2000);
                } catch (_) { /* DOM may be unavailable in tests */ }
                // Missing roomId often means the state was already reset. Do
                // not let that stale call unlock a newly-entered match.
                try {
                    if (!hasPendingGameplayOwner()) {
                        GameState.setActionInProgress(false);
                    }
                } catch (_) { }
                return Promise.resolve({ ok: false, code: 'leave_invalid' });
            }
            clearRematchRecovery();
            const state = GameState?.get?.() || {};
            const currentRoomId = state.roomId
                || activeAuthoritativeScope?.roomId
                || null;
            if (!currentRoomId || currentRoomId !== roomId) {
                debugLog('[SocketHandler] Ignoring stale leaveRoom request', {
                    requestedRoomId: roomId,
                    currentRoomId,
                });
                return Promise.resolve({ ok: false, code: 'leave_stale' });
            }
            const matchId = state.matchId || activeAuthoritativeScope?.matchId || null;
            const playerId = state.player?.id
                || state.playerId
                || activeAuthoritativeScope?.playerId
                || null;
            const leaveScope = { roomId, matchId, playerId };
            const requestId = createClientActionId();
            const leaveRequest = Object.freeze({ ...leaveScope, requestId });
            explicitLeaveInFlight = leaveRequest;
            clearGameResumeRetry();
            GameResume?.cancelAttempt?.();
            GameResume?.clearIfScope?.(leaveScope);
            ReconnectStatus?.clear?.();
            document.body?.classList?.remove('game-resuming', 'game-suspended');
            clearConnectionNotice();
            retireAllPendingGameplayOwners();
            // Only the room being left owns its visible lock. A delayed leave
            // for an older room must not unlock the current match.
            try {
                if (GameState?.get?.()?.roomId === roomId && !hasPendingGameplayOwner()) {
                    GameState.setActionInProgress(false);
                }
            } catch (_) { }
            resetThinkingIndicator();
            if (activeAuthoritativeScope?.roomId === roomId) {
                activeAuthoritativeScope = null;
            }
            // A validated room without a stable seat id can only arise from a
            // malformed/partial authoritative start. There is no credentialed
            // seat target the server can safely release, so fail closed by
            // dropping this game transport. Normal waiting rooms carry the
            // server-issued player id and continue through explicit leave.
            if (!playerId) {
                if (explicitLeaveInFlight === leaveRequest) {
                    explicitLeaveInFlight = null;
                }
                GameResume?.clear?.();
                setRoomAffinity(null);
                try {
                    if (socket.connected) socket.disconnect();
                } catch (_) { }
                return Promise.resolve({
                    ok: true,
                    code: 'leave_unscoped_disconnect',
                    roomId,
                });
            }
            setRoomAffinity(roomId);
            const leavePromise = (async () => {
                try {
                    await ensureConnected();
                    const payload = await withRequiredAuthPayload({
                        roomId,
                        ...(matchId ? { matchId } : {}),
                        ...(playerId ? { playerId } : {}),
                        requestId,
                    });
                    const response = await emitWithAck('leaveRoom', payload, 5_000);
                    if (
                        response?.action !== 'leaveRoom'
                        || response?.requestId !== requestId
                    ) return { ok: false, code: 'leave_stale_response' };
                    return response;
                } catch (_) {
                    return { ok: false, code: 'connection_failed' };
                } finally {
                    if (explicitLeaveInFlight === leaveRequest) {
                        explicitLeaveInFlight = null;
                    }
                    GameResume?.clearIfScope?.(leaveScope);
                    if (!activeAuthoritativeScope && !explicitLeaveInFlight) {
                        setRoomAffinity(null);
                    }
                }
            })();
            debugLog('Leaving room authoritatively:', roomId);
            return leavePromise;
        },

        leaveCurrentRoom: function () {
            const roomId = GameState?.get?.()?.roomId
                || activeAuthoritativeScope?.roomId
                || null;
            if (!roomId) return false;
            this.leaveRoom(roomId);
            return true;
        },

        cancelLobbyRoomAdmission,

        /**
         * Start the game (host only)
         */


        /**
         * Summon a hand Character. Returns a promise of the SETTLEMENT —
         * `{ ok: true }` once the server completed the summon, `{ ok: false,
         * reason }` when it was refused here, by the server, or never sent.
         * The drag that dispatched it holds the card over its landing slot
         * until then and flies it home on a refusal. Presentation only: the
         * server's answer is the answer.
         */
        summonCharacter: function (cardInstanceId) {
            let settleSummon = null;
            const settled = new Promise((resolve) => { settleSummon = resolve; });
            const refuse = (reason) => {
                settleSummon({ ok: false, reason });
                return settled;
            };
            const state = GameState.get();
            syncPendingGameplayOwnersToCurrentContext(state);

            // --- Start New Robust Logging ---
            if (window.CLIENT_DEBUG_MODE) {
                debugLog('[SocketHandler.summonCharacter] Received call with cardInstanceId:', cardInstanceId);
                debugLog('[SocketHandler.summonCharacter] Raw state object from GameState.get():', state);
                if (state) {
                    debugLog('[SocketHandler.summonCharacter] state.player from GameState.get():', state.player);
                    if (state.player && state.player.hand) {
                        debugLog('[SocketHandler.summonCharacter] state.player.hand (first 3 cards):', state.player.hand.slice(0, 3));
                    } else if (state.player) {
                        console.warn('[SocketHandler.summonCharacter] state.player exists, but state.player.hand is missing.');
                    } else {
                        console.warn('[SocketHandler.summonCharacter] state.player is missing from state object.');
                    }
                } else {
                    console.error('[SocketHandler.summonCharacter] CRITICAL: GameState.get() returned null or undefined itself!');
                    // Early exit if state is fundamentally broken
                    GameState.setActionInProgress(false); // Try to reset action flag
                    return refuse('no-state');
                }
            } else if (!state) {
                // Minimal check if debug mode is off
                console.error('[SocketHandler.summonCharacter] CRITICAL: GameState.get() returned null or undefined itself!');
                GameState.setActionInProgress(false);
                return refuse('no-state');
            }
            // --- End New Robust Logging ---

            if (!state.isYourTurn) {
                console.warn('Not your turn, summonCharacter blocked');
                showGameNotification(translate('game.notYourTurn', 'Not your turn'));
                Effects?.invalidAction?.({
                    cardElement: document.querySelector(`.card[data-instance-id="${cardInstanceId}"]`),
                    reason: 'turn'
                });
                return refuse('turn');
            }
            if (isGameplayBusy(state)) {
                console.warn('Action in progress, summonCharacter blocked');
                // Basic notification for user feedback could be added here
                return refuse('busy');
            }

            // Block during mulligan phase
            if (state.state === 'mulligan' || Mulligan?.isActive?.()) {
                console.warn('Cannot summon during mulligan phase');
                return refuse('mulligan');
            }

            // The robust logging added above replaces the direct need for this specific line here.
            // The check for state.player will still occur below.

            if (!state.player) {
                console.error('[SocketHandler.summonCharacter] Error: state.player is undefined or null.');
                return refuse('no-state');
            }

            const playerHand = state.player.hand;
            if (!playerHand || !Array.isArray(playerHand)) {
                console.error('[SocketHandler.summonCharacter] Error: state.player.hand is undefined, null, or not an array.');
                debugLog('[SocketHandler.summonCharacter] typeof state.player.hand:', typeof playerHand);
                debugLog('[SocketHandler.summonCharacter] state.player.hand value:', playerHand);
                return refuse('no-hand');
            }

            const cardToSummon = playerHand.find(card => card.instanceId === cardInstanceId);

            if (!cardToSummon) {
                console.error(`[SocketHandler.summonCharacter] Card with instanceId ${cardInstanceId} not found in hand.`);
                if (window.CLIENT_DEBUG_MODE) {
                    try {
                        debugLog('[SocketHandler.summonCharacter] Hand lookup summary:', {
                            handSize: playerHand.length,
                            requestedCardPresent: false
                        });
                    } catch (e) {
                        console.error('[SocketHandler.summonCharacter] Error while preparing lookup diagnostics:', e);
                    }
                }
                return refuse('not-in-hand');
            }

            if (cardToSummon.type !== 'Character') {
                console.error(`Summon character: Card ${cardToSummon.name} (instanceId: ${cardInstanceId}) is not a Character. Type: ${cardToSummon.type}`);
                return refuse('not-a-character');
            }

            // Mana check (client-side pre-check, server will validate fully)
            const playerMana = state.player?.mana || 0;
            if (playerMana < cardToSummon.manaCost) {
                console.warn(`Not enough mana to summon ${cardToSummon.name} (instanceId: ${cardInstanceId}). Have ${playerMana}, need ${cardToSummon.manaCost}`);
                showGameNotification(translate('game.notEnoughMana', 'Not enough mana'));
                Effects?.invalidAction?.({
                    cardElement: document.querySelector(`.card[data-instance-id="${cardInstanceId}"]`),
                    reason: 'mana'
                });
                return refuse('mana');
            }

            if (Cards && typeof Cards.queueStellarSummonPlayback === 'function') {
                const isStellar = cardToSummon.isStellar === true
                    || cardToSummon.stellar === true
                    || (typeof cardToSummon.isStellar === 'string' && cardToSummon.isStellar.toLowerCase() === 'true')
                    || (typeof cardToSummon.stellar === 'string' && cardToSummon.stellar.toLowerCase() === 'true');
                if (isStellar) {
                    const summonVideoUrl = typeof Cards.resolveStellarSummonVideoSource === 'function'
                        ? Cards.resolveStellarSummonVideoSource(cardToSummon)
                        : cardToSummon.stellarSummonVideoUrl || '';
                    if (summonVideoUrl) {
                        const summonVideoFallbackUrl = summonVideoUrl !== cardToSummon.stellarSummonVideoUrl
                            ? cardToSummon.stellarSummonVideoUrl || ''
                            : '';
                        Cards.queueStellarSummonPlayback(
                            cardInstanceId,
                            true,
                            summonVideoUrl,
                            summonVideoFallbackUrl
                        );
                    }
                }
            }

            debugLog(`✨ Emitting summonCharacter for card instanceId: ${cardInstanceId}, Name: ${cardToSummon.name}`);
            // Summoning should feel instant — apply the input lock but suppress
            // the "Processing action…" chrome (the latch survives the server's
            // actionInProgress mirror until the summon completes).
            const owner = createLegacyGameplayOwner('summonCharacter', state);
            if (!acquireLegacyGameplayOwner(owner, { silent: true })) return refuse('busy');
            armCorrelatedLegacyGameplayOwner(owner, {
                completionAction: 'summonCharacter',
                onSettled: ({ error, reason }) => {
                    settleSummon(error ? { ok: false, reason: reason || 'failed' } : { ok: true, reason: 'complete' });
                    if (!error || reason === 'retired') return;
                    if (reason === 'server-error') alert(socketErrorMessage(error));
                    else showGameNotification(translate('common.actionFailed', 'Action failed.'), 3000, true);
                },
            });
            ensureConnected()
                .then(async () => {
                    if (!(await awaitLegacyEmitWindow(owner))) {
                        owner.retire();
                        return;
                    }
                    const payload = await withRequiredAuthPayload({
                        roomId: owner.roomId,
                        ...(owner.matchId ? { matchId: owner.matchId } : {}),
                        ...(owner.expectedTurn !== null ? { expectedTurn: owner.expectedTurn } : {}),
                        clientActionId: owner.clientActionId,
                        cardInstanceId: cardInstanceId
                    });
                    if (!(await awaitLegacyEmitWindow(owner))) {
                        owner.retire();
                        return;
                    }
                    socket.emit('summonCharacter', payload);
                })
                .catch((err) => {
                    console.error('Failed to connect for summonCharacter:', err);
                    if (!releaseLegacyGameplayOwner(owner)) return;
                    settleSummon({ ok: false, reason: 'connection' });
                    alert(connectionFailureMessage(err));
                });
            return settled;
        },






        /**
         * Play an action with a card
         * @param {string} sourceCardId - ID of the card performing the action
         * @param {string} targetType - Type of target (card, player, etc.)
         * @param {string} targetId - ID of the target
         * @param {number} [targetIndex]
         * @param {{processingGraceMs?: number}} [presentation] - presentation
         *   only, never sent: defer the "Processing action…" chrome
         * @returns {Promise} A promise that resolves when the action completes or rejects on error
         */
        playAction: function (sourceCardId, targetType, targetId, targetIndex, presentation) {
            const actionTraceId = beginActionTrace(sourceCardId, {
                origin: 'socket-handler',
                targetType: targetType || null,
                targetId: targetId || null,
                targetIndex: Number.isInteger(targetIndex) ? targetIndex : null,
            });
            recordActionTrace(actionTraceId, 'play-action-called', {
                targetType: targetType || null,
                targetId: targetId || null,
                targetIndex: Number.isInteger(targetIndex) ? targetIndex : null,
            });

            return new Promise((resolve, reject) => {
                const stateAtCall = GameState.get();
                syncPendingGameplayOwnersToCurrentContext(stateAtCall);
                if (isGameplayBusy(stateAtCall)) {
                    finishActionTrace(actionTraceId, 'client-blocked', { reason: 'action-in-progress' });
                    showGameNotification(
                        resolvedText('game.processingAction', 'Processing action...'),
                        1500,
                    );
                    return reject(new Error('Action already in progress'));
                }

                const sourceCardAtCall = Array.isArray(stateAtCall.player?.hand)
                    ? stateAtCall.player.hand.find(card => card.instanceId === sourceCardId)
                    : null;
                const actionType = sourceCardAtCall?.type === 'Spell'
                    ? 'spellCast'
                    : 'cardAction';

                const owner = {
                    clientActionId: createClientActionId(),
                    roomId: stateAtCall.roomId || null,
                    matchId: stateAtCall.matchId || null,
                    expectedTurn: Number.isInteger(stateAtCall.currentTurn)
                        ? stateAtCall.currentTurn
                        : null,
                    requestAction: 'playAction',
                    completionAction: 'cardAction',
                    actionType,
                    timeoutId: null,
                    settled: false,
                    uiLocked: false,
                    settle: null,
                    retire: null,
                    handleSocketError: null,
                    handleActionComplete: null,
                };

                const isCurrentOwner = () => pendingPlayAction === owner && !owner.settled;
                owner.settle = ({ error = null, traceEvent, traceDetails = {}, recover = false }) => {
                    if (!isCurrentOwner()) return false;
                    owner.settled = true;
                    if (owner.timeoutId) clearTimeout(owner.timeoutId);
                    pendingPlayAction = null;
                    if (owner.uiLocked) releaseSharedActionLock(owner);
                    if (error) rememberPlayActionFailure(owner, error);
                    finishActionTrace(actionTraceId, traceEvent, traceDetails);
                    if (recover) requestFreshGameStateForRecovery(owner.roomId, owner.matchId);
                    if (error) reject(normalizeSocketError(error, 'Action failed'));
                    else resolve();
                    return true;
                };
                owner.retire = (error) => owner.settle({
                    error: error || new Error('The game changed before the action completed.'),
                    traceEvent: 'client-context-changed',
                    traceDetails: { roomId: owner.roomId, matchId: owner.matchId },
                });
                owner.handleSocketError = (error) => {
                    if (!isExactActionCorrelation(
                        error,
                        owner.requestAction,
                        owner.clientActionId,
                    )) return false;
                    const didSettle = owner.settle({
                        error,
                        traceEvent: 'server-error',
                        traceDetails: { message: socketErrorMessage(error) },
                        recover: true,
                    });
                    if (didSettle) surfacePlayActionFailure(error);
                    return didSettle;
                };
                owner.handleActionComplete = (data) => {
                    if (!isExactActionCorrelation(
                        data,
                        owner.completionAction,
                        owner.clientActionId,
                    )) return false;
                    return owner.settle({ traceEvent: 'action-complete' });
                };
                pendingPlayAction = owner;
                /* Presentation only, never sent: a card spent by a drag asks for
                   a grace before the processing chrome (GameState). */
                const processingGraceMs = Number(presentation && presentation.processingGraceMs);
                if (Number.isFinite(processingGraceMs) && processingGraceMs > 0) {
                    acquireSharedActionLock(owner, { graceMs: processingGraceMs });
                } else {
                    acquireSharedActionLock(owner);
                }
                owner.timeoutId = setTimeout(() => {
                    console.warn('Action timed out - resetting state');
                    owner.settle({
                        error: {
                            message: 'Action timed out',
                            action: owner.requestAction,
                            clientActionId: owner.clientActionId,
                        },
                        traceEvent: 'client-timeout',
                        traceDetails: {
                            socketConnected: socket.connected,
                            tabVisible: isTabVisible,
                        },
                        recover: true,
                    });
                }, 150000);

                const actionAck = (response) => {
                    if (!isExactActionCorrelation(
                        response,
                        owner.requestAction,
                        owner.clientActionId,
                    )) return;
                    if (response?.error) {
                        const error = {
                            message: response.error,
                            action: owner.requestAction,
                            clientActionId: owner.clientActionId,
                        };
                        const didSettle = owner.settle({
                            error,
                            traceEvent: 'server-ack-error',
                            traceDetails: { message: response.error },
                            recover: true,
                        });
                        if (didSettle) surfacePlayActionFailure(error);
                        return;
                    }
                    if (response?.ok && isCurrentOwner()) {
                        recordActionTrace(actionTraceId, 'server-ack-ok', {});
                    }
                    // Receipt is not completion. The correlated actionComplete
                    // event remains the only successful terminal signal.
                };

                ensureConnected()
                    .then(async () => {
                        if (!isCurrentOwner()) return;
                        const state = GameState.get();
                        recordActionTrace(actionTraceId, 'socket-ready', {
                            socketConnected: socket.connected,
                            tabVisible: isTabVisible,
                        });

                        if (!isCurrentActionContext(owner.roomId, owner.matchId)) {
                            owner.settle({
                                error: new Error('The game changed before the action could start.'),
                                traceEvent: 'client-context-changed',
                                traceDetails: { roomId: owner.roomId, matchId: owner.matchId },
                            });
                            return;
                        }
                        if (!state.isYourTurn) {
                            console.warn('Not your turn, action blocked');
                            showGameNotification(translate('game.notYourTurn', 'Not your turn'), 2000);
                            Effects?.invalidAction?.({ reason: 'turn' });
                            owner.settle({
                                error: new Error('Not your turn'),
                                traceEvent: 'client-blocked',
                                traceDetails: { reason: 'not-your-turn' },
                            });
                            return;
                        }
                        if (state.state === 'mulligan' || Mulligan?.isActive?.()) {
                            console.warn('Cannot play action during mulligan phase');
                            owner.settle({
                                error: new Error('Mulligan phase active'),
                                traceEvent: 'client-blocked',
                                traceDetails: { reason: 'mulligan-active' },
                            });
                            return;
                        }
                        if (!sourceCardId) {
                            owner.settle({
                                error: new Error('Invalid game state for action'),
                                traceEvent: 'client-blocked',
                                traceDetails: {
                                    reason: 'invalid-game-state',
                                },
                            });
                            return;
                        }

                        if (SoundEffects) {
                            try {
                                const sourceCardInHand = Array.isArray(state.player?.hand)
                                    ? state.player.hand.find(card => card.instanceId === sourceCardId)
                                    : null;
                                if (sourceCardInHand?.type !== 'Spell') {
                                    SoundEffects.play('play');
                                }
                            } catch (_) {
                                SoundEffects.play('play');
                            }
                        }

                        const sourceZone = state.dragSourceZone || null;
                        const payload = await withRequiredAuthPayload({
                            roomId: owner.roomId,
                            matchId: owner.matchId,
                            clientActionId: owner.clientActionId,
                            ...(owner.expectedTurn !== null
                                ? { expectedTurn: owner.expectedTurn }
                                : {}),
                            sourceCardId,
                            targetType,
                            targetId,
                            ...(Number.isInteger(targetIndex) ? { targetIndex } : {}),
                            actionType: owner.actionType,
                            sourceZone,
                        });

                        // Auth can resolve after an error, disconnect, timeout,
                        // or room transition. A settled owner must never emit.
                        if (!isCurrentOwner() || !socket.connected
                            || !isCurrentActionContext(owner.roomId, owner.matchId)) return;

                        recordActionTrace(actionTraceId, 'socket-emitted', {
                            targetType: targetType || null,
                            targetId: targetId || null,
                            targetIndex: Number.isInteger(targetIndex) ? targetIndex : null,
                            actionType: owner.actionType,
                            sourceZone,
                            socketConnected: socket.connected,
                            tabVisible: isTabVisible,
                        });
                        socket.emit('playAction', payload, actionAck);
                    })
                    .catch((error) => {
                        const didSettle = owner.settle({
                            error,
                            traceEvent: 'client-error',
                            traceDetails: { message: socketErrorMessage(error, 'Failed to connect') },
                            recover: true,
                        });
                        if (!didSettle) return;
                        console.error('Failed to connect for playAction:', error);
                        showGameNotification(connectionFailureMessage(error), 3000, true);
                    });
            });
        },

        /**
         * Request player intent choices for a high-cost dragged board action.
         * @param {string} sourceCardId
         * @param {string|null} targetType
         * @param {string} targetId
         * @param {number|undefined} targetIndex
         * @returns {Promise<void>}
         */
        requestActionIntentOptions: function (sourceCardId, targetType, targetId, targetIndex) {
            return new Promise((resolve, reject) => {
                const state = GameState.get();
                syncPendingGameplayOwnersToCurrentContext(state);
                if (!state.isYourTurn) {
                    showGameNotification(translate('game.notYourTurn', 'Not your turn'));
                    Effects?.invalidAction?.({ reason: 'turn' });
                    return reject(new Error('Not your turn'));
                }
                if (isGameplayBusy(state)) {
                    return reject(new Error('Action already in progress'));
                }
                const owner = createLegacyGameplayOwner('requestActionIntentOptions', state);
                if (!acquireLegacyGameplayOwner(owner)) {
                    reject(new Error('Action already in progress'));
                    return;
                }
                let requestPromiseSettled = false;
                armCorrelatedLegacyGameplayOwner(owner, {
                    timeoutMs: ACTION_INTENT_REQUEST_TIMEOUT_MS,
                    timeoutMessage: 'Action intent timed out',
                    onTimeout: () => {
                        if (!owner.requestId) return false;
                        void cancelPendingActionIntent(owner, owner.requestId)
                            .then(() => ActionIntent?.clear?.('timeout-cancelled'))
                            .catch((error) => {
                                if (!isCurrentLegacyGameplayOwner(owner)) return;
                                showGameNotification(
                                    socketErrorMessage(error, 'Could not cancel intent.'),
                                    3000,
                                    true,
                                );
                            });
                        return true;
                    },
                    onSettled: ({ error, reason }) => {
                        const cancelOperation = owner.cancelOperation;
                        if (cancelOperation && !cancelOperation.settled) {
                            cancelOperation.settled = true;
                            if (cancelOperation.timeoutId) clearTimeout(cancelOperation.timeoutId);
                            cancelOperation.reject(error || new Error('Action intent is no longer active.'));
                        }
                        if (!requestPromiseSettled && error) {
                            requestPromiseSettled = true;
                            reject(normalizeSocketError(error, 'Action intent failed'));
                            return;
                        }
                        /* A FAILURE AFTER THE ACKNOWLEDGEMENT. The server answers
                           the packet with the requestId as soon as it holds the
                           request, then generates the options; the dialog sits on
                           "Reading the field" until they arrive. When generation
                           fails, its catch block emits the correlated socket
                           `error` FIRST and `actionIntentError` second — and the
                           `error` settles this owner, so the `actionIntentError`
                           that follows finds nobody to route through. The
                           request promise is long resolved, so nothing reached
                           the dialog either: it kept its spinner and Minimize
                           button while the server had already released the
                           hold, until the turn timer ended the turn (staging,
                           2026-09-18, a Gemini 503 then a 55 s attempt abort).
                           Tell the dialog here, from the packet that actually
                           settled the owner. `handleIntentError` settles with
                           its own reason because it has told the dialog itself. */
                        if (error && reason === 'server-error' && owner.requestId) {
                            ActionIntent?.handleError?.(
                                socketErrorMessage(error, 'Action intent failed'),
                                owner.requestId,
                            );
                        }
                    },
                });
                owner.handleIntentError = (data) => {
                    if (!isExactActionCorrelation(data, owner.action, owner.clientActionId)
                        || data?.requestId !== owner.requestId) return false;
                    const message = socketErrorMessage(data, 'Action intent failed');
                    const handled = ActionIntent?.handleError?.(message, owner.requestId) !== false;
                    if (!handled) return false;
                    return settleLegacyGameplayOwner(owner, {
                        error: normalizeSocketError(data, 'Action intent failed'),
                        reason: 'intent-error',
                    });
                };
                ensureConnected()
                    .then(async () => {
                        if (!(await awaitLegacyEmitWindow(owner))) {
                            owner.retire();
                            return;
                        }
                        const payload = await withRequiredAuthPayload({
                            roomId: owner.roomId,
                            ...(owner.matchId ? { matchId: owner.matchId } : {}),
                            ...(owner.expectedTurn !== null ? { expectedTurn: owner.expectedTurn } : {}),
                            clientActionId: owner.clientActionId,
                            sourceCardId,
                            targetType,
                            targetId,
                            ...(Number.isInteger(targetIndex) ? { targetIndex } : {})
                        });
                        if (!(await awaitLegacyEmitWindow(owner))) {
                            owner.retire();
                            return;
                        }
                        socket.emit('requestActionIntentOptions', payload, (response) => {
                            if (!isCurrentLegacyGameplayOwner(owner)
                                || !isExactActionCorrelation(response, owner.action, owner.clientActionId)) return;
                            if (response?.error) {
                                settleLegacyGameplayOwner(owner, {
                                    error: new Error(response.error),
                                    reason: 'server-ack-error',
                                    recover: true,
                                });
                                return;
                            }
                            if (!response?.ok || typeof response?.requestId !== 'string' || !response.requestId) return;
                            owner.requestId = response.requestId;
                            requestPromiseSettled = true;
                            resolve(response || {});
                        });
                    })
                    .catch((err) => {
                        settleLegacyGameplayOwner(owner, {
                            error: err || new Error('Failed to connect'),
                            reason: 'client-error',
                        });
                    });
            });
        },

        /**
         * Submit a selected player intent option index.
         * @param {string} requestId
         * @param {number} optionIndex
         * @returns {Promise<void>}
         */
        /**
         * Adopt a pending action-intent request the SERVER still holds but this
         * client has no owner for.
         *
         * Recovery rebuilds the dialog from authoritative state, but the client
         * also tracks a per-action "gameplay owner" used to correlate the reply.
         * Without one, submitting a restored dialog is refused locally with
         * "Action intent request is no longer active" — the player sees a
         * working dialog whose buttons do nothing. Only realistic (slow) timing
         * exposed this; a fast run kept the original owner alive.
         *
         * Refuses whenever some OTHER action already owns the slot, so this can
         * never be used to jump an in-flight action.
         * @param {string} requestId
         * @returns {boolean} true when this client can now submit that request
         */
        adoptPendingActionIntent: function (requestId) {
            if (!requestId) return false;
            const state = GameState.get();
            syncPendingGameplayOwnersToCurrentContext(state);
            const existing = pendingLegacyGameplayOwner;
            if (isCurrentLegacyGameplayOwner(existing)) {
                // Already ours for this exact request — nothing to adopt.
                return existing.action === 'requestActionIntentOptions'
                    && existing.requestId === requestId;
            }
            if (hasPendingGameplayOwner()) return false;
            const owner = createLegacyGameplayOwner('requestActionIntentOptions', state, { requestId });
            owner.requestId = requestId;
            if (!acquireLegacyGameplayOwner(owner)) return false;
            armCorrelatedLegacyGameplayOwner(owner, {
                timeoutMs: ACTION_INTENT_REQUEST_TIMEOUT_MS,
                timeoutMessage: 'Action intent timed out',
            });
            return true;
        },

        submitActionIntentSelection: function (requestId, optionIndex) {
            return new Promise((resolve, reject) => {
                const state = GameState.get();
                syncPendingGameplayOwnersToCurrentContext(state);
                const requestOwner = pendingLegacyGameplayOwner;
                if (!isCurrentLegacyGameplayOwner(requestOwner)
                    || requestOwner.action !== 'requestActionIntentOptions'
                    || requestOwner.requestId !== requestId) {
                    reject(new Error(hasPendingGameplayOwner()
                        ? 'Action already in progress'
                        : 'Action intent request is no longer active'));
                    return;
                }
                const owner = createLegacyGameplayOwner('submitActionIntentSelection', state, {
                    completionAction: 'cardAction',
                    requestId,
                });
                if (!transferLegacyGameplayOwner(requestOwner, owner)) {
                    reject(new Error('Action already in progress'));
                    return;
                }
                armCorrelatedLegacyGameplayOwner(owner, {
                    completionAction: 'cardAction',
                    onSettled: ({ error }) => {
                        if (error) reject(normalizeSocketError(error, 'Action failed'));
                        else resolve();
                    },
                });
                ensureConnected()
                    .then(async () => {
                        if (!(await awaitLegacyEmitWindow(owner))) {
                            owner.retire();
                            return;
                        }
                        const payload = await withRequiredAuthPayload({
                            roomId: owner.roomId,
                            ...(owner.matchId ? { matchId: owner.matchId } : {}),
                            ...(owner.expectedTurn !== null ? { expectedTurn: owner.expectedTurn } : {}),
                            clientActionId: owner.clientActionId,
                            requestId,
                            optionIndex
                        });
                        if (!(await awaitLegacyEmitWindow(owner))) {
                            owner.retire();
                            return;
                        }
                        socket.emit('submitActionIntentSelection', payload, (response) => {
                            if (!isCurrentLegacyGameplayOwner(owner)
                                || !isExactActionCorrelation(response, owner.action, owner.clientActionId)) return;
                            if (!response?.error) return;
                            settleLegacyGameplayOwner(owner, {
                                error: new Error(response.error),
                                reason: 'server-ack-error',
                                recover: true,
                            });
                        });
                    })
                    .catch((err) => {
                        settleLegacyGameplayOwner(owner, {
                            error: err || new Error('Failed to connect'),
                            reason: 'client-error',
                        });
                    });
            });
        },

        /**
         * Cancel a pending player intent request.
         * @param {string} requestId
         * @returns {Promise<void>}
         */
        cancelActionIntentRequest: function (requestId) {
            const state = GameState.get();
            syncPendingGameplayOwnersToCurrentContext(state);
            return cancelPendingActionIntent(pendingLegacyGameplayOwner, requestId);
        },

        /**
         * End the current turn
         */
        endTurn: function () {
            const state = GameState.get();
            syncPendingGameplayOwnersToCurrentContext(state);

            // An action owner is authoritative even if unrelated UI code has
            // momentarily desynchronized the visible loading flag. End Turn
            // must never release or bypass that owner.
            if (hasPendingGameplayOwner() || state.actionInProgress) {
                debugLog('End Turn blocked while another action is in progress');
                showGameNotification(
                    resolvedText('game.processingAction', 'Processing action...'),
                    1500,
                );
                Effects?.invalidAction?.({
                    cardElement: document.getElementById('end-turn-btn'),
                    reason: 'busy'
                });
                return;
            }

            if (!state.isYourTurn) {
                console.warn('Not your turn, endTurn blocked');
                showGameNotification(translate('game.notYourTurn', 'Not your turn'));
                Effects?.invalidAction?.({
                    cardElement: document.getElementById('end-turn-btn'),
                    reason: 'turn'
                });
                return;
            }

            // Prevent duplicate clicks for this exact authoritative turn while
            // still allowing a fast AI to hand back a genuinely new turn well
            // inside the old 500 ms window.
            const expectedTurn = Number.isInteger(state.currentTurn) ? state.currentTurn : null;
            const endTurnActionKey = [
                'endTurnAction',
                state.roomId || 'no-room',
                state.matchId || 'no-match',
                expectedTurn ?? 'unknown-turn'
            ].join(':');
            if (debounceTimers[endTurnActionKey]) {
                debugLog('Debouncing duplicate end turn request');
                return;
            }
            debounceTimers[endTurnActionKey] = setTimeout(() => {
                delete debounceTimers[endTurnActionKey];
            }, 500); // 500ms debounce

            GameState.set('draggedCard', null);
            GameState.set('draggedCardIndex', null);
            GameState.set('dragSourceType', null);

            document.querySelectorAll('.valid-target').forEach(el => {
                el.classList.remove('valid-target');
            });

            if (SoundEffects?.play) {
                try {
                    SoundEffects.play('press');
                } catch (e) {
                    console.warn('Could not play end turn press sound:', e);
                }
            }

            const clientActionId = createClientActionId();
            const owner = {
                clientActionId,
                roomId: state.roomId || null,
                matchId: state.matchId || null,
                expectedTurn,
                requestAction: 'endTurn',
                timeoutId: null,
                settled: false,
                uiLocked: false,
                settle: null,
                retire: null,
                handleSocketError: null,
                handleAck: null,
            };
            const isCurrentOwner = () => pendingEndTurn === owner && !owner.settled;
            const matchesOwnerContext = (current, { requireTurn = true } = {}) => {
                if (!current || current.roomId !== owner.roomId) return false;
                if ((current.matchId || null) !== (owner.matchId || null)) return false;
                if (requireTurn && owner.expectedTurn !== null && current.currentTurn !== owner.expectedTurn) {
                    return false;
                }
                return true;
            };
            owner.retire = () => {
                return owner.settle({ notify: false });
            };
            owner.settle = ({ error = null, accepted = false, recover = false, notify = true } = {}) => {
                if (!isCurrentOwner()) return false;
                owner.settled = true;
                if (owner.timeoutId) clearTimeout(owner.timeoutId);
                pendingEndTurn = null;

                const currentContext = matchesOwnerContext(GameState.get(), { requireTurn: false });
                if (owner.uiLocked) releaseSharedActionLock(owner);
                if (error) rememberEndTurnFailure(owner, error);
                if (recover) requestFreshGameStateForRecovery(owner.roomId, owner.matchId);
                if (error && notify) {
                    showGameNotification(
                        socketErrorMessage(error, translate('common.actionFailed', 'Action failed.')),
                        3000,
                        true,
                    );
                }
                if (accepted && Tutorial?.isActive?.()) {
                    Tutorial.onGameEvent('endTurn');
                }
                if (currentContext) syncEndTurnButton({ keepDisabled: accepted });
                return true;
            };
            owner.handleSocketError = (error) => {
                if (!isExactActionCorrelation(
                    error,
                    owner.requestAction,
                    owner.clientActionId,
                )) return false;
                return owner.settle({ error, recover: true });
            };
            owner.handleAck = (response) => {
                if (!isExactActionCorrelation(
                    response,
                    owner.requestAction,
                    owner.clientActionId,
                )) return false;
                if (response?.error) {
                    return owner.settle({
                        error: {
                            message: response.error,
                            action: owner.requestAction,
                            clientActionId: owner.clientActionId,
                        },
                        recover: true,
                    });
                }
                if (response?.ok) return owner.settle({ accepted: true });
                return false;
            };

            pendingEndTurn = owner;
            acquireSharedActionLock(owner);
            const endTurnButton = document.getElementById('end-turn-btn');
            if (endTurnButton) {
                endTurnButton.disabled = true;
                endTurnButton.setAttribute('aria-busy', 'true');
            }
            owner.timeoutId = setTimeout(() => {
                owner.settle({
                    error: new Error(translate('common.actionFailed', 'Action failed.')),
                    recover: true,
                });
            }, END_TURN_TIMEOUT_MS);

            debugLog('✨ Emitting endTurn');
            ensureConnected()
                .then(async () => {
                    if (!isCurrentOwner()) return;
                    const payload = await withRequiredAuthPayload({
                        roomId: owner.roomId,
                        ...(owner.matchId ? { matchId: owner.matchId } : {}),
                        ...(expectedTurn !== null ? { expectedTurn } : {}),
                        clientActionId,
                    });
                    // Auth may resolve after navigation, recovery, or a fast
                    // authoritative update. Never emit an action for a context
                    // that is no longer the one the player approved.
                    if (!isCurrentOwner()) return;
                    if (!socket.connected || !matchesOwnerContext(GameState.get())) {
                        owner.retire();
                        return;
                    }
                    socket.emit('endTurn', payload, owner.handleAck);
                })
                .catch((err) => {
                    console.error('Failed to connect for endTurn:', err);
                    if (!isCurrentOwner()) return;
                    if (!matchesOwnerContext(GameState.get())) {
                        owner.retire();
                        return;
                    }
                    owner.settle({ error: new Error(connectionFailureMessage(err)), recover: true });
                });


        },



        /**
         * Get info about a room before joining
         * @param {string} roomId
         * @returns {Promise<Object>}
         */
        getRoomInfo: async function (roomId) {
            // Pin before connecting so a first connection is routed to the
            // room's worker; a connection made earlier without the room may
            // still land elsewhere, which the server answers with wrong_worker.
            const tentativeRoomId = setRoomAffinity(roomId);
            await ensureConnected();
            const payload = await withRequiredAuthPayload({ roomId });
            let response = await emitWithAck('getRoomInfo', payload);
            if (isWrongWorker(response)) {
                await rerouteToRoom(roomId);
                response = await emitWithAck('getRoomInfo', payload);
            }
            if (response?.error || response?.ok === false) {
                clearTentativeRoomAffinity(tentativeRoomId);
                const error = new Error(
                    response?.error || translate('lobby.roomCheckFailed', 'Failed to check room.')
                );
                if (response.code) error.code = response.code;
                throw error;
            }
            return response;
        },

        /**
         * Start a solo adventure game (single player vs AI)
         */
        startSoloGame: async function (deck = null, options = {}) {
            return startThroughServerUpdate(async () => {
                // Set just before the start is handed to the socket (failedLobbyStart).
                let sent = false;
                try {
                    const cardbackClaim = warmStartCardback();
                    const prepared = await runLobbyStartPreflight(async () => {
                        await ensureConnected();
                        const authToken = await getRequiredAuthToken();
                        return {
                            authToken,
                            ...cardbackClaim,
                            language: getCurrentLanguage(),
                        };
                    });
                    const payload = deck ? { deck, ...prepared } : { ...prepared };
                    if (typeof options.includeManga === 'boolean') payload.includeManga = options.includeManga;
                    if (typeof options.lucid === 'boolean') payload.lucid = options.lucid;
                    if (options?.qaScenario === 'deckout') {
                        payload.qaScenario = 'deckout';
                    }
                    clearEndedGuestLatchForAuthenticatedStart();
                    sent = true;
                    const result = await emitWithAck(
                        'startSoloAdventure',
                        payload,
                        LOBBY_START_ACK_TIMEOUT_MS,
                        { captureLateAck: true },
                    );
                    if (result?.ok === true) pinAcknowledgedStart(result);
                    return surfaceIndeterminateStart(result);
                } catch (err) {
                    return failedLobbyStart(err, { sent });
                }
            });
        },

        /**
         * Start the tutorial game mode
         */
        startTutorial: async function (options = {}) {
            return startThroughServerUpdate(async () => {
                // Set just before the start is handed to the socket (failedLobbyStart).
                let sent = false;
                try {
                    const cardbackClaim = warmStartCardback();
                    const prepared = await runLobbyStartPreflight(async () => {
                        await ensureConnected();
                        return {
                            authToken: await getRequiredAuthToken(),
                            ...cardbackClaim,
                            language: getCurrentLanguage(),
                        };
                    });
                    const payload = { ...prepared };
                    if (options && typeof options.resumeToken === 'string' && options.resumeToken) {
                        payload.resumeToken = options.resumeToken;
                    }

                    clearEndedGuestLatchForAuthenticatedStart();
                    sent = true;
                    const result = await emitWithAck(
                        'startTutorial',
                        payload,
                        LOBBY_START_ACK_TIMEOUT_MS,
                        { captureLateAck: true },
                    );
                    if (result?.ok === true) pinAcknowledgedStart(result);
                    return surfaceIndeterminateStart(result);
                } catch (err) {
                    return failedLobbyStart(err, { sent });
                }
            });
        },

        /** Start the guest tutorial with a server-issued guest credential. */
        startGuestTutorial: async function ({ guestToken, isCurrent } = {}) {
            /* guest-session.js offers "Try again" for `connection_failed`, so
               that answer means the start never left. Once it is handed to
               the socket, a failure answers `emit_failed`: it may have left. */
            let sent = false;
            try {
                if (guestStartWasSuperseded(isCurrent)) {
                    return { ok: false, code: 'superseded' };
                }
                // A guest never goes through proceedToLobby (which is where signed-in
                // users call init()), so ensure the socket event listeners
                // (gameStarted, error, storyUpdate, …) are registered first. init() is
                // idempotent, so this is a no-op if already set up.
                this.init();
                await ensureConnected();
                if (guestStartWasSuperseded(isCurrent)) {
                    return { ok: false, code: 'superseded' };
                }
                if (!guestToken) {
                    console.warn('[SocketHandler] startGuestTutorial called without a guest token');
                    return;
                }
                sent = true;
                return await emitWithAck('startTutorial', {
                    guestToken,
                    hasMangaPass: false,
                    selectedCardback: 'default',
                    language: getCurrentLanguage(),
                });
            } catch (err) {
                return {
                    ok: false,
                    code: sent ? 'emit_failed' : 'connection_failed',
                    message: connectionFailureMessage(err),
                };
            }
        },

        resumeGuestTutorial: async function ({
            roomId,
            playerId,
            guestToken,
            isCurrent,
        } = {}) {
            // As startGuestTutorial: `connection_failed` only before it leaves.
            let sent = false;
            try {
                if (guestStartWasSuperseded(isCurrent)) {
                    return { ok: false, code: 'superseded' };
                }
                this.init();
                // Pin BEFORE connecting: the primary routes this connection by
                // the room in its handshake. A roomless reconnect after a
                // reload was dealt to any worker, the wrong one answered
                // resume_not_found, and guest-session started a FRESH tutorial.
                setRoomAffinity(roomId);
                await ensureConnected();
                if (guestStartWasSuperseded(isCurrent)) {
                    return { ok: false, code: 'superseded' };
                }
                sent = true;
                let response = await emitWithAck('resumeGuestTutorial', { roomId, playerId, guestToken });
                if (isWrongWorker(response)) {
                    // An earlier roomless connection was still up; reconnect
                    // with the room pinned and ask once more. The worker that
                    // answered owns no such seat, so nothing has been asked of
                    // the right one until the second request is sent.
                    sent = false;
                    await rerouteToRoom(roomId);
                    if (guestStartWasSuperseded(isCurrent)) {
                        return { ok: false, code: 'superseded' };
                    }
                    sent = true;
                    response = await emitWithAck('resumeGuestTutorial', { roomId, playerId, guestToken });
                }
                return response;
            } catch (err) {
                return {
                    ok: false,
                    code: sent ? 'emit_failed' : 'connection_failed',
                    message: connectionFailureMessage(err),
                };
            }
        },

        /**
         * Confirm mulligan decision (discard selected cards and draw replacements)
         * @param {Array} discardCardIds - Array of card instanceIds to discard (empty = keep all)
         */
        confirmMulligan: function (discardCardIds = []) {
            const state = GameState.get();
            syncPendingGameplayOwnersToCurrentContext(state);
            if (hasPendingGameplayOwner()) {
                console.warn('[SocketHandler] Cannot confirm mulligan while another action is in progress');
                return;
            }
            if (!state.roomId) {
                console.error('[SocketHandler] Cannot confirm mulligan: no roomId');
                return;
            }
            const owner = createLegacyGameplayOwner('mulligan', state);
            /* ONE LOADING STATE AFTER KEEP (round 15, 28 September 2026): the
               keep put "Processing action..." and "Waiting for opponent..."
               in the same centred box at once, inside the grace, on every
               recorded run. An opponent still deciding has the mulligan's
               waiting line as the wait's loading state, so the processing
               chrome stays silent. Otherwise nobody is being waited for, and
               the chrome shows only for a keep that outlasts the shared grace.
               The input lock is taken at once either way. */
            const lockPresentation = Mulligan?.isOpponentReady?.() === false
                ? { silent: true }
                : { graceMs: KEEP_PROCESSING_GRACE_MS };
            if (!acquireLegacyGameplayOwner(owner, lockPresentation)) return;
            const submission = captureMulliganSubmission(state);
            debugLog('[SocketHandler] Confirming mulligan with discards:', discardCardIds);
            return new Promise((resolve) => {
                armCorrelatedLegacyGameplayOwner(owner, {
                    timeoutMs: MULLIGAN_ACK_TIMEOUT_MS,
                    timeoutMessage: 'Mulligan acknowledgement timed out',
                    onSettled: ({ data, error, reason }) => {
                        if (data?.ok !== true) recoverFailedMulliganSubmission(submission);
                        if (reason === 'client-error') {
                            console.error('[SocketHandler] Failed to send mulligan:', error);
                            alert(connectionFailureMessage(error));
                        }
                        resolve(data || { error: reason === 'timeout' ? 'ack_timeout' : 'context_changed' });
                    },
                });
                ensureConnected()
                    .then(async () => {
                        if (!(await awaitLegacyEmitWindow(owner))) {
                            owner.retire();
                            return;
                        }
                        const payload = await withRequiredAuthPayload({
                            roomId: owner.roomId,
                            ...(owner.matchId ? { matchId: owner.matchId } : {}),
                            ...(owner.expectedTurn !== null ? { expectedTurn: owner.expectedTurn } : {}),
                            clientActionId: owner.clientActionId,
                            discardCardIds,
                        });
                        if (!(await awaitLegacyEmitWindow(owner))) {
                            owner.retire();
                            return;
                        }
                        socket.emit('mulligan', payload, (response) => {
                            if (!isCurrentLegacyGameplayOwner(owner)
                                || !isExactActionCorrelation(response, owner.action, owner.clientActionId)) return;
                            settleLegacyGameplayOwner(owner, {
                                data: response,
                                error: response?.ok === true ? null : new Error(response?.error || 'Mulligan failed'),
                                reason: response?.ok === true ? 'complete' : 'server-ack-error',
                            });
                        });
                    })
                    .catch((error) => {
                        settleLegacyGameplayOwner(owner, { error, reason: 'client-error' });
                    });
            });
        },

        /**
         * Get the Socket.IO socket instance (for testing purposes)
         * @returns {Object} - Socket.IO socket
         */
        getSocket: function () {
            return socket;
        },

        /**
         * Explicitly emit a user activity signal (throttled)
         */
        emitActivity: function (reason) {
            __emitActivityThrottled(reason || 'manual');
        },

        /**
         * Set the player's language preference
         * @param {string} language - Language code (e.g., 'ja', 'en', 'fr')
         */
        setLanguage: function (language) {
            if (!language) return;
            ensureConnected()
                .then(() => {
                    socket.emit('setLanguage', { language });
                    if (window.CLIENT_DEBUG_MODE) {
                        debugLog('[SocketHandler] Language set to:', language);
                    }
                })
                .catch((err) => {
                    console.warn('[SocketHandler] Could not send language preference:', err.message);
                });
        },

        /**
         * Start the heartbeat monitoring (for testing)
         * @returns {Boolean} - true if started
         */
        startHeartbeat: function () {
            if (heartbeatInterval) {
                debugLog('Heartbeat already running');
                return false;
            }
            missedHeartbeats = 0;
            heartbeatInterval = setInterval(() => {
                if (!socket.connected) return;
                debugLog('🫀 Sending heartbeat');
                const ackTimeout = setTimeout(() => {
                    console.warn('⚠️ Heartbeat acknowledgment missed');
                    missedHeartbeats++;
                    if (missedHeartbeats > 2) {
                        console.error('⚠️ Too many heartbeats missed. Connection may be lost.');
                        const notification = document.createElement('div');
                        notification.className = 'game-notification';
                        notification.textContent = translate('common.connectionIssuesRefresh', 'Connection issues detected. You may need to refresh.');
                        document.body.appendChild(notification);
                        setTimeout(() => {
                            notification.classList.add('fade-out');
                            setTimeout(() => {
                                if (notification.parentNode) {
                                    notification.parentNode.removeChild(notification);
                                }
                            }, 500);
                        }, 5000);
                        missedHeartbeats = 0;
                    }
                }, 3000); // Heartbeat ack timeout

                try {
                    if (socket.connected) {
                        socket.emit('heartbeat', null, () => {
                            clearTimeout(ackTimeout);
                            missedHeartbeats = 0;
                            debugLog('🫀 Heartbeat acknowledged');
                        });
                    } else {
                        console.warn('Cannot send heartbeat - socket not connected');
                        clearTimeout(ackTimeout);
                    }
                } catch (error) {
                    console.error('Error sending heartbeat:', error);
                    clearTimeout(ackTimeout);
                }
            }, 15000); // Heartbeat interval
            debugLog('🫀 Started heartbeat monitoring');
            return true;
        },

        /**
         * Stop the heartbeat monitoring (for testing)
         * @returns {Boolean} - true if stopped
         */
        stopHeartbeat: function () {
            if (!heartbeatInterval) {
                debugLog('No heartbeat running');
                return false;
            }
            clearInterval(heartbeatInterval);
            heartbeatInterval = null;
            debugLog('🛑 Stopped heartbeat monitoring');
            return true;
        },

        clearGameNotifications,
    };
})();

// Expose for browser global access and tests
// The game page is the only one with a socket, so it is the side that opts in
// to language changes. The registry is a leaf module, so this file and i18n.js
// stay out of each other's dependency graphs entirely — see i18n-events.js.
onLanguageChange((language) => SocketHandler.setLanguage(language));

export { SocketHandler };
